import React, { useState, useEffect, useLayoutEffect, useMemo, useRef, useCallback } from 'react';
import { logger } from './lib/logger';
import DOMPurify from 'dompurify';
import { X, Plus, Trash2, ZoomIn, ZoomOut, Save, FolderOpen, Share2, Undo, Redo, FileText, LogOut, Clock, Home, Bold, Italic, Underline, List, ListOrdered, Download, Eye, EyeOff, Highlighter, Search, CheckCircle2, Circle, Pencil, ArrowLeft } from 'lucide-react';
import supabase from './supabase';
import ShareDialog from './ShareDialog';
import MindmapTourOverlay from './MindmapTourOverlay';
import { MindmapNode, SavedFlowchart, HistoryEntry } from './types';
import type { FlowchartWithAccess } from './types';
import MinimapOverlay from './MinimapOverlay';
import { useRealtimeSync, usePresence } from './useRealtimeSync';
import { mergeById, mergeScalar } from './lib/collabMerge';
import { evaluateRealtimeUpdate, writeWithConflictMerge } from './lib/collabSync';
import { useFlowchartAccess, RoleBadge } from './useFlowchartAccess';
import { useT } from './i18n/index';
import LanguageSwitcher from './LanguageSwitcher';

const APP_VERSION = '1.2.0';

// ─── Constants ───────────────────────────────────────────────────────────────

const NODE_COLORS = [
  '#6366f1', '#ec4899', '#f59e0b', '#10b981', '#3b82f6',
  '#8b5cf6', '#ef4444', '#14b8a6', '#f97316', '#84cc16',
];

const MONO_COLOR = '#258C9B';

// Priority colors: 1=red (highest) → 5=gray (lowest)
const PRIO_COLORS: Record<number, string> = {
  1: '#ef4444',
  2: '#f97316',
  3: '#eab308',
  4: '#3b82f6',
  5: '#9ca3af',
};

// Step radius FROM PARENT per depth level (px in canvas space)
// Step radius FROM PARENT per depth level (px in canvas space)
// level 1 (root→child): original distance; level 2+ all equal to the level-2 value
const STEP_R = [0, 200, 90, 115, 115, 115];
// Each linked child mindmap gets its own ID namespace: child i uses IDs (i+1)*LINK_OFFSET … (i+2)*LINK_OFFSET-1
const LINK_OFFSET = 1_000_000;
// Vertical spacing between sibling nodes — level-dependent
const CHILD_GAP_DEFAULT = 62;
const CHILD_GAP_DEEP = 26;   // level 3+
const MIN_GAP_DEFAULT = 20;
const MIN_GAP_DEEP = 8;      // level 3+

// ─── Layout helpers ───────────────────────────────────────────────────────────

function buildChildrenMap(nodes: MindmapNode[]): Map<number, number[]> {
  const map = new Map<number, number[]>();
  for (const n of nodes) map.set(n.id, []);
  for (const n of nodes) {
    if (n.parentId !== null) map.get(n.parentId)?.push(n.id);
  }
  return map;
}

function getNodeLevel(nodes: MindmapNode[], id: number): number {
  const nodeMap = new Map(nodes.map(n => [n.id, n]));
  let level = 0;
  let cur = nodeMap.get(id);
  while (cur && cur.parentId !== null) {
    level++;
    cur = nodeMap.get(cur.parentId);
    if (level > 100) break;
  }
  return level;
}

// getNodeLevel rebuilds a full id→node Map from scratch on every single call — fine for
// the occasional one-off call (e.g. after a drag-drop reparent), but the main SVG render
// calls it 2-3 times per node (once per node, again for linked-mindmap nodes' attachment
// level), which made every render — including every single mousemove while panning,
// since panOffset is plain state with no ref/DOM-transform bypass — effectively O(n²) in
// the total node count. Worse the more nodes there are, which is exactly why panning felt
// slower specifically on mindmaps with linked child mindmaps merged into the same `nodes`
// array. Computes every node's level in one O(n) pass (each node's chain walked at most
// once, thanks to memoizing intermediate results) instead of O(n) per lookup.
function computeAllLevels(nodes: MindmapNode[]): Map<number, number> {
  const nodeMap = new Map(nodes.map(n => [n.id, n]));
  const levels = new Map<number, number>();
  function levelOf(id: number, depthGuard: number): number {
    const cached = levels.get(id);
    if (cached !== undefined) return cached;
    const node = nodeMap.get(id);
    const level = (!node || node.parentId === null || depthGuard > 100)
      ? 0
      : levelOf(node.parentId, depthGuard + 1) + 1;
    levels.set(id, level);
    return level;
  }
  for (const n of nodes) levelOf(n.id, 0);
  return levels;
}

function countDescendants(childrenMap: Map<number, number[]>, id: number): number {
  const ch = childrenMap.get(id) ?? [];
  return ch.reduce((s, c) => s + 1 + countDescendants(childrenMap, c), 0);
}

function getAllDescendants(childrenMap: Map<number, number[]>, id: number): number[] {
  const ch = childrenMap.get(id) ?? [];
  return [...ch, ...ch.flatMap(c => getAllDescendants(childrenMap, c))];
}

/**
 * Radial layout:
 * - Level 0 (root): at (0,0)
 * - Level 1: distributed around the root (fully circular)
 * - Level 2+: column-wise (stacked perpendicular to the radial direction)
 *   so that child nodes line up neatly one below another.
 */
function computeLayout(nodes: MindmapNode[], rootId: number): Map<number, { x: number; y: number }> {
  const positions = new Map<number, { x: number; y: number }>();
  const childrenMap = buildChildrenMap(nodes);
  const nodeMap = new Map(nodes.map(n => [n.id, n]));

  const getStepR = (level: number) => level < STEP_R.length ? STEP_R[level] : 115;

/** Places children in a column perpendicular to forwardAngle.
   *  parentW = width of the parent node (for dynamic step distance). */
  function placeColumn(ch: number[], parentX: number, parentY: number, forwardAngle: number, level: number, parentW: number = 0, parentBaseW: number = 0) {
    const maxChildBaseW = ch.reduce((mx, cid) => {
      const node = nodeMap.get(cid);
      return Math.max(mx, nodeSize(level, node?.text ?? '', node?.manualW).baseW);
    }, 0);
    const minStep = parentW > 0 ? parentW - parentBaseW / 2 + maxChildBaseW / 2 + 30 : 0;
    const stepR = Math.max(getStepR(level), minStep);
    const cosF = Math.cos(forwardAngle);
    const sinF = Math.sin(forwardAngle);
    const perpX = -sinF;
    const perpY = cosF;

    // Height-aware slot sizing: each slot = max(CHILD_GAP, nodeH + MIN_GAP) for leaves,
    // or sum of children slots for parents — so taller nodes push siblings apart.
    function subtreeH(cid: number, lvl: number): number {
      const n = nodeMap.get(cid);
      const { h } = nodeSize(lvl, n?.text ?? '', n?.manualW);
      const gap  = lvl >= 3 ? CHILD_GAP_DEEP  : CHILD_GAP_DEFAULT;
      const mGap = lvl >= 3 ? MIN_GAP_DEEP    : MIN_GAP_DEFAULT;
      const minH = Math.max(gap, h + mGap);
      const grandCh = childrenMap.get(cid) ?? [];
      if (grandCh.length === 0 || n?.collapsed) return minH;
      return Math.max(minH, grandCh.reduce((sum, c) => sum + subtreeH(c, lvl + 1), 0));
    }

    const slotHeights = ch.map(cid => subtreeH(cid, level));
    const totalH = slotHeights.reduce((s, v) => s + v, 0);

    let cumH = 0;
    ch.forEach((cid, i) => {
      const sh = slotHeights[i];
      const offset = cumH + sh / 2 - totalH / 2;

      const node = nodeMap.get(cid);
      let x: number, y: number;
      if (node?.manualX !== undefined && node?.manualY !== undefined) {
        x = node.manualX; y = node.manualY;
      } else {
        x = parentX + stepR * cosF + perpX * offset;
        y = parentY + stepR * sinF + perpY * offset;
      }
      positions.set(cid, { x, y });

      const grandCh = childrenMap.get(cid) ?? [];
      if (grandCh.length > 0 && !node?.collapsed) {
        const { w: childW, baseW: childBaseW } = nodeSize(level, node?.text ?? '', node?.manualW);
        const adjustedChildW = childW + nodeDispAdj(node?.priority, node?.note, node?.url);
        placeColumn(grandCh, x, y, forwardAngle, level + 1, adjustedChildW, childBaseW);
      }
      cumH += sh;
    });
  }

  // Root at (0,0)
  positions.set(rootId, { x: 0, y: 0 });
  const rootNode = nodeMap.get(rootId);
  if (!rootNode || rootNode.collapsed) return positions;

  const l1 = childrenMap.get(rootId) ?? [];
  if (l1.length === 0) return positions;

  // Level-1: first half right (angle=0), second half left (angle=π)
  const { w: rootW, baseW: rootBaseW } = nodeSize(0, rootNode.text ?? '', rootNode.manualW);
  const adjustedRootW = rootW + nodeDispAdj(rootNode.priority, rootNode.note, rootNode.url);
  const half = Math.ceil(l1.length / 2);
  const rightGroup = l1.slice(0, half);
  const leftGroup = l1.slice(half);

  if (rightGroup.length > 0) placeColumn(rightGroup, 0, 0, 0, 1, adjustedRootW, rootBaseW);
  if (leftGroup.length > 0) placeColumn(leftGroup, 0, 0, Math.PI, 1, adjustedRootW, rootBaseW);

  return positions;
}

/** Cubic bezier that leaves along the tangent of the snap point so the line never disappears behind a node. */
/**
 * Elbow connector: horizontal → vertical → horizontal (Z- or L-shape).
 * Because all peer nodes share the same mid_x, their vertical segments
 * run parallel and the lines can no longer cross over one another.
 * Rounded corners (r=8) for a clean look.
 */
function makePath(x1: number, y1: number, x2: number, y2: number, side1: SnapSide, side2: SnapSide): string {
  const dx = x2 - x1, dy = y2 - y1;
  const dist = Math.sqrt(dx * dx + dy * dy);
  if (dist < 1) return `M ${x1} ${y1} L ${x2} ${y2}`;

  // Fallback bezier for T/B connections (not used in the mindmap)
  if (side1 === 'T' || side1 === 'B' || side2 === 'T' || side2 === 'B') {
    const t = Math.max(dist * 0.38, 36);
    const [cx1, cy1] = side1 === 'R' ? [x1 + t, y1] : side1 === 'L' ? [x1 - t, y1] : side1 === 'B' ? [x1, y1 + t] : [x1, y1 - t];
    const [cx2, cy2] = side2 === 'L' ? [x2 - t, y2] : side2 === 'R' ? [x2 + t, y2] : side2 === 'T' ? [x2, y2 - t] : [x2, y2 + t];
    return `M ${x1} ${y1} C ${cx1} ${cy1} ${cx2} ${cy2} ${x2} ${y2}`;
  }

  // Elbow: two straight bends, no overlapping lines possible
  const sx = side1 === 'R' ? 1 : -1; // horizontal direction
  const mid_x = (x1 + x2) / 2;

  if (Math.abs(dy) < 1) return `M ${x1} ${y1} L ${x2} ${y2}`; // same height

  const sy = dy > 0 ? 1 : -1;
  const r = Math.min(8, Math.abs(dx) / 2 - 2, Math.abs(dy) / 2 - 2);

  if (r < 2) {
    // Not enough room: sharp corners
    return `M ${x1} ${y1} L ${mid_x} ${y1} L ${mid_x} ${y2} L ${x2} ${y2}`;
  }

  return [
    `M ${x1} ${y1}`,
    `H ${mid_x - sx * r}`,
    `Q ${mid_x} ${y1} ${mid_x} ${y1 + sy * r}`,
    `V ${y2 - sy * r}`,
    `Q ${mid_x} ${y2} ${mid_x + sx * r} ${y2}`,
    `H ${x2}`,
  ].join(' ');
}

// ─── Text wrap & node size ─────────────────────────────────────────────────────

/** Splits text into lines that fit within maxChars. */
function wrapText(text: string, maxChars: number): string[] {
  const words = text.split(' ');
  const lines: string[] = [];
  let line = '';
  for (const word of words) {
    const candidate = line ? line + ' ' + word : word;
    if (candidate.length <= maxChars) {
      line = candidate;
    } else {
      if (line) lines.push(line);
      // If a single word is longer than maxChars, truncate it
      line = word.length > maxChars ? word.slice(0, maxChars - 1) + '…' : word;
    }
  }
  if (line) lines.push(line);
  return lines.length > 0 ? lines : [''];
}

interface NodeDims { w: number; h: number; rx: number; lines: string[]; fontSize: number; lineH: number; baseW: number; }

function nodeSize(level: number, text: string, manualW?: number): NodeDims {
  const configs = [
    { baseW: 164, fs: 14, rx: 14 },
    { baseW: 140, fs: 12, rx: 10 },
    { baseW: 120, fs: 11, rx: 17 },
  ];
  const { baseW, fs, rx } = level < configs.length ? configs[level] : configs[configs.length - 1];
  // The central (root) node's boundary felt too tight around its text — give it a bit
  // more breathing room than the other levels, which stay at their existing padding.
  const hPad = level === 0 ? 16 : level >= 3 ? 16 : 12;
  const vPad = level === 0 ? 14 : 10;
  // charW slightly above the lower bound (0.40) so the padding stays consistent across all node sizes
  const charW = fs * 0.44;
  const lineH = fs * 1.45;
  const textPx = text.length * charW;

  if (manualW !== undefined) {
    // Manual width: wrap at that width, height follows line count
    const w = Math.max(hPad * 2 + 20, manualW);
    if (textPx + 2 * hPad <= w) {
      return { w, h: Math.ceil(lineH + 2 * vPad), rx, lines: [text], fontSize: fs, lineH, baseW };
    }
    const maxChars = Math.floor((w - 2 * hPad) / charW);
    const lines = wrapText(text, maxChars);
    return { w, h: Math.ceil(lines.length * lineH + 2 * vPad), rx, lines, fontSize: fs, lineH, baseW };
  }

  // Auto-size: single line if it fits, otherwise wrap and size to the longest line
  if (textPx + 2 * hPad <= baseW * 2) {
    // charW (0.44) is a mean estimate; for longer strings character-width variance accumulates.
    // Add a small proportional buffer only for single-line nodes above ~22 chars so short nodes
    // and multi-line nodes are unaffected.
    const wExtra = text.length > 22 ? Math.ceil((text.length - 22) * 0.7) : 0;
    const w = Math.max(2 * hPad + charW, Math.ceil(textPx + 2 * hPad) + wExtra);
    return { w, h: Math.ceil(lineH + 2 * vPad), rx, lines: [text], fontSize: fs, lineH, baseW };
  }
  const maxChars = Math.floor((baseW * 2 - 2 * hPad) / charW);
  const lines = wrapText(text, maxChars);
  const longestChars = Math.max(...lines.map(l => l.length));
  const w = Math.max(2 * hPad + charW, Math.ceil(longestChars * charW * 1.05 + 2 * hPad));
  return { w, h: Math.ceil(lines.length * lineH + 2 * vPad), rx, lines, fontSize: fs, lineH, baseW };
}

/** Extra width for badges: 20px left for priority, 20px right per right-side badge (note, url). */
function nodeDispAdj(priority?: number, note?: string, url?: string): number {
  return (priority ? 20 : 0) + (note ? 20 : 0) + (url ? 20 : 0);
}

/**
 * Computes the visual center of a node after outward growth.
 * Grows to the right if the node is right of center, to the left if left.
 */
function getVisualCenter(pos: { x: number; y: number }, w: number, baseW: number): { x: number; y: number } {
  const growth = (w - baseW) / 2;
  if (pos.x > 30) return { x: pos.x + growth, y: pos.y };
  if (pos.x < -30) return { x: pos.x - growth, y: pos.y };
  return pos;
}

type SnapSide = 'L' | 'R' | 'T' | 'B';



/** Connection point for tree lines: always L or R based on x-direction.
 *  Prevents connections from ending up above or below on narrow/tall nodes. */
function treePoint(cx: number, cy: number, w: number, targetX: number): { x: number; y: number; side: SnapSide } {
  return targetX < cx
    ? { x: cx - w / 2, y: cy, side: 'L' }
    : { x: cx + w / 2, y: cy, side: 'R' };
}

// ─── Component ────────────────────────────────────────────────────────────────

interface Props {
  user: { id: string; email?: string };
  onSignOut: () => void;
  isActive?: boolean;
  onSwitchToFlowchart: () => void;
  onOpenProjectList: (handlers: {
    filterMapType?: 'flowchart' | 'mindmap';
    onLoadFlowchart: (f: SavedFlowchart) => void;
    onDeleteFlowchart: (id: string) => void;
    onRestoreVersion?: (versionData: SavedFlowchart, flowchart: FlowchartWithAccess) => void;
    onSetProject?: (projectId: string | null, projectName: string | null) => void;
    currentFlowchartId?: string;
  }) => void;
}

const FRESH_ROOT: MindmapNode = { id: 1, text: 'Central Topic', parentId: null, collapsed: false, color: '#6366f1' };

export default function MindmapBuilder({ user, onSignOut, isActive = true, onSwitchToFlowchart, onOpenProjectList }: Props) {
  const { t: tr } = useT();
  const [nodes, setNodes] = useState<MindmapNode[]>([FRESH_ROOT]);
  const [nextId, setNextId] = useState(2);
  const [selectedNode, setSelectedNode] = useState<number | null>(null);
  const [editingNode, setEditingNode] = useState<number | null>(null);
  const [editText, setEditText] = useState('');
  const [panOffset, setPanOffset] = useState({ x: 600, y: 400 });
  const [zoom, setZoom] = useState(1);
  const [isPanning, setIsPanning] = useState<{ startX: number; startY: number; initX: number; initY: number } | null>(null);
  const [selectionRect, setSelectionRect] = useState<{ startX: number; startY: number; currentX: number; currentY: number } | null>(null);
  const [selectedNodes, setSelectedNodes] = useState<Set<number>>(new Set());
  const [title, setTitle] = useState('Untitled Mindmap');
  const [currentId, setCurrentId] = useState<string | null>(null);
  const [currentProjectId, setCurrentProjectId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const { userRole, canEdit: canEditMindmap, canEditRef: canEditMindmapRef } = useFlowchartAccess(currentId);
  const [notePanel, setNotePanel] = useState<{ nodeId: number } | null>(null);
  const [notePanelWidth, setNotePanelWidth] = useState(380);
  const notePanelDragRef = useRef<{ startX: number; startW: number } | null>(null);
  const [linkPanel, setLinkPanel] = useState<{ nodeId: number } | null>(null);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [exportMenuY, setExportMenuY] = useState(0);
  const [showPriorities, setShowPriorities] = useState(true);
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; nodeId: number } | null>(null);
  const [showPrioPopup, setShowPrioPopup] = useState(false);
  const [history, setHistory] = useState<MindmapNode[][]>([[FRESH_ROOT]]);
  const [histIdx, setHistIdx] = useState(0);
  const [reparentDrag, setReparentDrag] = useState<{
    nodeId: number;      // anchor node (for visual feedback)
    nodeIds: number[];   // all dragged nodes
    mouseX: number; // screen coordinates
    mouseY: number;
    targetParentId: number | null;
  } | null>(null);
  const [reorderTarget, setReorderTarget] = useState<{ afterId: number | null; parentId: number } | null>(null);
  const reorderTargetRef = useRef(reorderTarget);
  const [pendingNodeDrag, setPendingNodeDrag] = useState<{ nodeId: number; nodeIds: number[]; startX: number; startY: number } | null>(null);
const [showHistory, setShowHistory] = useState(false);
  const [historyVersions, setHistoryVersions] = useState<HistoryEntry[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [showTour, setShowTour] = useState(false);
  const [showHelpPanel, setShowHelpPanel] = useState(false);
  // "Search in mindmap" — client-side only, never persisted (distinct from node.highlighted,
  // which is a per-node, saved manual marker). null = no search run yet; an empty Set means
  // a search ran but found nothing (still worth distinguishing for the "no matches" message).
  const [searchQuery, setSearchQuery] = useState('');
  const [searchMatchIds, setSearchMatchIds] = useState<Set<number> | null>(null);
  const [defaultMindmapId, setDefaultMindmapId] = useState<string | null>(() => localStorage.getItem('mindmap-default-id'));
  const [colorMode, setColorMode] = useState<'multi' | 'mono'>('multi');
  const colorModeRef = useRef<'multi' | 'mono'>('multi');
  const [movePicker, setMovePicker] = useState<{ nodeId: number } | null>(null);
  const [showPriorityMatrix, setShowPriorityMatrix] = useState(false);
  const [quadrantLabels, setQuadrantLabels] = useState<[string, string, string, string]>(() => {
    try { const s = localStorage.getItem('mm-quadrant-labels'); if (s) return JSON.parse(s); } catch {}
    return ['Important & Urgent', 'Important, Not Urgent', 'Urgent, Not Important', 'Not Urgent & Not Important'];
  });
  const [quadrantOrder, setQuadrantOrder] = useState<[number[], number[], number[], number[]] | null>(null);
  const [pmShowDays, setPmShowDays] = useState<boolean>(() => { try { return localStorage.getItem('mm-pm-show-days') === 'true'; } catch { return false; } });
  const [pmDrag, setPmDrag] = useState<{ src: 'q'; qi: number; nodeId: number } | { src: 'd'; di: number; nodeId: number } | null>(null);
  const [pmDragOver, setPmDragOver] = useState<{ dst: 'q'; qi: number; beforeNodeId: number | null } | { dst: 'd'; di: number; beforeNodeId: number | null } | null>(null);
  const [pmFilter, setPmFilter] = useState<number[] | null>(null);
  const [pmContextMenu, setPmContextMenu] = useState<{ nodeId: number; x: number; y: number } | null>(null);
  const [editPmTitle, setEditPmTitle] = useState<{ nodeId: number; text: string } | null>(null);
  // "+Add priority" popup, opened from a quadrant header (qi fixed, pick a quadrant too
  // isn't needed) or a day header (di fixed, picking the quadrant IS needed since a day
  // alone doesn't imply a priority).
  const [addPriorityPopup, setAddPriorityPopup] = useState<{ source: 'quadrant'; qi: number } | { source: 'day'; di: number } | null>(null);
  const [addPriorityBranchId, setAddPriorityBranchId] = useState<number | null>(null);
  const [addPriorityQi, setAddPriorityQi] = useState<number>(1);
  const [addPriorityText, setAddPriorityText] = useState('');
  const [dayOrder, setDayOrder] = useState<number[][]>([[], [], [], [], []]);
  const [dayLabels, setDayLabels] = useState<string[]>(() => {
    try { const s = localStorage.getItem('mm-day-labels'); if (s) return JSON.parse(s); } catch {}
    return ['Day 1', 'Day 2', 'Day 3', 'Day 4', 'Day 5'];
  });
  // How many day-columns the Schedule view shows (3/5/7) — per-user, global like
  // dayLabels/quadrantLabels above (user_profiles.schedule_days), not per-mindmap.
  const [scheduleDays, setScheduleDays] = useState<number>(() => {
    try { const s = localStorage.getItem('mm-schedule-days'); if (s) return JSON.parse(s); } catch {}
    return 3;
  });
  const [resizeDrag, setResizeDrag] = useState<{ nodeId: number; startMouseCanvasX: number; startW: number; isRightSide: boolean } | null>(null);
  const [lastWriteToast, setLastWriteToast] = useState<string | null>(null);
  const [userPriorities, setUserPriorities] = useState<Record<number, number>>({});
  const [userDurations, setUserDurations] = useState<Record<number, string>>({});
  const [userCompleted, setUserCompleted] = useState<Record<number, boolean>>({});
  const prioritiesLoadedForRef = useRef<string | null>(null);
  // Separate from prioritiesLoadedForRef — see the dayOrder persist effect's own comment.
  const dayOrderLoadedForRef = useRef<string | null>(null);
  const resizeDragRef = useRef(resizeDrag);

  // ── Mindmap links ─────────────────────────────────────────────────────────────
  // Ordered list of links: index i → child mindmap uses namespace (i+1)*LINK_OFFSET
  const [mindmapLinks, setMindmapLinks] = useState<Array<{ nodeId: number; childId: string }>>([]);
  const mindmapLinksRef = useRef(mindmapLinks);
  const [childTitles, setChildTitles] = useState<Record<string, string>>({});
  const [showLinkModal, setShowLinkModal] = useState<{ nodeId: number } | null>(null);
  const [availableMindmaps, setAvailableMindmaps] = useState<Array<{ id: string; title: string }>>([]);

  const svgRef = useRef<SVGSVGElement>(null);
  const canvasWrapperRef = useRef<HTMLDivElement>(null);
  const editInputRef = useRef<HTMLTextAreaElement>(null);
  // Mutable refs so keyboard handler always has latest state without re-subscribing
  const nodesRef = useRef(nodes);
  const selectedNodeRef = useRef(selectedNode);
  const selectedNodesRef = useRef(selectedNodes);
  const editingNodeRef = useRef(editingNode);
  const editTextRef = useRef(editText);
  const setSelectedNodeRef = useRef(setSelectedNode);
  const positionsRef = useRef<Map<number, { x: number; y: number }>>(new Map());
  const noteEditorRef = useRef<HTMLDivElement>(null);
  const linkInputRef = useRef<HTMLInputElement>(null);
  const marqueeJustFinished = useRef(false);
  const histIdxRef = useRef(histIdx);
  const historyRef = useRef(history);
  const nextIdRef = useRef(nextId);
  const mindmapClipboardRef = useRef<MindmapNode[] | null>(null);
  const currentIdRef = useRef(currentId);
  const panOffsetRef = useRef(panOffset);
  const zoomRef = useRef(zoom);
  // Applies zoom around a fixed anchor point (default: center of the visible viewport)
  // so the point currently in view stays put instead of shifting toward the canvas origin.
  const applyZoom = useCallback((computeZoom: (current: number) => number, anchor?: { x: number; y: number }) => {
    const rect = canvasWrapperRef.current?.getBoundingClientRect();
    const cx = anchor ? anchor.x : (rect ? rect.width / 2 : 0);
    const cy = anchor ? anchor.y : (rect ? rect.height / 2 : 0);
    const prevZoom = zoomRef.current;
    const prevPan = panOffsetRef.current;
    const newZoom = Math.max(0.3, Math.min(2.5, computeZoom(prevZoom)));
    setZoom(newZoom);
    setPanOffset({
      x: cx - ((cx - prevPan.x) / prevZoom) * newZoom,
      y: cy - ((cy - prevPan.y) / prevZoom) * newZoom,
    });
  }, []);
  // Called right after a new node's position has settled in positionsRef (addChild's
  // setTimeout) — if that node would land outside, or too close to the edge of, the
  // visible canvas (e.g. above the toolbar on a crowded mindmap), zoom out just enough
  // to bring it into view. Only ever zooms OUT (never in) — a node that already fits
  // is left untouched.
  const ensureNodeVisible = useCallback((nodeId: number) => {
    const rect = canvasWrapperRef.current?.getBoundingClientRect();
    const pos = positionsRef.current.get(nodeId);
    if (!rect || !pos || rect.width <= 0 || rect.height <= 0) return;
    const prevZoom = zoomRef.current;
    const prevPan = panOffsetRef.current;
    const cx = rect.width / 2;
    const cy = rect.height / 2;
    const anchorX = (cx - prevPan.x) / prevZoom;
    const anchorY = (cy - prevPan.y) / prevZoom;
    const dx = pos.x - anchorX;
    const dy = pos.y - anchorY;
    // Flat screen-space clearance around the node's center — covers its own box (tiny
    // while empty, grows as the user types) plus breathing room from the edges/toolbar.
    const MARGIN = 110;
    const boundX = Math.abs(dx) > 0.01 ? Math.max(0, rect.width / 2 - MARGIN) / Math.abs(dx) : Infinity;
    const boundY = Math.abs(dy) > 0.01 ? Math.max(0, rect.height / 2 - MARGIN) / Math.abs(dy) : Infinity;
    const requiredZoom = Math.max(0.3, Math.min(prevZoom, boundX, boundY));
    if (requiredZoom < prevZoom - 0.001) {
      applyZoom(() => requiredZoom, { x: cx, y: cy });
    }
  }, [applyZoom]);
  const dayOrderRef = useRef(dayOrder);
  const dayLabelsRef = useRef(dayLabels);
  const quadrantOrderRef = useRef(quadrantOrder);
  // Presence — who is online in this mindmap. Gated on `isActive`, not just
  // `!!currentId` — see the matching comment on FlowchartBuilder.tsx's own
  // usePresence call for why (both builders stay mounted behind the scenes when
  // switching modes, App.tsx only toggles CSS display).
  const { onlineUsers } = usePresence(currentId, user, isActive && !!currentId);

  // Realtime sync — receives updates from other users via Postgres subscription
  const { registerOwnWrite } = useRealtimeSync(
    currentId,
    user?.id,
    {
      enabled: !!currentId,
      onUpdate: (updatedData: any) => {
        const newNodes: MindmapNode[] = Array.isArray(updatedData.boxes) ? updatedData.boxes
          : typeof updatedData.boxes === 'string' ? JSON.parse(updatedData.boxes || '[]') : [];
        const newTitle: string = updatedData.title || '';

        if (newNodes.length === 0 && nodesRef.current.length > 0) {
          logger.warn('⚠️ Realtime update genegeerd: zou', nodesRef.current.length, 'nodes wissen');
          return;
        }

        const incomingContent = JSON.stringify({ nodes: newNodes, title: newTitle });
        // NS=0 is the only part of local state this remote row can speak to — child-
        // namespace injections aren't part of the parent's own DB row.
        const currentLocalContent = JSON.stringify({ nodes: nodesRef.current.filter(n => nodeNS(n.id) === 0), title: titleRef.current });

        const decision = evaluateRealtimeUpdate({
          incomingId: updatedData.id,
          currentId: currentId ?? '',
          incomingContent,
          lastRemoteContent: lastRemoteContentRef.current,
          lastSyncedContent: lastSyncedContentRef.current,
          currentLocalContent,
          updatedAt: updatedData.updated_at,
          lastLocalSaveTime: lastLocalSaveTimeRef.current,
          equals: (a, b) => a === b,
        });
        if (decision === 'ignore') return;

        lastRemoteContentRef.current = incomingContent;
        // Base for the 3-way merge = the last synced state, BEFORE we overwrite it
        // below with the incoming content.
        const baseParsed = (() => {
          try { return lastSyncedContentRef.current ? JSON.parse(lastSyncedContentRef.current) : null; } catch { return null; }
        })();
        lastSyncedContentRef.current = incomingContent;
        if (typeof updatedData.version === 'number') mindmapVersionRef.current = updatedData.version;

        // Metadata-only broadcast: NS=0 content is byte-identical to what's already
        // on screen, so there's nothing to merge — and no matching change_log entry
        // to justify a "changes made by" toast either.
        if (decision === 'metadata-only') return;

        // Only replace NS=0 nodes; leave child-namespace injections intact. 3-way
        // merge (base = last synced, local = own unsaved state, remote =
        // incoming update) so an unsaved local change doesn't silently
        // disappear when another user (or the same user in another tab)
        // saves in the meantime — previously the remote state was blindly adopted here.
        // Collapsed state is local per-user UI state — don't adopt it from the other user.
        const ns0existing = nodesRef.current.filter(n => nodeNS(n.id) === 0);
        const localCollapsed = new Map(ns0existing.map(n => [n.id, n.collapsed]));
        const mergedNewNodes = mergeById<MindmapNode>(baseParsed?.nodes ?? [], ns0existing, newNodes).map(n => ({
          ...n,
          collapsed: localCollapsed.has(n.id) ? localCollapsed.get(n.id)! : n.collapsed,
        }));
        const mergedTitle = mergeScalar(baseParsed?.title ?? titleRef.current, titleRef.current, newTitle);

        setNodes(prev => {
          const childNamespaceNodes = prev.filter(n => nodeNS(n.id) !== 0);
          return [...mergedNewNodes, ...childNamespaceNodes];
        });
        setTitle(mergedTitle);

        if (mergedNewNodes.length > 0) {
          const maxId = Math.max(...mergedNewNodes.map((n: MindmapNode) => n.id));
          setNextId(maxId + 1);
          // Remove stale priorities for NS=0 IDs that no longer exist — otherwise
          // new nodes with recycled IDs can spontaneously inherit a priority.
          const liveNS0Ids = new Set(mergedNewNodes.map((n: MindmapNode) => n.id));
          setUserPriorities(prev => {
            const stale = Object.keys(prev).filter(id => nodeNS(Number(id)) === 0 && !liveNS0Ids.has(Number(id)));
            if (stale.length === 0) return prev;
            const next = { ...prev };
            stale.forEach(id => delete next[Number(id)]);
            return next;
          });
        }

        if (updatedData.updated_by_email && updatedData.updated_by_email !== user?.email) {
          setLastWriteToast(updatedData.updated_by_email);
          setTimeout(() => setLastWriteToast(null), 4000);
        }
      },
      onError: (error: any) => {
        logger.error('❌ Realtime sync error mindmap:', error);
      }
    }
  );
  const isActiveRef = useRef<boolean>(isActive);
  useEffect(() => { isActiveRef.current = isActive; }, [isActive]);

  const titleRef = useRef(title);
  useEffect(() => { titleRef.current = title; }, [title]);
  useLayoutEffect(() => { nodesRef.current = nodes; }, [nodes]);
  useEffect(() => { colorModeRef.current = colorMode; }, [colorMode]);
  useEffect(() => { resizeDragRef.current = resizeDrag; }, [resizeDrag]);
  useEffect(() => { selectedNodeRef.current = selectedNode; }, [selectedNode]);
  useEffect(() => { selectedNodesRef.current = selectedNodes; }, [selectedNodes]);
  useEffect(() => { editingNodeRef.current = editingNode; }, [editingNode]);
  useEffect(() => { editTextRef.current = editText; }, [editText]);
  useEffect(() => { histIdxRef.current = histIdx; }, [histIdx]);
  useEffect(() => { historyRef.current = history; }, [history]);
  useEffect(() => { nextIdRef.current = nextId; }, [nextId]);
  useEffect(() => { currentIdRef.current = currentId; }, [currentId]);
  useEffect(() => { reorderTargetRef.current = reorderTarget; }, [reorderTarget]);
  useEffect(() => { panOffsetRef.current = panOffset; }, [panOffset]);
  useEffect(() => { zoomRef.current = zoom; }, [zoom]);
  useEffect(() => { dayOrderRef.current = dayOrder; }, [dayOrder]);
  useEffect(() => { dayLabelsRef.current = dayLabels; }, [dayLabels]);
  useEffect(() => { quadrantOrderRef.current = quadrantOrder; }, [quadrantOrder]);
  useLayoutEffect(() => { mindmapLinksRef.current = mindmapLinks; }, [mindmapLinks]);

  // Session timeout + realtime sync guards
  const lastLoadedAtRef = useRef<number>(0);
  const loadMindmapRef = useRef<((id: string) => Promise<void>) | null>(null);
  const lastLocalSaveTimeRef = useRef<number>(0);
  const lastSyncedContentRef = useRef<string | null>(null);
  const lastRemoteContentRef = useRef<string | null>(null);
  const [syncFailed, setSyncFailed] = useState(false); // autosave failed — banner; the interval retries automatically
  // Last known content version (server-managed) for optimistic locking
  const mindmapVersionRef = useRef<number>(0);
  // Same optimistic-locking pattern, but per linked child mindmap (keyed on childId,
  // stable across link removal/reindexing — unlike the NS/linkIndex which shifts).
  // version = last known DB version of that child; baseNodes = last synced (native,
  // non-offset) node state — used as the 3-way-merge base on a write conflict.
  const childVersionsRef = useRef<Record<string, number>>({});
  const childBaseNodesRef = useRef<Record<string, MindmapNode[]>>({});

  // Priority matrix touch/mouse-drag refs
  type PmTouchState = { nodeId: number; src: { type: 'q'; qi: number } | { type: 'd'; di: number }; isDragging: boolean; startX: number; startY: number };
  type PmOver = { dst: 'q'; qi: number; beforeNodeId: number | null } | { dst: 'd'; di: number; beforeNodeId: number | null };
  const pmTouchRef = useRef<PmTouchState | null>(null);
  const pmDragOverRef = useRef<PmOver | null>(null);
  const pmApplyDropRef = useRef<((over: PmOver) => void) | null>(null);
  const pmMouseDraggedRef = useRef(false); // prevents click-after-drag
  // Auto-scroll a quadrant/day list while dragging a card near its top/bottom edge —
  // without this, a long list's earlier items were simply unreachable: the drop
  // target can only ever be something document.elementFromPoint actually finds on
  // screen, so "drag all the way to the top" maxed out at whatever was already
  // scrolled into view. `dir` is -1 (scroll up, revealing earlier items) or 1 (scroll
  // down). pmLastPointerRef holds the last known pointer position so the scroll-tick
  // interval below can keep re-evaluating the drop target while the pointer itself
  // stays still (held at the edge) — a plain mousemove/touchmove listener alone
  // never fires again once the pointer stops moving.
  const pmAutoScrollRef = useRef<{ el: HTMLElement; dir: 1 | -1 } | null>(null);
  const pmLastPointerRef = useRef<{ x: number; y: number } | null>(null);
  const centerNodeAfterMatrixRef = useRef<number | null>(null);
  useEffect(() => { pmDragOverRef.current = pmDragOver; }, [pmDragOver]);

  // Always-current refs for functions called from touch handlers
  const applyReparentRef = useRef<(nodeId: number, newParentId: number) => void>(() => {});
  const applyReorderRef = useRef<(nodeId: number, afterId: number | null, parentId: number) => void>(() => {});
  const computeReorderTargetRef = useRef<(cx: number, cy: number, dragNodeId: number) => { afterId: number | null; parentId: number } | null>(() => null);
  const startEditRef = useRef<(nodeId: number, initialText: string) => void>(() => {});
  const commitEditRef = useRef<() => void>(() => {});
  const editFromKeyRef = useRef(false); // true when edit was started via a keystroke (cursor at end, not select-all)
  const reparentDragRef = useRef(reparentDrag);
  // Sync note editor content when the note panel opens or switches to a different node
  useEffect(() => {
    if (notePanel && noteEditorRef.current) {
      const noteNode = nodesRef.current.find(n => n.id === notePanel.nodeId);
      noteEditorRef.current.innerHTML = DOMPurify.sanitize(noteNode?.note || '');
      noteEditorRef.current.focus();
    }
  }, [notePanel?.nodeId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (linkPanel && linkInputRef.current) {
      const linkNode = nodesRef.current.find(n => n.id === linkPanel.nodeId);
      linkInputRef.current.value = linkNode?.url || '';
      linkInputRef.current.focus();
      linkInputRef.current.select();
    }
  }, [linkPanel?.nodeId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist dayOrder per mindmap in localStorage + DB (per-user).
  // dayOrderLoadedForRef must already equal currentId — without this guard, the moment
  // loadMindmap() calls setCurrentId(id) (early in that function), this effect fires
  // immediately with whatever dayOrder is STILL holding (the previous mindmap's value,
  // or the initial empty default on a fresh page load) — well before loadMindmap's own
  // later, awaited day_order fetch for `id` has resolved. That premature write lands in
  // BOTH localStorage AND the DB for `id`, and since loadMindmap's day_order logic
  // checks localStorage FIRST and uses it unconditionally when present (any cached
  // string, including a just-written empty one, is truthy), it then never even queries
  // the DB — silently discarding the real, previously-saved schedule. This was reported
  // live: dragging a priority into the schedule appeared not to persist, gone again on
  // the next login.
  useEffect(() => {
    if (!currentId || dayOrderLoadedForRef.current !== currentId) return;
    try { localStorage.setItem(`mm-dorder-${currentId}`, JSON.stringify(dayOrder)); } catch {}
    if (!user?.id) return;
    const uid = user.id; const fid = currentId;
    supabase.from('user_mindmap_priorities').upsert(
      { user_id: uid, flowchart_id: fid, day_order: dayOrder, updated_at: new Date().toISOString() },
      { onConflict: 'user_id,flowchart_id' }
    ).then(() => {}, () => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dayOrder, currentId, user?.id]);


  // Center canvas on mount and resize.
  // On mobile, opening/closing the on-screen keyboard (e.g. when starting or
  // confirming a node edit) also fires a window 'resize' event, even though it's
  // only the viewport HEIGHT that changes, not the width. A genuine resize or
  // orientation change always changes the width too, so we use that to tell the
  // two apart: only re-center on a real width change, and leave the current
  // pan/zoom untouched otherwise so the view doesn't jump during editing.
  useEffect(() => {
    const center = () => setPanOffset({ x: window.innerWidth / 2, y: (window.innerHeight - 40) / 2 });
    let lastWidth = window.innerWidth;
    const onResize = () => {
      if (window.innerWidth === lastWidth) return;
      lastWidth = window.innerWidth;
      center();
    };
    center();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Show tour on first visit — check user_profiles (cross-device), fall back to localStorage
  useEffect(() => {
    if (!user?.id) return;
    supabase.from('user_profiles').select('mindmap_tour_seen').eq('id', user.id).single()
      .then(({ data }) => {
        if (!data?.mindmap_tour_seen && !localStorage.getItem('mindmap-tour-done')) {
          setShowTour(true);
        }
      });
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load default mindmap + global user prefs (labels) — from user_profiles (cross-device)
  useEffect(() => {
    if (!user?.id) return;
    supabase.from('user_profiles').select('default_mindmap_id, quadrant_labels, day_labels, schedule_days').eq('id', user.id).single()
      .then(({ data }) => {
        const defaultId = data?.default_mindmap_id ?? localStorage.getItem('mindmap-default-id');
        if (defaultId) {
          setDefaultMindmapId(defaultId);
          loadMindmap(defaultId);
        }
        if (data?.quadrant_labels) {
          setQuadrantLabels(data.quadrant_labels as [string, string, string, string]);
          try { localStorage.setItem('mm-quadrant-labels', JSON.stringify(data.quadrant_labels)); } catch {}
        }
        if (data?.day_labels) {
          setDayLabels(data.day_labels as string[]);
          try { localStorage.setItem('mm-day-labels', JSON.stringify(data.day_labels)); } catch {}
        }
        if (data?.schedule_days) {
          setScheduleDays(data.schedule_days as number);
          try { localStorage.setItem('mm-schedule-days', JSON.stringify(data.schedule_days)); } catch {}
        }
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Priority matrix: touch/mouse-drag support
  useEffect(() => {
    if (showPriorityMatrix || centerNodeAfterMatrixRef.current === null) return;
    const nodeId = centerNodeAfterMatrixRef.current;
    centerNodeAfterMatrixRef.current = null;
    const pos = positionsRef.current.get(nodeId);
    if (pos && canvasWrapperRef.current) {
      const r = canvasWrapperRef.current.getBoundingClientRect();
      setPanOffset({ x: r.width / 2 - pos.x * zoomRef.current, y: r.height / 2 - pos.y * zoomRef.current });
    }
  }, [showPriorityMatrix]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!showPriorityMatrix) { pmTouchRef.current = null; return; }

    const PM_AUTOSCROLL_EDGE = 36; // px from the list's own top/bottom edge that triggers scrolling
    const PM_AUTOSCROLL_SPEED = 10; // px per tick (see the interval below)

    const handlePmMove = (clientX: number, clientY: number) => {
      if (!pmTouchRef.current) return;
      pmLastPointerRef.current = { x: clientX, y: clientY };
      if (!pmTouchRef.current.isDragging) {
        const dx = clientX - pmTouchRef.current.startX;
        const dy = clientY - pmTouchRef.current.startY;
        if (Math.sqrt(dx * dx + dy * dy) < 6) return;
        pmTouchRef.current.isDragging = true;
        const { nodeId, src } = pmTouchRef.current;
        if (src.type === 'q') setPmDrag({ src: 'q', qi: src.qi, nodeId });
        else setPmDrag({ src: 'd', di: src.di, nodeId });
      }
      const el = document.elementFromPoint(clientX, clientY) as HTMLElement | null;
      if (!el) return;

      // Auto-scroll: the scrollable list is the SAME element data-pmzone already
      // marks (cards sit directly inside it), so this re-checks nothing new — just
      // whether the pointer is currently near its top/bottom edge, regardless of
      // whether `el` itself landed on a card or the zone's own empty padding.
      const scrollZoneEl = el.closest('[data-pmzone]') as HTMLElement | null;
      if (scrollZoneEl) {
        const r = scrollZoneEl.getBoundingClientRect();
        if (clientY < r.top + PM_AUTOSCROLL_EDGE && scrollZoneEl.scrollTop > 0) {
          pmAutoScrollRef.current = { el: scrollZoneEl, dir: -1 };
        } else if (clientY > r.bottom - PM_AUTOSCROLL_EDGE && scrollZoneEl.scrollTop + scrollZoneEl.clientHeight < scrollZoneEl.scrollHeight - 1) {
          pmAutoScrollRef.current = { el: scrollZoneEl, dir: 1 };
        } else {
          pmAutoScrollRef.current = null;
        }
      } else {
        pmAutoScrollRef.current = null;
      }

      const cardEl = el.closest('[data-pmcard]') as HTMLElement | null;
      if (cardEl) {
        const cardId = parseInt(cardEl.getAttribute('data-pmcard') || '0');
        const { nodeId, src } = pmTouchRef.current;
        if (cardId !== nodeId) {
          const srcType = cardEl.getAttribute('data-pmsrc-type');
          const qi = cardEl.getAttribute('data-pmsrc-qi');
          const di = cardEl.getAttribute('data-pmsrc-di');
          if (srcType === 'q' && qi !== null) setPmDragOver({ dst: 'q', qi: parseInt(qi), beforeNodeId: cardId });
          else if (srcType === 'd' && di !== null) setPmDragOver({ dst: 'd', di: parseInt(di), beforeNodeId: cardId });
        } else {
          // Hovering back over the dragged card's own slot: recompute the
          // true no-op target (right before its original next sibling)
          // instead of leaving whatever stale target the pointer passed
          // through on its way back here (see bug_pm_drag_return_to_origin).
          const list = src.type === 'q' ? (quadrantOrderRef.current?.[src.qi] ?? []) : (dayOrderRef.current[src.di] ?? []);
          const idx = list.indexOf(nodeId);
          const nextId = idx >= 0 && idx + 1 < list.length ? list[idx + 1] : null;
          if (src.type === 'q') setPmDragOver({ dst: 'q', qi: src.qi, beforeNodeId: nextId });
          else setPmDragOver({ dst: 'd', di: src.di, beforeNodeId: nextId });
        }
        return;
      }
      if (scrollZoneEl) {
        const zoneType = scrollZoneEl.getAttribute('data-pmzone');
        const qi = scrollZoneEl.getAttribute('data-pmzone-qi');
        const di = scrollZoneEl.getAttribute('data-pmzone-di');
        if (zoneType === 'q' && qi !== null) setPmDragOver({ dst: 'q', qi: parseInt(qi), beforeNodeId: null });
        else if (zoneType === 'd' && di !== null) setPmDragOver({ dst: 'd', di: parseInt(di), beforeNodeId: null });
      }
    };

    // Ticks continuously while a drag is active (not just on mousemove/touchmove,
    // which never fire again once the pointer stops moving) — advances the scroll
    // position by a fixed step and re-runs handlePmMove against the last known
    // pointer position so the drop target keeps tracking the content scrolling
    // underneath it.
    const scrollTickId = setInterval(() => {
      const auto = pmAutoScrollRef.current;
      const last = pmLastPointerRef.current;
      if (!auto || !last) return;
      auto.el.scrollTop += auto.dir * PM_AUTOSCROLL_SPEED;
      handlePmMove(last.x, last.y);
    }, 16);

    const handlePmEnd = () => {
      pmAutoScrollRef.current = null;
      pmLastPointerRef.current = null;
      if (!pmTouchRef.current?.isDragging) { pmTouchRef.current = null; return; }
      pmMouseDraggedRef.current = true;
      setTimeout(() => { pmMouseDraggedRef.current = false; }, 50);
      pmTouchRef.current = null;
      const over = pmDragOverRef.current;
      if (over && pmApplyDropRef.current) pmApplyDropRef.current(over);
      setPmDrag(null);
      setPmDragOver(null);
    };

    const onTouchMove = (e: TouchEvent) => {
      if (!pmTouchRef.current || e.touches.length !== 1) return;
      e.preventDefault();
      handlePmMove(e.touches[0].clientX, e.touches[0].clientY);
    };
    const onTouchEnd = () => handlePmEnd();
    const onMouseMove = (e: MouseEvent) => handlePmMove(e.clientX, e.clientY);
    const onMouseUp = () => handlePmEnd();

    document.addEventListener('touchmove', onTouchMove, { passive: false });
    document.addEventListener('touchend', onTouchEnd);
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      clearInterval(scrollTickId);
      pmAutoScrollRef.current = null;
      pmLastPointerRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPriorityMatrix]);

  // Focus edit input
  useEffect(() => {
    if (editingNode !== null) {
      editInputRef.current?.focus();
      if (editFromKeyRef.current) {
        const len = editInputRef.current?.value.length ?? 0;
        editInputRef.current?.setSelectionRange(len, len);
        editFromKeyRef.current = false;
      } else {
        editInputRef.current?.select();
      }
    }
  }, [editingNode]);

  // ── Realtime sync for linked child mindmaps ──────────────────────────────
  useEffect(() => {
    if (!currentId || mindmapLinks.length === 0) return;
    const channels = mindmapLinks.map(({ nodeId: placeholderNodeId, childId }, i) => {
      const ch = supabase.channel(`mm-child-${childId}`)
        .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'flowcharts', filter: `id=eq.${childId}` },
          (payload: { new?: { boxes?: string | MindmapNode[]; version?: number; title?: string } }) => {
            const raw = payload.new?.boxes;
            if (!raw) return;
            // Guard: link may have been removed while this event was in-flight
            if (!mindmapLinksRef.current.some(l => l.childId === childId)) return;
            const childNodes: MindmapNode[] = Array.isArray(raw) ? raw : JSON.parse(raw);
            // Base for the 3-way merge = the previously known child state, BEFORE we
            // overwrite it below with the incoming update.
            const oldBase = childBaseNodesRef.current[childId] ?? [];
            // Keep version/base up to date for the merge base in saveChildMindmap, regardless
            // of whether the local view actually changes below (own-echo check).
            if (typeof payload.new?.version === 'number') childVersionsRef.current[childId] = payload.new.version;
            childBaseNodesRef.current[childId] = childNodes;
            const offset = (i + 1) * LINK_OFFSET;
            const injected = childNodes.map(n => ({
              ...n,
              id: n.id + offset,
              parentId: n.parentId === null ? placeholderNodeId : n.parentId + offset,
              manualX: undefined, manualY: undefined, manualW: undefined,
            }));
            const baseInjected = oldBase.map(n => ({
              ...n,
              id: n.id + offset,
              parentId: n.parentId === null ? placeholderNodeId : n.parentId + offset,
            }));
            const ns = i + 1;
            // 3-way merge (base = previously known child state, local = own unsaved
            // state, remote = incoming update) so an unsaved local change to
            // this linked child doesn't disappear when someone else saves that
            // child in the meantime — previously the remote state was blindly adopted here.
            // Computed outside the setNodes updater (via nodesRef) so setUserPriorities below
            // can reliably use the same merged set.
            const existingNow = nodesRef.current.filter(n => nodeNS(n.id) === ns);
            const localCollapsed = new Map(existingNow.map(n => [n.id, n.collapsed]));
            const merged = mergeById<MindmapNode>(baseInjected, existingNow, injected).map(n => ({
              ...n,
              collapsed: localCollapsed.has(n.id) ? localCollapsed.get(n.id)! : n.collapsed,
            }));
            const liveChildIds = new Set(merged.map(n => n.id));
            setNodes(prev => {
              const existing = prev.filter(n => nodeNS(n.id) === ns);
              // Skip reorder if structure and content are identical (own echo of saveChildMindmap)
              const toKey = (arr: MindmapNode[]) => arr.map(n => `${n.id}|${n.text}|${n.parentId}|${n.collapsed ?? ''}`).join(';');
              if (existing.length === merged.length && toKey(existing) === toKey(merged)) return prev;
              // Replace in place to preserve array order; new nodes at the end
              const mergedById = new Map(merged.map(n => [n.id, n]));
              const existingIds = new Set(existing.map(n => n.id));
              const mergedIds = new Set(merged.map(n => n.id));
              const updated = prev
                .filter(n => nodeNS(n.id) !== ns || mergedIds.has(n.id))
                .map(n => nodeNS(n.id) === ns ? (mergedById.get(n.id) ?? n) : n);
              const newOnes = merged.filter(n => !existingIds.has(n.id));
              return [...updated, ...newOnes];
            });
            setUserPriorities(prev => {
              const stale = Object.keys(prev).filter(id => nodeNS(Number(id)) === ns && !liveChildIds.has(Number(id)));
              if (stale.length === 0) return prev;
              const next = { ...prev };
              stale.forEach(id => delete next[Number(id)]);
              return next;
            });
            setChildTitles(prev => ({ ...prev, [childId]: payload.new?.title ?? prev[childId] }));
          })
        .subscribe();
      return ch;
    });
    return () => { channels.forEach(ch => supabase.removeChannel(ch)); };
  // Re-subscribe when the set of linked children changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentId, mindmapLinks.length]);

  // ── Derived ─────────────────────────────────────────────────────────────────
  // This used to run unmemoized on every render — including every single pixel
  // of panning/zooming (panOffset/zoom are separate state, but any state update
  // anywhere re-runs the whole component body) — recomputing the full tree
  // layout and cloning every node into a new object each time. On a mindmap of
  // any real size that's the main cause of sluggish panning. None of these
  // values depend on panOffset/zoom/selection/etc — only on the node data and
  // per-user priorities — so memoizing on just those two skips all of that work
  // for anything that isn't an actual content edit.
  const { childrenMap, nodesForLayout, positions, levelById } = useMemo(() => {
    const childrenMap = buildChildrenMap(nodes);
    const rootId = nodes.find(n => n.parentId === null)?.id ?? 1;
    // Overlay per-user priorities onto nodes for layout/spacing calculations
    const nodesForLayout = nodes.map(n => ({ ...n, priority: (userPriorities[n.id] as 1|2|3|4|5|undefined) }));
    const positions = computeLayout(nodesForLayout, rootId);
    const levelById = computeAllLevels(nodes);
    return { childrenMap, nodesForLayout, positions, levelById };
  }, [nodes, userPriorities]);
  positionsRef.current = positions;

  // MinimapOverlay is React.memo'd (see MinimapOverlay.tsx), but was previously handed a
  // brand-new `items` array (with a getNodeLevel() call per node) built inline in JSX on
  // every render — defeating the memo and re-scanning every node on every single
  // mousemove while panning, for no visible reason (nothing minimap-relevant changes
  // during a pan). Depends only on the same inputs the main layout memo above already
  // isolated, so it stays referentially stable across pan/zoom/selection.
  const minimapItems = useMemo(() => nodes.map(node => {
    const pos = positions.get(node.id);
    if (!pos) return null;
    const level = levelById.get(node.id) ?? 0;
    const { w: rawW, h, baseW } = nodeSize(level, node.text, node.manualW);
    const dispW = rawW + nodeDispAdj(userPriorities[node.id], node.note, node.url);
    const vc = getVisualCenter(pos, dispW, baseW);
    return { x: vc.x - dispW / 2, y: vc.y - h / 2, w: dispW, h, color: node.customColor ?? node.color };
  }).filter((item): item is NonNullable<typeof item> => item !== null), [nodes, userPriorities, positions, levelById]);

  // ── History ──────────────────────────────────────────────────────────────────
  function pushHistory(newNodes: MindmapNode[]) {
    const newHist = [...historyRef.current.slice(0, histIdxRef.current + 1), JSON.parse(JSON.stringify(newNodes))];
    const trimmed = newHist.length > 20 ? newHist.slice(-20) : newHist;
    setHistory(trimmed);
    setHistIdx(trimmed.length - 1);
  }

  const undo = useCallback(() => {
    const idx = histIdxRef.current;
    if (idx > 0) { setHistIdx(idx - 1); setNodes(JSON.parse(JSON.stringify(historyRef.current[idx - 1]))); }
  }, []);

  const redo = useCallback(() => {
    const idx = histIdxRef.current;
    if (idx < historyRef.current.length - 1) { setHistIdx(idx + 1); setNodes(JSON.parse(JSON.stringify(historyRef.current[idx + 1]))); }
  }, []);

  // ── Editing ──────────────────────────────────────────────────────────────────
  const commitEdit = useCallback(() => {
    const id = editingNodeRef.current;
    if (id === null) return;
    if (!canEditMindmapRef.current) { setEditingNode(null); return; }
    const text = editTextRef.current.trim() || 'Topic';
    const newNodes = nodesRef.current.map(n => n.id === id ? { ...n, text } : n);
    // If the node was just created (empty text in the current history entry), overwrite
    // that entry in place so a single ctrl+Z removes the node and its connector line together.
    const lastHist = historyRef.current[histIdxRef.current];
    const nodeInHist = lastHist?.find((n: MindmapNode) => n.id === id);
    if (nodeInHist && nodeInHist.text === '') {
      const updatedHist = [...historyRef.current];
      updatedHist[histIdxRef.current] = JSON.parse(JSON.stringify(newNodes));
      setHistory(updatedHist);
      historyRef.current = updatedHist;
    } else {
      pushHistory(newNodes);
    }
    setNodes(newNodes);
    setEditingNode(null);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function startEdit(nodeId: number, initialText: string) {
    if (!canEditMindmapRef.current) return;
    setEditingNode(nodeId);
    setEditText(initialText);
  }

  // ── Node operations ───────────────────────────────────────────────────────────
  function addChild(parentId: number) {
    if (!canEditMindmapRef.current) return;
    const curNodes = nodesRef.current;
    const level = getNodeLevel(curNodes, parentId);
    const siblings = buildChildrenMap(curNodes).get(parentId) ?? [];
    const parentNode = curNodes.find(n => n.id === parentId);
    const color = colorModeRef.current === 'mono'
      ? MONO_COLOR
      : level === 0
        ? NODE_COLORS[siblings.length % NODE_COLORS.length]
        : (parentNode?.color ?? '#6366f1');

    // Determine the parent's ID namespace: 0 = own mindmap, >0 = child mindmap namespace
    const parentNS = nodeNS(parentId);
    let newId: number;
    if (parentNS > 0) {
      const nodesInNS = curNodes.filter(n => nodeNS(n.id) === parentNS);
      newId = nodesInNS.length > 0 ? Math.max(...nodesInNS.map(n => n.id)) + 1 : parentNS * LINK_OFFSET + 1;
    } else {
      newId = nextIdRef.current;
      setNextId(newId + 1);
    }
    const newNode: MindmapNode = { id: newId, text: '', parentId, collapsed: false, color };

    // On the left side (x < 0), index 0 sits visually at the bottom (perpY=-1 inverts the order).
    // Inserting the new node before the first existing sibling gives it index 0,
    // so it appears visually at the bottom of the group — consistent with the right side.
    const parentPos = positionsRef.current.get(parentId);
    const isLeftSide = parentPos ? parentPos.x < -10 : false;
    const mapped: MindmapNode[] = curNodes.map(n => ({ ...n, manualX: undefined, manualY: undefined, ...(n.id === parentId ? { collapsed: false } : {}) }));
    let newNodes: MindmapNode[];
    if (isLeftSide && siblings.length > 0) {
      const firstSiblingIdx = mapped.findIndex(n => n.id === siblings[0]);
      mapped.splice(firstSiblingIdx, 0, newNode);
      newNodes = mapped;
    } else {
      newNodes = [...mapped, newNode];
    }
    pushHistory(newNodes);
    setNodes(newNodes);
    if (parentNS === 0) nextIdRef.current = newId + 1; // update the ref right away, to handle rapid successive calls
    // Clear any stale priority for this new ID (last line of defense against ID recycling).
    setUserPriorities(prev => {
      if (prev[newId] === undefined) return prev;
      const next = { ...prev };
      delete next[newId];
      return next;
    });
    setSelectedNode(newNode.id);
    setTimeout(() => { startEdit(newNode.id, ''); ensureNodeVisible(newNode.id); }, 40);
  }

  // Name used for the auto-created "catch-all" child node under a branch when adding
  // a priority/schedule item straight from the matrix overview — plain node content,
  // not a UI string, so (like the 'Topic' default elsewhere) it's not run through i18n.
  const UNSORTED_NODE_TEXT = 'Ongesorteerd';

  /**
   * Adds a brand-new leaf node under branchId's "Ongesorteerd" child (creating that
   * child first if it doesn't exist yet), and immediately assigns it the given
   * quadrant (qi, 0-based) and, when adding from a day column, the given day (di).
   * Used by the Priority Matrix overview's per-quadrant/per-day "+" buttons — lets the
   * user grow the mindmap without leaving that view. branchId is always one of the
   * root's direct children (l1Nodes), always in this mindmap's own NS=0 namespace
   * (link-injected nodes attach deeper, never replace an l1 child), so this can use
   * nextIdRef directly like addChild's own NS===0 branch does.
   */
  function addPriorityNode(branchId: number, text: string, qi: number, di?: number) {
    if (!canEditMindmapRef.current) return;
    const curNodes = nodesRef.current;
    const branch = curNodes.find(n => n.id === branchId);
    if (!branch) return;
    const existingUnsorted = (buildChildrenMap(curNodes).get(branchId) ?? [])
      .map(id => curNodes.find(n => n.id === id))
      .find(n => n && n.text === UNSORTED_NODE_TEXT);

    let working: MindmapNode[] = curNodes.map(n => ({
      ...n,
      manualX: undefined,
      manualY: undefined,
      ...(n.id === branchId ? { collapsed: false } : {}),
      ...(existingUnsorted && n.id === existingUnsorted.id ? { collapsed: false } : {}),
    }));

    let unsortedId: number;
    if (existingUnsorted) {
      unsortedId = existingUnsorted.id;
    } else {
      unsortedId = nextIdRef.current;
      const unsortedNode: MindmapNode = { id: unsortedId, text: UNSORTED_NODE_TEXT, parentId: branchId, collapsed: false, color: branch.color };
      working = [...working, unsortedNode];
      nextIdRef.current = unsortedId + 1;
      setNextId(unsortedId + 1);
    }

    const leafId = nextIdRef.current;
    nextIdRef.current = leafId + 1;
    setNextId(leafId + 1);
    const leafNode: MindmapNode = { id: leafId, text, parentId: unsortedId, collapsed: false, color: branch.color };
    working = [...working, leafNode];

    pushHistory(working);
    setNodes(working);

    setUserPriorities(prev => ({ ...prev, [leafId]: qi + 1 }));
    setQuadrantOrder(prev => {
      const base: [number[], number[], number[], number[]] = prev ? [...prev.map(q => [...q])] as [number[], number[], number[], number[]] : [[], [], [], []];
      base[qi] = [...base[qi], leafId];
      return base;
    });
    if (di !== undefined) {
      setDayOrder(prev => {
        const next = prev.map(d => [...d]);
        while (next.length <= di) next.push([]);
        next[di] = [...next[di], leafId];
        return next;
      });
    }
  }

  /**
   * Combines selectedNode + selectedNodes into the effective multi-selection for a
   * context-menu action. Both state variables can coexist — e.g. after a
   * plain click followed by ctrl+click, the first node ends up in selectedNode and only the
   * second in selectedNodes. The canvas then visually shows a multi-selection (isSelected
   * checks both), but selectedNodes alone then contains only 1 node. Without this
   * combination, a context-menu action would only apply to the node that was
   * right-clicked, even though several appear to be selected.
   */
  function getMultiActionIds(anchorId: number): number[] {
    const combined = new Set(selectedNodes);
    if (selectedNode !== null) combined.add(selectedNode);
    return combined.size > 1 && combined.has(anchorId) ? [...combined] : [anchorId];
  }

  function deleteNode(id: number) {
    deleteNodes([id]);
  }

  function deleteNodes(ids: number[]) {
    if (!canEditMindmapRef.current) return;
    const curNodes = nodesRef.current;
    const cm = buildChildrenMap(curNodes);
    const toDelete = new Set<number>();
    for (const id of ids) {
      if (curNodes.find(n => n.id === id)?.parentId === null) continue;
      toDelete.add(id);
      getAllDescendants(cm, id).forEach(d => toDelete.add(d));
    }
    if (toDelete.size === 0) return;
    const newNodes = curNodes.filter(n => !toDelete.has(n.id)).map(n => ({ ...n, manualX: undefined as undefined, manualY: undefined as undefined }));
    pushHistory(newNodes);
    setNodes(newNodes);
    // Clean up priorities for deleted IDs so future nodes with the same ID
    // don't automatically inherit a priority (ID-recycling scenario).
    setUserPriorities(prev => {
      if (!Object.keys(prev).some(id => toDelete.has(Number(id)))) return prev;
      const next = { ...prev };
      toDelete.forEach(id => delete next[id]);
      return next;
    });
    setSelectedNode(null);
    setSelectedNodes(new Set());
  }

  function copyMindmapNodes(ids: number[], cut: boolean) {
    if (cut && !canEditMindmapRef.current) return;
    const curNodes = nodesRef.current;
    const cm = buildChildrenMap(curNodes);
    const toCollect = new Set<number>();
    for (const id of ids) {
      if (curNodes.find(n => n.id === id)?.parentId === null) continue; // skip root
      toCollect.add(id);
      getAllDescendants(cm, id).forEach(d => toCollect.add(d));
    }
    if (toCollect.size === 0) return;
    mindmapClipboardRef.current = curNodes.filter(n => toCollect.has(n.id));
    if (cut) {
      deleteNodes(ids.filter(id => curNodes.find(n => n.id === id)?.parentId !== null));
    }
  }

  function pasteMindmapNodes(targetId: number) {
    if (!canEditMindmapRef.current) return;
    const clipboard = mindmapClipboardRef.current;
    if (!clipboard || clipboard.length === 0) return;
    const curNodes = nodesRef.current;
    const clipboardIds = new Set(clipboard.map(n => n.id));
    // Assign new IDs
    let newId = nextIdRef.current;
    const idMap = new Map<number, number>();
    for (const node of clipboard) { idMap.set(node.id, newId++); }
    // Remap nodes: new id, new parentId, no manual position
    const pastedNodes: MindmapNode[] = clipboard.map(node => ({
      ...node,
      id: idMap.get(node.id)!,
      parentId: !clipboardIds.has(node.parentId as number) ? targetId : idMap.get(node.parentId as number)!,
      manualX: undefined,
      manualY: undefined,
    }));
    const newNodes = [...curNodes, ...pastedNodes];
    pushHistory(newNodes);
    setNodes(newNodes);
    setNextId(newId);
    nextIdRef.current = newId;
    // Select the pasted root nodes
    const pastedRootIds = clipboard
      .filter(n => !clipboardIds.has(n.parentId as number))
      .map(n => idMap.get(n.id)!);
    if (pastedRootIds.length === 1) {
      setSelectedNode(pastedRootIds[0]);
      setSelectedNodes(new Set());
    } else {
      setSelectedNodes(new Set(pastedRootIds));
      setSelectedNode(null);
    }
  }

  function buildMindmapSVGString(whiteBg: boolean): { svg: string; W: number; H: number } | null {
    const svgEl = svgRef.current;
    if (!svgEl) return null;

    const PAD = 60;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const [id, pos] of positionsRef.current) {
      const node = nodesRef.current.find(n => n.id === id);
      if (!node) continue;
      const level = getNodeLevel(nodesRef.current, id);
      const { w, h } = nodeSize(level, node.text, node.manualW);
      minX = Math.min(minX, pos.x - w / 2);
      minY = Math.min(minY, pos.y - h / 2);
      maxX = Math.max(maxX, pos.x + w / 2);
      maxY = Math.max(maxY, pos.y + h / 2);
    }
    if (!isFinite(minX)) return null;

    const vx = minX - PAD, vy = minY - PAD;
    const W = maxX - minX + PAD * 2, H = maxY - minY + PAD * 2;

    const clone = svgEl.cloneNode(true) as SVGSVGElement;
    clone.setAttribute('viewBox', `${vx} ${vy} ${W} ${H}`);
    clone.setAttribute('width', String(W));
    clone.setAttribute('height', String(H));

    // Remove background rects (dots pattern and gray bg)
    clone.querySelectorAll(':scope > rect').forEach(el => el.remove());
    clone.querySelector('defs')?.remove();

    // Add a white background for PNG, none for SVG (transparent)
    if (whiteBg) {
      const bg = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
      bg.setAttribute('x', String(vx)); bg.setAttribute('y', String(vy));
      bg.setAttribute('width', String(W)); bg.setAttribute('height', String(H));
      bg.setAttribute('fill', 'white');
      clone.insertBefore(bg, clone.firstChild);
    }

    const g = clone.querySelector('g');
    if (g) {
      g.querySelector('rect[fill="transparent"]')?.remove();
      g.setAttribute('transform', 'translate(0 0) scale(1)');
    }

    const svgStr = '<?xml version="1.0" encoding="UTF-8"?>\n' + new XMLSerializer().serializeToString(clone);
    return { svg: svgStr, W, H };
  }

  function exportAs(format: 'svg' | 'png') {
    const result = buildMindmapSVGString(format === 'png');
    if (!result) return;
    const { svg, W, H } = result;
    const filename = title || 'mindmap';
    if (format === 'svg') {
      const blob = new Blob([svg], { type: 'image/svg+xml' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `${filename}.svg`; a.click();
      URL.revokeObjectURL(url);
    } else {
      const blob = new Blob([svg], { type: 'image/svg+xml' });
      const url = URL.createObjectURL(blob);
      const img = new window.Image();
      img.onload = () => {
        const scale = 2;
        const canvas = document.createElement('canvas');
        canvas.width = W * scale; canvas.height = H * scale;
        const ctx = canvas.getContext('2d')!;
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.scale(scale, scale);
        ctx.drawImage(img, 0, 0, W, H);
        URL.revokeObjectURL(url);
        const a = document.createElement('a');
        a.href = canvas.toDataURL('image/png');
        a.download = `${filename}.png`; a.click();
      };
      img.src = url;
    }
  }

  function toggleCollapse(id: number) {
    const newNodes = nodesRef.current.map(n => n.id === id ? { ...n, collapsed: !n.collapsed } : n);
    setNodes(newNodes);
    // Patch the current history entry in place so the collapse state is preserved on undo
    const updatedHist = [...historyRef.current];
    updatedHist[histIdxRef.current] = JSON.parse(JSON.stringify(newNodes));
    historyRef.current = updatedHist;
    setHistory(updatedHist);
  }

  function collapseToLevel(targetLevel: number | null) {
    const cur = nodesRef.current;
    const newNodes = targetLevel === null
      ? cur.map(n => ({ ...n, collapsed: false }))
      : cur.map(n => ({ ...n, collapsed: getNodeLevel(cur, n.id) === targetLevel }));
    setNodes(newNodes);
    // Patch the current history entry in place so the collapse state is preserved on undo
    const updatedHist = [...historyRef.current];
    updatedHist[histIdxRef.current] = JSON.parse(JSON.stringify(newNodes));
    historyRef.current = updatedHist;
    setHistory(updatedHist);
  }

  /** Case-insensitive substring search over every node's text, regardless of collapse state. */
  function runSearch(query: string) {
    const q = query.trim().toLowerCase();
    if (!q) { setSearchMatchIds(null); return; }
    const cur = nodesRef.current;
    const matchIds = new Set(cur.filter(n => (n.text || '').toLowerCase().includes(q)).map(n => n.id));
    // A match hidden under a collapsed ancestor would highlight nothing visible — expand
    // every collapsed ancestor of every match first, same approach as collapseToLevel
    // above (patch the current history entry in place so this doesn't become its own
    // undo step, consistent with how toggling a single node's collapse state is handled).
    if (matchIds.size > 0) {
      const byId = new Map(cur.map(n => [n.id, n]));
      const toExpand = new Set<number>();
      matchIds.forEach(id => {
        let node = byId.get(id);
        while (node?.parentId != null) {
          const parent = byId.get(node.parentId);
          if (!parent) break;
          if (parent.collapsed) toExpand.add(parent.id);
          node = parent;
        }
      });
      if (toExpand.size > 0) {
        const newNodes = cur.map(n => toExpand.has(n.id) ? { ...n, collapsed: false } : n);
        setNodes(newNodes);
        const updatedHist = [...historyRef.current];
        updatedHist[histIdxRef.current] = JSON.parse(JSON.stringify(newNodes));
        historyRef.current = updatedHist;
        setHistory(updatedHist);
      }
    }
    setSearchMatchIds(matchIds);
  }

  function clearSearch() {
    setSearchQuery('');
    setSearchMatchIds(null);
  }

  /** Converts screen coordinates to canvas coordinates (corrects for SVG offset). */
  function toCanvas(screenX: number, screenY: number) {
    const r = svgRef.current?.getBoundingClientRect();
    const ox = r?.left ?? 0;
    const oy = r?.top ?? 0;
    return { cx: (screenX - ox - panOffset.x) / zoom, cy: (screenY - oy - panOffset.y) / zoom };
  }

  /** Finds the node whose bounding box contains canvas position (cx,cy), excluding the given IDs. */
  function getNodeAtCanvasPos(cx: number, cy: number, excludeIds: Set<number>): number | null {
    for (const [id, pos] of positionsRef.current) {
      if (excludeIds.has(id)) continue;
      const node = nodesRef.current.find(n => n.id === id);
      if (!node) continue;
      const level = getNodeLevel(nodesRef.current, id);
      const { w, h, baseW } = nodeSize(level, node.text);
      const dispW = w + nodeDispAdj(userPriorities[node.id], node.note, node.url);
      const vc = getVisualCenter(pos, dispW, baseW);
      if (cx >= vc.x - dispW / 2 && cx <= vc.x + dispW / 2 && cy >= vc.y - h / 2 && cy <= vc.y + h / 2) return id;
    }
    return null;
  }

  /** Cross-namespace reparenting assigns new IDs to moved nodes (every linked
   *  mindmap has its own ID namespace). userPriorities/userDurations are tracked separately
   *  from the node itself (local per-user state, key = node ID) — without this remap
   *  the old key would point to an ID that no longer exists, and the priority/duration
   *  would vanish without a trace after the move. */
  function remapUserPriorityMaps(idMap: Map<number, number>) {
    setUserPriorities(prev => {
      const next = { ...prev };
      let changed = false;
      idMap.forEach((newId, oldId) => {
        if (prev[oldId] !== undefined) { next[newId] = prev[oldId]; delete next[oldId]; changed = true; }
      });
      return changed ? next : prev;
    });
    setUserDurations(prev => {
      const next = { ...prev };
      let changed = false;
      idMap.forEach((newId, oldId) => {
        if (prev[oldId] !== undefined) { next[newId] = prev[oldId]; delete next[oldId]; changed = true; }
      });
      return changed ? next : prev;
    });
  }

  function applyReparent(nodeId: number, newParentId: number) {
    if (!canEditMindmapRef.current) return;
    const allNodes = nodesRef.current;
    const newParent = allNodes.find(n => n.id === newParentId);
    const parentLevel = getNodeLevel(allNodes, newParentId);
    const newSiblingCount = (buildChildrenMap(allNodes).get(newParentId) ?? []).length;
    const newColor = colorModeRef.current === 'mono'
      ? MONO_COLOR
      : parentLevel === 0
        ? NODE_COLORS[newSiblingCount % NODE_COLORS.length]
        : (newParent?.color ?? '#6366f1');
    const cm = buildChildrenMap(allNodes);
    const toMove = [nodeId, ...getAllDescendants(cm, nodeId)];
    const srcNS = nodeNS(nodeId);
    const dstNS = nodeNS(newParentId);

    if (srcNS !== dstNS) {
      // Cross-namespace drag: assign new IDs in the destination namespace
      const dstNodesMaxId = allNodes
        .filter(n => nodeNS(n.id) === dstNS)
        .reduce((mx, n) => Math.max(mx, n.id), dstNS === 0 ? 1 : dstNS * LINK_OFFSET);
      let nextId = dstNodesMaxId + 1;
      const idMap = new Map<number, number>();
      for (const id of toMove) idMap.set(id, nextId++);

      const newNodes = allNodes
        .filter(n => !idMap.has(n.id)) // remove from source namespace
        .map(n => {
          // update parentId references that point to the moved nodes
          if (n.parentId !== null && idMap.has(n.parentId)) {
            return { ...n, parentId: idMap.get(n.parentId)! };
          }
          return n;
        });
      for (const id of toMove) {
        const node = allNodes.find(n => n.id === id)!;
        const newId = idMap.get(id)!;
        const newParentForNode = id === nodeId
          ? newParentId
          : (node.parentId !== null && idMap.has(node.parentId) ? idMap.get(node.parentId)! : node.parentId);
        newNodes.push({
          ...node,
          id: newId,
          parentId: newParentForNode,
          color: node.customColor ? node.color : newColor,
          manualX: undefined, manualY: undefined,
        });
      }
      pushHistory(newNodes);
      setNodes(newNodes);
      remapUserPriorityMaps(idMap);
      return;
    }

    // Within the same namespace: only adjust parentId and color
    const toRecolor = new Set(toMove);
    const newNodes = allNodes.map(n => {
      if (n.id === nodeId) return { ...n, parentId: newParentId, color: newColor, manualX: undefined, manualY: undefined };
      if (toRecolor.has(n.id)) return n.customColor ? n : { ...n, color: newColor };
      return n;
    });
    pushHistory(newNodes);
    setNodes(newNodes);
  }

  /** Compute where to insert the drag node based on mouse canvas position.
   *  Returns { afterId, parentId } where afterId is the sibling node to insert after
   *  (null = insert before first sibling). Handles left-side reversed y-order. */
  function computeReorderTarget(cx: number, cy: number, dragNodeId: number): { afterId: number | null; parentId: number } | null {
    const allNodes = nodesRef.current;
    const dragNode = allNodes.find(n => n.id === dragNodeId);
    if (!dragNode || dragNode.parentId === null) return null;

    const parentId = dragNode.parentId;
    const parentNode = allNodes.find(n => n.id === parentId);
    const isL1 = parentNode?.parentId === null;
    const cm = buildChildrenMap(allNodes);
    const allSiblings = cm.get(parentId) ?? []; // flat array order

    // Remove no-op positions (current position): don't show an indicator if the node is already there
    const noOp = (afterId: number | null): boolean => {
      const currentIdx = allSiblings.indexOf(dragNodeId);
      if (afterId === null) return currentIdx === 0;
      const afterIdx = allSiblings.indexOf(afterId);
      return currentIdx === afterIdx + 1;
    };

    if (isL1) {
      // Level-1: split into right (first half) and left (second half)
      const half = Math.ceil(allSiblings.length / 2);
      const rightGroup = allSiblings.slice(0, half);
      const leftGroup = allSiblings.slice(half);
      const inRight = rightGroup.includes(dragNodeId);
      const targetRight = cx >= 0;

      if (targetRight) {
        const otherRight = rightGroup.filter(id => id !== dragNodeId);
        let afterId: number | null = null;
        // Right side: y increases with array index → afterId = last with y < cy
        for (const id of otherRight) {
          const y = positionsRef.current.get(id)?.y ?? 0;
          if (y < cy) afterId = id;
        }
        // Cross-side: moving from left to right → insert into right group
        if (!inRight && afterId === null && otherRight.length > 0) {
          // Insert before first right node (afterId stays null, which means before first sibling)
        } else if (!inRight && otherRight.length === 0) {
          afterId = null;
        }
        return noOp(afterId) ? null : { afterId, parentId };
      } else {
        const otherLeft = leftGroup.filter(id => id !== dragNodeId);
        let afterId: number | null = null;
        // Left side: y decreases with array index → afterId = last with y > cy
        for (const id of otherLeft) {
          const y = positionsRef.current.get(id)?.y ?? 0;
          if (y > cy) afterId = id;
        }
        // Cross-side: moving from right to left → insert after last right-group node
        if (inRight && otherLeft.length === 0) {
          const otherRight = rightGroup.filter(id => id !== dragNodeId);
          afterId = otherRight.length > 0 ? otherRight[otherRight.length - 1] : null;
        } else if (inRight && afterId === null && otherLeft.length > 0) {
          // Insert before first left node → afterId = last right-group node
          const otherRight = rightGroup.filter(id => id !== dragNodeId);
          afterId = otherRight.length > 0 ? otherRight[otherRight.length - 1] : null;
        }
        return noOp(afterId) ? null : { afterId, parentId };
      }
    }

    // Non-L1: reorder within same parent
    const otherSibs = allSiblings.filter(id => id !== dragNodeId);
    if (otherSibs.length === 0) return null;

    const parentPos = positionsRef.current.get(parentId);
    const isLeftSide = (parentPos?.x ?? 0) < -30;

    let afterId: number | null = null;
    if (!isLeftSide) {
      // Right side: y increases with array index
      for (const id of otherSibs) {
        if ((positionsRef.current.get(id)?.y ?? 0) < cy) afterId = id;
      }
    } else {
      // Left side: y decreases with array index
      for (const id of otherSibs) {
        if ((positionsRef.current.get(id)?.y ?? 0) > cy) afterId = id;
      }
    }
    return noOp(afterId) ? null : { afterId, parentId };
  }

  /** Move nodeId in the flat nodes array so that it appears after afterId among its siblings.
   *  Also handles L1 cross-side color reassignment. */
  function applyReorder(nodeId: number, afterId: number | null, targetParentId: number) {
    const allNodes = nodesRef.current;
    const dragNode = allNodes.find(n => n.id === nodeId);
    if (!dragNode) return;

    // Check if position would actually change
    const cm = buildChildrenMap(allNodes);
    const siblings = cm.get(targetParentId) ?? [];
    const currentIdx = siblings.indexOf(nodeId);
    const afterIdx = afterId !== null ? siblings.indexOf(afterId) : -1;
    if (afterId === null && currentIdx === 0) return;
    if (afterId !== null && currentIdx === afterIdx + 1) return;

    const withoutDrag = allNodes.filter(n => n.id !== nodeId);
    let updatedNode: MindmapNode = { ...dragNode, parentId: targetParentId, manualX: undefined as undefined, manualY: undefined as undefined };

    // Adopt color from the new parent if the parent changes
    if (targetParentId !== dragNode.parentId && !dragNode.customColor) {
      const newParent = allNodes.find(n => n.id === targetParentId);
      const parentLevel = getNodeLevel(allNodes, targetParentId);
      const existingSibCount = withoutDrag.filter(n => n.parentId === targetParentId).length;
      updatedNode = {
        ...updatedNode,
        color: colorModeRef.current === 'mono' ? MONO_COLOR
          : parentLevel === 0 ? NODE_COLORS[existingSibCount % NODE_COLORS.length]
          : (newParent?.color ?? '#6366f1'),
      };
    }

    let newNodes: MindmapNode[];
    if (afterId === null) {
      const firstSibIdx = withoutDrag.findIndex(n => n.parentId === targetParentId);
      const insertAt = firstSibIdx >= 0 ? firstSibIdx : withoutDrag.length;
      newNodes = [...withoutDrag.slice(0, insertAt), updatedNode, ...withoutDrag.slice(insertAt)];
    } else {
      const afterFlatIdx = withoutDrag.findIndex(n => n.id === afterId);
      const insertAt = afterFlatIdx >= 0 ? afterFlatIdx + 1 : withoutDrag.length;
      newNodes = [...withoutDrag.slice(0, insertAt), updatedNode, ...withoutDrag.slice(insertAt)];
    }

    pushHistory(newNodes);
    setNodes(newNodes);
  }

  function applyMultiReparent(draggedIds: number[], newParentId: number) {
    if (!canEditMindmapRef.current) return;
    const allNodes = nodesRef.current;
    const draggedSet = new Set(draggedIds);
    // Only move top-level nodes (whose parent is NOT also selected)
    const rootIds = draggedIds.filter(id => {
      const node = allNodes.find(n => n.id === id);
      return node && node.parentId !== null && !draggedSet.has(node.parentId);
    });
    if (rootIds.length === 0) return;

    const newParent = allNodes.find(n => n.id === newParentId);
    const parentLevel = getNodeLevel(allNodes, newParentId);
    const cm = buildChildrenMap(allNodes);
    const dstNS = nodeNS(newParentId);

    const toMoveSet = new Set<number>();
    for (const rootId of rootIds) {
      toMoveSet.add(rootId);
      getAllDescendants(cm, rootId).forEach(id => toMoveSet.add(id));
    }

    const allSameNS = rootIds.every(id => nodeNS(id) === dstNS);

    if (!allSameNS) {
      // Cross-namespace: assign new IDs for all moved nodes
      let maxDstId = allNodes
        .filter(n => nodeNS(n.id) === dstNS)
        .reduce((mx, n) => Math.max(mx, n.id), dstNS === 0 ? 1 : dstNS * LINK_OFFSET);
      const idMap = new Map<number, number>();
      for (const id of toMoveSet) idMap.set(id, ++maxDstId);

      const newNodes: MindmapNode[] = allNodes
        .filter(n => !toMoveSet.has(n.id))
        .map(n => n.parentId !== null && idMap.has(n.parentId) ? { ...n, parentId: idMap.get(n.parentId)! } : n);

      const existingSibCount = newNodes.filter(n => n.parentId === newParentId).length;
      for (const id of toMoveSet) {
        const node = allNodes.find(n => n.id === id)!;
        const newId = idMap.get(id)!;
        const isRoot = rootIds.includes(id);
        const newParentForNode = isRoot
          ? newParentId
          : (node.parentId !== null && idMap.has(node.parentId) ? idMap.get(node.parentId)! : node.parentId);
        const sibIdx = isRoot ? existingSibCount + rootIds.indexOf(id) : 0;
        const newColor = colorModeRef.current === 'mono' ? MONO_COLOR
          : isRoot ? (parentLevel === 0 ? NODE_COLORS[sibIdx % NODE_COLORS.length] : (newParent?.color ?? '#6366f1'))
          : node.color;
        newNodes.push({ ...node, id: newId, parentId: newParentForNode, color: node.customColor ? node.color : newColor, manualX: undefined, manualY: undefined });
      }
      pushHistory(newNodes);
      setNodes(newNodes);
      remapUserPriorityMaps(idMap);
      return;
    }

    // Same namespace: adjust parentId and color for root nodes and descendants
    const existingSibCount = allNodes.filter(n => n.parentId === newParentId && !toMoveSet.has(n.id)).length;

    // Compute color per root node; descendants inherit the same color
    const rootColorMap = new Map<number, string>();
    rootIds.forEach((rootId, idx) => {
      const sibIdx = existingSibCount + idx;
      const color = colorModeRef.current === 'mono' ? MONO_COLOR
        : parentLevel === 0 ? NODE_COLORS[sibIdx % NODE_COLORS.length]
        : (newParent?.color ?? '#6366f1');
      rootColorMap.set(rootId, color);
      getAllDescendants(cm, rootId).forEach(descId => rootColorMap.set(descId, color));
    });

    const newNodes = allNodes.map(n => {
      if (rootIds.includes(n.id)) {
        return { ...n, parentId: newParentId, color: n.customColor ? n.color : rootColorMap.get(n.id)!, manualX: undefined, manualY: undefined };
      }
      if (toMoveSet.has(n.id)) {
        const col = rootColorMap.get(n.id);
        return (col && !n.customColor) ? { ...n, color: col } : n;
      }
      return n;
    });
    pushHistory(newNodes);
    setNodes(newNodes);
  }

  function applyMultiReorder(nodeIds: number[], afterId: number | null, targetParentId: number) {
    if (!canEditMindmapRef.current) return;
    const allNodes = nodesRef.current;
    const toMoveSet = new Set(nodeIds);
    const withoutDragged = allNodes.filter(n => !toMoveSet.has(n.id));

    // Adopt color from the new parent if the parent changes
    const anyParentChange = nodeIds.some(id => {
      const n = allNodes.find(m => m.id === id);
      return n && n.parentId !== targetParentId;
    });
    const newParent = anyParentChange ? allNodes.find(n => n.id === targetParentId) : undefined;
    const parentLevel = anyParentChange ? getNodeLevel(allNodes, targetParentId) : 0;
    const existingSibCount = withoutDragged.filter(n => n.parentId === targetParentId).length;

    const movedNodes = nodeIds
      .map(id => allNodes.find(n => n.id === id))
      .filter((n): n is MindmapNode => !!n)
      .map((n, idx) => {
        const parentChanged = n.parentId !== targetParentId;
        const color = (anyParentChange && parentChanged && !n.customColor)
          ? (colorModeRef.current === 'mono' ? MONO_COLOR
            : parentLevel === 0 ? NODE_COLORS[(existingSibCount + idx) % NODE_COLORS.length]
            : (newParent?.color ?? '#6366f1'))
          : n.color;
        return { ...n, parentId: targetParentId, color, manualX: undefined as undefined, manualY: undefined as undefined };
      });

    let newNodes: MindmapNode[];
    if (afterId === null) {
      const firstSibIdx = withoutDragged.findIndex(n => n.parentId === targetParentId);
      const insertAt = firstSibIdx >= 0 ? firstSibIdx : withoutDragged.length;
      newNodes = [...withoutDragged.slice(0, insertAt), ...movedNodes, ...withoutDragged.slice(insertAt)];
    } else {
      const afterFlatIdx = withoutDragged.findIndex(n => n.id === afterId);
      const insertAt = afterFlatIdx >= 0 ? afterFlatIdx + 1 : withoutDragged.length;
      newNodes = [...withoutDragged.slice(0, insertAt), ...movedNodes, ...withoutDragged.slice(insertAt)];
    }
    pushHistory(newNodes);
    setNodes(newNodes);
  }

  function toggleHighlight(ids: number[]) {
    if (!canEditMindmapRef.current) return;
    const idSet = new Set(ids);
    // Multi-select toggles as one group: if any of them isn't highlighted yet,
    // turn highlighting ON for all of them; only turn it off when they all
    // already are — otherwise a mixed selection would just flip randomly
    // depending on each node's own current state.
    const anyUnhighlighted = nodesRef.current.some(n => idSet.has(n.id) && !n.highlighted);
    const newNodes = nodesRef.current.map(n => idSet.has(n.id) ? { ...n, highlighted: anyUnhighlighted } : n);
    pushHistory(newNodes);
    setNodes(newNodes);
  }


  function toggleColorMode() {
    if (colorModeRef.current === 'multi') {
      // Do NOT change node.color — the render already overrides via colorMode === 'mono' ? MONO_COLOR : node.color.
      // If we did change node.color, the original colors would be lost on save and after reload.
      setColorMode('mono');
    } else {
      // Assign new colors for ns=0 nodes: L1 nodes get palette colors,
      // their ns=0 children inherit the color of their L1 ancestor.
      // Child-namespace nodes (linked mindmaps) are NOT touched — they keep their own color scheme.
      const allNodes = nodesRef.current;
      const rootNode = allNodes.find(n => n.parentId === null);
      if (!rootNode) { setColorMode('multi'); return; }
      const nsZeroNodes = allNodes.filter(n => nodeNS(n.id) === 0);
      const cm = buildChildrenMap(nsZeroNodes);
      const l1 = cm.get(rootNode.id) ?? [];
      const colorMap = new Map<number, string>();
      l1.forEach((id, i) => {
        const color = NODE_COLORS[i % NODE_COLORS.length];
        colorMap.set(id, color);
        getAllDescendants(cm, id).forEach(descId => colorMap.set(descId, color));
      });
      const newNodes = allNodes.map(n => {
        if (nodeNS(n.id) !== 0) return n; // leave child-namespace nodes untouched
        return { ...n, color: colorMap.get(n.id) ?? n.color };
      });
      pushHistory(newNodes);
      setNodes(newNodes);
      setColorMode('multi');
    }
  }

  // ── Mindmap link helpers ──────────────────────────────────────────────────────

  /** Namespace of a node ID: 0 = parent, 1 = first child, etc. */
  function nodeNS(id: number) { return Math.floor(id / LINK_OFFSET); }

  /**
   * Shape guard against foreign (FlowchartBuilder Box) objects ending up in a
   * mindmap's node array — every real MindmapNode constructor sets `collapsed`
   * explicitly, while a Box never has that field (it has `departmentColor`,
   * `width`/`height`, `type`, `importantFinish`, ... instead). Applied at every
   * point a node array is read from the DB (load, linked-child injection, the
   * conflict-merge's fresh-fetch) so a corrupted row can never be re-adopted
   * into live state again, however it got there. See bug_mindmap_autosave_no_merge_guard.
   */
  function isMindmapNode(n: unknown): n is MindmapNode {
    return !!n && typeof n === 'object' && typeof (n as MindmapNode).collapsed === 'boolean';
  }

  /** Injects all linked child nodes into the parent node array. */
  async function injectLinkedChildren(parentId: string, parentNodes: MindmapNode[]): Promise<{ allNodes: MindmapNode[]; links: Array<{ nodeId: number; childId: string }>; titles: Record<string, string> }> {
    const { data: linkRows } = await supabase
      .from('mindmap_links').select('*').eq('parent_id', parentId);
    if (!linkRows || linkRows.length === 0) return { allNodes: parentNodes, links: [], titles: {} };

    const links: Array<{ nodeId: number; childId: string }> = linkRows.map((r: { node_id: number; child_id: string }) => ({ nodeId: r.node_id, childId: r.child_id }));
    let allNodes = [...parentNodes];
    const titles: Record<string, string> = {};

    for (let i = 0; i < links.length; i++) {
      const { nodeId: placeholderNodeId, childId } = links[i];
      const offset = (i + 1) * LINK_OFFSET;
      const { data: childData } = await supabase.from('flowcharts').select('boxes, title, version').eq('id', childId).single();
      if (!childData) continue;
      const childNodes: MindmapNode[] = Array.isArray(childData.boxes) ? childData.boxes : JSON.parse(childData.boxes || '[]');
      titles[childId] = childData.title;
      childVersionsRef.current[childId] = typeof childData.version === 'number' ? childData.version : 0;
      childBaseNodesRef.current[childId] = childNodes;
      const injected = childNodes.map(n => ({
        ...n,
        id: n.id + offset,
        parentId: n.parentId === null ? placeholderNodeId : n.parentId + offset,
        manualX: undefined, manualY: undefined, manualW: undefined,
      }));
      allNodes = [...allNodes, ...injected];
    }
    return { allNodes, links, titles };
  }

  /**
   * Compute child nodes for one child and save them, with optimistic locking.
   * This write touches a DIFFERENT flowchart record than the one currently open (currentId), and
   * therefore fell outside the version protection of the main save (fix 3.1) — a
   * concurrent edit via another parent, or the child itself opened standalone, could
   * be silently overwritten here. Same 3-way-merge pattern as the main save,
   * but tracked per childId (childVersionsRef/childBaseNodesRef) because a child's
   * namespace/linkIndex can shift when another link is removed.
   */
  async function saveChildMindmap(childId: string, linkIndex: number, allNodes: MindmapNode[]) {
    const namespace = linkIndex + 1;
    const offset = namespace * LINK_OFFSET;
    const childEffective = allNodes.filter(n => nodeNS(n.id) === namespace);
    if (childEffective.length === 0) {
      logger.warn(`[saveChildMindmap] ns=${namespace} — skipping save (0 nodes, prevents accidental overwrite)`);
      return;
    }
    const childOriginal: MindmapNode[] = childEffective.map(n => ({
      ...n,
      id: n.id - offset,
      parentId: n.parentId === null || nodeNS(n.parentId) === 0 ? null : n.parentId - offset,
      linkedMindmapId: undefined,
    }));
    const expectedVersion = childVersionsRef.current[childId] ?? 0;
    const outcome = await writeWithConflictMerge<MindmapNode[], { boxes: unknown; version: number }>({
      write: async () => {
        const { data, error } = await supabase.from('flowcharts')
          .update({ boxes: JSON.stringify(childOriginal) })
          .eq('id', childId)
          .eq('version', expectedVersion)
          .select('updated_at, version')
          .maybeSingle();
        if (error || !data) return { data: null, error };
        return { data: { updated_at: data.updated_at, version: data.version }, error: null };
      },
      expectedVersion,
      fetchFresh: async () => {
        const { data, error } = await supabase.from('flowcharts').select('boxes, version').eq('id', childId).single();
        return { data, error };
      },
      parseFresh: (row) => Array.isArray(row.boxes) ? row.boxes as MindmapNode[] : (() => { try { return JSON.parse((row.boxes as string) || '[]'); } catch { return []; } })(),
      isEmpty: (nodes) => nodes.length === 0,
      hasLocalContent: childOriginal.length > 0,
      base: childBaseNodesRef.current[childId] ?? [],
      local: childOriginal,
      merge: (b, l, r) => mergeById<MindmapNode>(b ?? [], l, r),
    });

    switch (outcome.outcome) {
      case 'written':
        childVersionsRef.current[childId] = outcome.version;
        childBaseNodesRef.current[childId] = childOriginal;
        return;
      case 'empty-remote-protected':
        // An empty remote state must not wipe out a populated linked mindmap.
        childVersionsRef.current[childId] = outcome.version;
        return;
      case 'no-real-conflict':
        // The update touched 0 rows for a reason other than a version conflict
        // (e.g. no write access to the child) — nothing to merge.
        logger.warn(`[saveChildMindmap] update raakte 0 rijen maar versie ongewijzigd (childId=${childId})`);
        return;
      case 'error':
        logger.error('[saveChildMindmap] DB error:', outcome.error);
        return;
      case 'merged': {
        const merged = outcome.mergedContent;
        childVersionsRef.current[childId] = outcome.version;
        childBaseNodesRef.current[childId] = merged;
        const placeholderNodeId = mindmapLinksRef.current[linkIndex]?.nodeId;
        setNodes(prev => {
          const existing = prev.filter(n => nodeNS(n.id) === namespace);
          const localCollapsed = new Map(existing.map(n => [n.id, n.collapsed]));
          const reInjected = merged.map(n => ({
            ...n,
            id: n.id + offset,
            parentId: n.parentId === null ? placeholderNodeId : n.parentId + offset,
            manualX: undefined, manualY: undefined, manualW: undefined,
          })).map(n => ({
            ...n,
            collapsed: localCollapsed.has(n.id) ? localCollapsed.get(n.id)! : n.collapsed,
          }));
          const kept = prev.filter(n => nodeNS(n.id) !== namespace);
          return [...kept, ...reInjected];
        });
        logger.warn(`🔀 [saveChildMindmap] sync conflict opgelost via merge (childId=${childId}, nieuwe versie=${outcome.version})`);
        return;
      }
    }
  }

  // ── Save / Load ───────────────────────────────────────────────────────────────
  async function save() {
    if (!canEditMindmapRef.current && currentIdRef.current) return;
    setIsSaving(true);
    try {
      const allNodes = nodesRef.current;
      const links = mindmapLinksRef.current;
      // Only save parent-namespace nodes (NS=0) in this flowchart record
      const parentNodes = links.length > 0 ? allNodes.filter(n => nodeNS(n.id) === 0) : allNodes;
      // Strip priority — priorities are per-user and are not stored in the shared DB data
      const nodesForDB = parentNodes.map(n => ({ ...n, priority: undefined, duration: undefined }));
      const payload: {
        title: string;
        user_id: string;
        boxes: string;
        arrows: string;
        departments: string;
        data: string;
        map_type: 'mindmap';
        updated_by_email: string | null;
        project_id?: string;
      } = {
        title: titleRef.current,
        user_id: user.id,
        boxes: JSON.stringify(nodesForDB),
        arrows: JSON.stringify([]),
        departments: JSON.stringify([]),
        data: JSON.stringify({ nextId: nextIdRef.current, dayOrder, dayLabels, quadrantOrder }),
        map_type: 'mindmap' as const,
        updated_by_email: user?.email || null,
      };
      const activeId = currentIdRef.current;
      let savedId = activeId;
      // What was ultimately actually saved — equal to parentNodes/title,
      // unless a version conflict below leads to a merge.
      let nodesToSave = parentNodes;
      let titleToSave = titleRef.current;
      if (activeId) {
        // Optimistic locking: only write if nobody else has saved in the meantime.
        // Without this version check, a manual save (button/Ctrl+S) would silently
        // overwrite a concurrent change from another user — the periodic autosave
        // already handled this correctly, but the manual save did not.
        const buildParentPayload = (nodesArg: MindmapNode[], titleArg: string) => ({
          title: titleArg,
          boxes: JSON.stringify(nodesArg.map(n => ({ ...n, priority: undefined, duration: undefined }))),
          arrows: JSON.stringify([]),
          departments: JSON.stringify([]),
          data: JSON.stringify({ nextId: nextIdRef.current, dayOrder: dayOrderRef.current, dayLabels: dayLabelsRef.current, quadrantOrder: quadrantOrderRef.current }),
          updated_by_email: user?.email || null,
        });
        // Version conflict: someone else wrote first. Fetch the fresh state, merge
        // instead of overwriting, and retry once with the merged state.
        const outcome = await writeWithConflictMerge<{ nodes: MindmapNode[]; title: string }, { title: string | null; boxes: unknown; version: number }>({
          write: async () => {
            const { data, error } = await supabase.from('flowcharts')
              .update(buildParentPayload(nodesToSave, titleToSave))
              .eq('id', activeId)
              .eq('version', mindmapVersionRef.current)
              .select('updated_at, version')
              .maybeSingle();
            if (error || !data) return { data: null, error };
            return { data: { updated_at: data.updated_at, version: data.version }, error: null };
          },
          expectedVersion: mindmapVersionRef.current,
          fetchFresh: async () => {
            const { data, error } = await supabase.from('flowcharts').select('title, boxes, version').eq('id', activeId).single();
            return { data, error };
          },
          parseFresh: (row) => ({
            nodes: Array.isArray(row.boxes) ? row.boxes as MindmapNode[] : (() => { try { return JSON.parse((row.boxes as string) || '[]'); } catch { return []; } })(),
            title: row.title || '',
          }),
          isEmpty: (content) => content.nodes.length === 0,
          hasLocalContent: parentNodes.length > 0,
          base: (() => { try { return lastSyncedContentRef.current ? JSON.parse(lastSyncedContentRef.current) : null; } catch { return null; } })(),
          local: { nodes: parentNodes, title: titleToSave },
          merge: (b, l, r) => {
            // priority/duration are per-user, never written to the shared row, so
            // "the fresh copy has none" must never look like "the local value was
            // cleared" — always keep the local ones after mergeById resolves the
            // id-level conflict on everything else (text, position, collapsed, ...).
            const localById = new Map(l.nodes.map(n => [n.id, n]));
            const mergedNodes = mergeById<MindmapNode>(b?.nodes ?? [], l.nodes, r.nodes).map(n => {
              const loc = localById.get(n.id);
              return loc ? { ...n, collapsed: loc.collapsed, priority: loc.priority, duration: loc.duration } : n;
            });
            return { nodes: mergedNodes, title: mergeScalar(b?.title ?? l.title, l.title, r.title) };
          },
        });

        let savedRow: { updated_at: string; version: number } | null = null;
        if (outcome.outcome === 'error') throw outcome.error;
        if (outcome.outcome === 'written') {
          savedRow = { updated_at: outcome.updatedAt, version: outcome.version };
        } else if (outcome.outcome === 'empty-remote-protected') {
          // An empty remote state must not wipe out a populated mindmap — just adopt the
          // version so the next save/autosave can write again.
          mindmapVersionRef.current = outcome.version;
        } else if (outcome.outcome === 'merged') {
          const merged = outcome.mergedContent;
          mindmapVersionRef.current = outcome.version;
          setNodes(prev => [...merged.nodes, ...prev.filter(n => nodeNS(n.id) !== 0)]);
          setTitle(merged.title);
          if (merged.nodes.length > 0) setNextId(Math.max(...merged.nodes.map(n => n.id)) + 1);
          logger.warn('🔀 Mindmap sync conflict opgelost via merge tijdens handmatig opslaan');
          nodesToSave = merged.nodes;
          titleToSave = merged.title;
          const retry = await supabase.from('flowcharts')
            .update(buildParentPayload(nodesToSave, titleToSave))
            .eq('id', activeId)
            .eq('version', mindmapVersionRef.current)
            .select('updated_at, version')
            .maybeSingle();
          if (retry.error) throw retry.error;
          savedRow = retry.data;
          // If the retry also conflicts, we give up for this click — the
          // next autosave tick will still pick up the now-merged local state.
        }
        // 'no-real-conflict': the update touched 0 rows for a reason other than a
        // version conflict (e.g. no write access) — nothing to merge, savedRow stays null.
        // Note: project_id is intentionally not in update payload — changes via ProjectList are preserved
        if (savedRow) {
          registerOwnWrite(savedRow.updated_at);
          if (typeof savedRow.version === 'number') mindmapVersionRef.current = savedRow.version;
          lastLocalSaveTimeRef.current = Date.now();
          lastSyncedContentRef.current = JSON.stringify({ nodes: nodesToSave, title: titleToSave });
        }
      } else {
        if (currentProjectId) payload.project_id = currentProjectId;
        const { data: d, error } = await supabase.from('flowcharts').insert(payload).select().single();
        if (error) throw error;
        savedId = d.id;
        setCurrentId(d.id);
        prioritiesLoadedForRef.current = d.id;
        dayOrderLoadedForRef.current = d.id;
        // Owner access is created by DB trigger — no manual insert needed
        const shareToken = Array.from(crypto.getRandomValues(new Uint8Array(16)))
          .map(b => b.toString(16).padStart(2, '0')).join('');
        await supabase.from('flowchart_share_settings').insert({
          flowchart_id: d.id,
          share_token: shareToken,
          link_sharing_enabled: false,
        });
      }
      // Also save every linked child mindmap
      for (let i = 0; i < links.length; i++) {
        await saveChildMindmap(links[i].childId, i, allNodes);
      }
      // Save a version snapshot (keep max 30)
      if (savedId) {
        await supabase.from('flowchart_versions').insert({
          flowchart_id: savedId,
          title: titleToSave,
          boxes: JSON.stringify(nodesToSave),
          arrows: JSON.stringify([]),
          departments: JSON.stringify([]),
          saved_by: user.id,
        });
        const { data: allV } = await supabase
          .from('flowchart_versions')
          .select('id, saved_at')
          .eq('flowchart_id', savedId)
          .order('saved_at', { ascending: false });
        if (allV && allV.length > 30) {
          const toDelete = allV.slice(30).map(v => v.id);
          await supabase.from('flowchart_versions').delete().in('id', toDelete);
        }
        const summary = diffNodeChanges(lastLoggedNodesRef.current, nodesToSave);
        lastLoggedNodesRef.current = JSON.parse(JSON.stringify(nodesToSave));
        await logChange(savedId, `Saved: ${summary}`);
      }
    } catch (e: any) {
      alert(tr('saveFailed'));
    } finally {
      setIsSaving(false);
    }
  }

  async function loadMindmap(id: string) {
    const { data, error } = await supabase.from('flowcharts').select('*').eq('id', id).single();
    if (error || !data) return;
    lastLoadedAtRef.current = Date.now();
    lastLocalSaveTimeRef.current = 0; // Reset so staleness guard uses lastLoadedAt as baseline
    mindmapVersionRef.current = typeof data.version === 'number' ? data.version : 0;
    const loadedNodes: MindmapNode[] = (Array.isArray(data.boxes) ? data.boxes : JSON.parse(data.boxes || '[]')).filter(isMindmapNode);
    const meta = data.data ? (typeof data.data === 'string' ? JSON.parse(data.data) : data.data) : {};
    // nextId based only on parent nodes (NS=0), so child ID ranges don't collide
    const calcNextId = loadedNodes.length > 0 ? Math.max(...loadedNodes.map(n => n.id)) + 1 : 2;
    // Inject linked child mindmaps
    const { allNodes, links, titles } = await injectLinkedChildren(id, loadedNodes);
    setMindmapLinks(links);
    setChildTitles(titles);
    setNodes(allNodes);
    // Never blindly trust the stored nextId: if this mindmap was edited as a linked
    // child via a parent, saveChildMindmap only writes `boxes` back
    // (not `data`), which means the stored nextId can lag behind the highest
    // existing node ID. A lower nextId leads to ID collisions (ghost nodes) as soon as
    // a new node is created standalone here — so always take the maximum.
    setNextId(Math.max(meta.nextId ?? 0, calcNextId));
    setTitle(data.title || 'Untitled Mindmap');
    setCurrentId(id);
    setCurrentProjectId(data.project_id ?? null);
    setSelectedNode(null);
    setEditingNode(null);
    // allNodes (parent + injected linked-child nodes), not loadedNodes (parent only)
    // — nodes itself is set to allNodes just above, so undoing all the way back to
    // this very first history entry used to silently drop every linked mindmap's
    // nodes, since they were never actually IN that entry to begin with.
    setHistory([JSON.parse(JSON.stringify(allNodes))]);
    setHistIdx(0);
    setPanOffset({ x: window.innerWidth / 2, y: (window.innerHeight - 40) / 2 });
    // Load per-user priorities + durations: DB → localStorage (fallback) → seed (owner, backwards compat)
    // DB is always the primary source so cross-device sync works.
    let loadedPriorities: Record<number, number> = {};
    let loadedDurations: Record<number, string> = {};
    let loadedCompleted: Record<number, boolean> = {};
    let loadedQOrder: number[][] | null = null;
    const { data: prioRow } = await supabase
      .from('user_mindmap_priorities')
      .select('priorities, durations, completed, quadrant_order')
      .eq('user_id', user.id)
      .eq('flowchart_id', id)
      .single();
    if (prioRow?.priorities && Object.keys(prioRow.priorities as object).length > 0) {
      // Filter down to existing node IDs — otherwise deleted nodes could pass their
      // priority on to new nodes with the same ID (ID-recycling via a nextId reset).
      const existingIds = new Set(loadedNodes.map(n => n.id));
      const raw = prioRow.priorities as Record<number, number>;
      loadedPriorities = Object.fromEntries(Object.entries(raw).filter(([id]) => existingIds.has(Number(id))));
      if (prioRow.quadrant_order) loadedQOrder = prioRow.quadrant_order as number[][];
      if (prioRow.durations) loadedDurations = prioRow.durations as Record<number, string>;
      if (prioRow.completed) loadedCompleted = prioRow.completed as Record<number, boolean>;
    } else {
      // No DB data → fall back to localStorage (backwards compat / first visit)
      const storedPriosRaw = localStorage.getItem(`mm-user-priorities-${user.id}-${id}`);
      let storedPriosParsed: Record<number, number> = {};
      if (storedPriosRaw) { try { storedPriosParsed = JSON.parse(storedPriosRaw); } catch {} }
      const hasLocalPrios = Object.keys(storedPriosParsed).length > 0;
      if (hasLocalPrios) {
        loadedPriorities = storedPriosParsed;
        const storedQ = localStorage.getItem(`mm-user-qorder-${user.id}-${id}`);
        if (storedQ) { try { loadedQOrder = JSON.parse(storedQ); } catch {} }
        const storedDurs = localStorage.getItem(`mm-user-durations-${user.id}-${id}`);
        if (storedDurs) { try { loadedDurations = JSON.parse(storedDurs); } catch {} }
        const storedCompl = localStorage.getItem(`mm-user-completed-${user.id}-${id}`);
        if (storedCompl) { try { loadedCompleted = JSON.parse(storedCompl); } catch {} }
        // Sync localStorage priorities to the DB so other devices can see them
        supabase.from('user_mindmap_priorities').upsert(
          { user_id: user.id, flowchart_id: id, priorities: loadedPriorities, durations: loadedDurations, completed: loadedCompleted, updated_at: new Date().toISOString() },
          { onConflict: 'user_id,flowchart_id' }
        ).then(() => {}, (err: any) => logger.error('[priorities] localStorage→DB sync failed:', err));
      } else if (data.user_id === user.id) {
        // Seed from node.priority/duration in the DB (migration from pre-per-user data)
        loadedNodes.forEach(n => {
          if (n.priority) loadedPriorities[n.id] = n.priority;
          if (n.duration) loadedDurations[n.id] = n.duration;
        });
      }
    }
    // Merge priorities + durations + completed from linked child mindmaps (with namespace offset)
    // Always query the DB for all child mindmaps — localStorage only as a fallback.
    const childIds = links.map(l => l.childId);
    const childDbRows: Record<string, { priorities: Record<number, number>; durations: Record<number, string>; completed: Record<number, boolean> }> = {};
    if (childIds.length > 0) {
      const { data: childRows } = await supabase
        .from('user_mindmap_priorities')
        .select('flowchart_id, priorities, durations, completed')
        .eq('user_id', user.id)
        .in('flowchart_id', childIds);
      (childRows ?? []).forEach((row: { flowchart_id: string; priorities: Record<number, number> | null; durations: Record<number, string> | null; completed: Record<number, boolean> | null }) => {
        childDbRows[row.flowchart_id] = { priorities: row.priorities ?? {}, durations: row.durations ?? {}, completed: row.completed ?? {} };
      });
    }
    links.forEach(({ childId }, i) => {
      const offset = (i + 1) * LINK_OFFSET;
      const ns = i + 1;
      // Existing IDs in this child NS (without offset) — for filtering stale priorities
      const existingChildIds = new Set(allNodes.filter(n => nodeNS(n.id) === ns).map(n => n.id - offset));
      let childPrios: Record<string, number> | null = null;
      let childDurs: Record<string, string> | null = null;
      let childCompl: Record<string, boolean> | null = null;
      if (childDbRows[childId] && Object.keys(childDbRows[childId].priorities).length > 0) {
        // DB has data → use it
        childPrios = childDbRows[childId].priorities as Record<string, number>;
        childDurs = childDbRows[childId].durations as Record<string, string>;
        childCompl = childDbRows[childId].completed as Record<string, boolean>;
      } else {
        // Fall back to localStorage
        const childPriosStr = localStorage.getItem(`mm-user-priorities-${user.id}-${childId}`);
        const childDursStr = localStorage.getItem(`mm-user-durations-${user.id}-${childId}`);
        const childComplStr = localStorage.getItem(`mm-user-completed-${user.id}-${childId}`);
        if (childPriosStr) {
          try { childPrios = JSON.parse(childPriosStr); } catch {}
          if (childDursStr) { try { childDurs = JSON.parse(childDursStr); } catch {} }
          if (childComplStr) { try { childCompl = JSON.parse(childComplStr); } catch {} }
        }
      }
      if (childPrios) {
        Object.entries(childPrios)
          .filter(([nid]) => existingChildIds.has(Number(nid)))
          .forEach(([nid, p]) => { loadedPriorities[Number(nid) + offset] = p; });
      }
      if (childDurs) {
        Object.entries(childDurs)
          .filter(([nid]) => existingChildIds.has(Number(nid)))
          .forEach(([nid, d]) => { loadedDurations[Number(nid) + offset] = d; });
      }
      if (childCompl) {
        Object.entries(childCompl)
          .filter(([nid]) => existingChildIds.has(Number(nid)))
          .forEach(([nid, c]) => { loadedCompleted[Number(nid) + offset] = c; });
      }
    });
    setUserPriorities(loadedPriorities);
    setUserDurations(loadedDurations);
    setUserCompleted(loadedCompleted);
    prioritiesLoadedForRef.current = id;
    try {
      // Set quadrantOrder: validate if loaded, otherwise build it from priorities
      const applyQOrder = (rawQ: number[][]) => {
        const corrected = rawQ.map((q, qi) => q.filter(nid => loadedPriorities[nid] === qi + 1));
        const alreadyIn = new Set(corrected.flat());
        Object.entries(loadedPriorities).forEach(([nid, p]) => {
          if (p >= 1 && p <= 4 && !alreadyIn.has(Number(nid))) corrected[p - 1].push(Number(nid));
        });
        setQuadrantOrder(corrected as [number[], number[], number[], number[]]);
      };
      if (loadedQOrder) {
        applyQOrder(loadedQOrder);
      } else {
        const built: number[][] = [[], [], [], []];
        Object.entries(loadedPriorities).forEach(([nid, p]) => {
          if (p >= 1 && p <= 4) built[p - 1].push(Number(nid));
        });
        setQuadrantOrder(built.some(q => q.length > 0) ? built as [number[], number[], number[], number[]] : null);
      }
      {
        const savedD = localStorage.getItem(`mm-dorder-${id}`);
        if (savedD) {
          setDayOrder(JSON.parse(savedD));
        } else {
          const { data: dRow } = await supabase
            .from('user_mindmap_priorities')
            .select('day_order')
            .eq('user_id', user.id)
            .eq('flowchart_id', id)
            .single();
          if (dRow?.day_order) {
            setDayOrder(dRow.day_order as number[][]);
          } else if (meta.dayOrder !== undefined) {
            setDayOrder(meta.dayOrder);
          } else {
            setDayOrder([[], [], [], [], []]);
          }
        }
        if (meta.dayLabels !== undefined) {
          setDayLabels(meta.dayLabels);
        }
      }
    } catch { setQuadrantOrder(null); setDayOrder([[], [], [], [], []]); }
    // Only now is dayOrder's own load for THIS id actually settled — see the matching
    // guard on the dayOrder persist effect's own comment for why this can't just reuse
    // prioritiesLoadedForRef (that one flips to this id earlier, while dayOrder's DB
    // fetch above is still in flight, which isn't late enough to prevent the exact bug
    // this ref exists to prevent).
    dayOrderLoadedForRef.current = id;
    // Seed lastSyncedContentRef with the loaded state so the first auto-save is skipped if there
    // are no local changes — prevents a stale session from overwriting the DB.
    // (lastLocalSaveTimeRef was already reset at the start of this function.)
    lastSyncedContentRef.current = JSON.stringify({ nodes: loadedNodes, title: data.title || '' });
    // Change-log diff baseline — without this, lastLoggedNodesRef would stay at [] (or at the previous
    // mindmap) until the first save-log after opening, which would then report all nodes as "Added"
    // instead of the actual edit (same bug as in FlowchartBuilder.load()).
    // lastAutoLogRef set to 0 so the very next edit logs immediately (with this correct baseline)
    // instead of waiting up to 5 minutes.
    lastLoggedNodesRef.current = loadedNodes;
    lastAutoLogRef.current = 0;
  }

  async function deleteMindmap(id: string) {
    if (!window.confirm(tr('confirmTrashMindmap'))) return;
    await supabase.from('flowcharts').update({ deleted_at: new Date().toISOString() }).eq('id', id);
    if (id === currentId) newMindmap();
  }

  function newMindmap() {
    setNodes([FRESH_ROOT]);
    setNextId(2);
    setTitle('Untitled Mindmap');
    setCurrentId(null);
    setCurrentProjectId(null);
    setHistory([[FRESH_ROOT]]);
    setHistIdx(0);
    setSelectedNode(null);
    setEditingNode(null);
    setQuadrantOrder(null);
    setDayOrder([[], [], [], [], []]);
    setUserPriorities({});
    setMindmapLinks([]);
    setChildTitles({});
    // Change-log diff baseline reset — see the matching comment in loadMindmap().
    lastLoggedNodesRef.current = [FRESH_ROOT];
    lastAutoLogRef.current = 0;
  }

  async function loadChangeLog() {
    if (!currentId) return;
    setLoadingHistory(true);
    const { data } = await supabase
      .from('change_log')
      .select('user_email, action, created_at')
      .eq('flowchart_id', currentId)
      .order('created_at', { ascending: false })
      .limit(100);
    setHistoryVersions(data || []);
    setLoadingHistory(false);
  }

  async function logChange(flowchartId: string, action: string) {
    try {
      await supabase.from('change_log').insert({
        flowchart_id: flowchartId,
        user_id: user.id,
        user_email: user.email || null,
        action,
      });
    } catch (_) {}
  }

  loadMindmapRef.current = loadMindmap; // always keep up to date for use in event handlers

  function handleRestoreVersion(versionData: SavedFlowchart, flowchart: FlowchartWithAccess) {
    const restored: MindmapNode[] = Array.isArray(versionData.boxes) ? versionData.boxes as MindmapNode[] : JSON.parse((versionData.boxes as string) || '[]');
    setTitle(versionData.title || flowchart.title || 'Untitled Mindmap');
    setNodes(restored);
    setHistory([JSON.parse(JSON.stringify(restored))]);
    setHistIdx(0);
    setCurrentId(flowchart.id);
    setCurrentProjectId(flowchart.project_id ?? null);
    // Change-log diff baseline reset — see the matching comment in loadMindmap().
    lastLoggedNodesRef.current = restored;
    lastAutoLogRef.current = 0;
    logChange(flowchart.id, `Version restored from ${new Date(versionData.saved_at ?? Date.now()).toLocaleString()}`);
  }

  // Periodic autosave to the DB (only if autoSaveEnabled + currentId)
  const lastAutoLogRef = useRef<number>(0);
  const lastLoggedNodesRef = useRef<MindmapNode[]>([]);

  function diffNodeChanges(prev: MindmapNode[], curr: MindmapNode[]): string {
    const prevMap = new Map(prev.map(n => [n.id, n]));
    const currMap = new Map(curr.map(n => [n.id, n]));
    const added = curr.filter(n => n.parentId !== null && !prevMap.has(n.id));
    const removed = prev.filter(n => n.parentId !== null && !currMap.has(n.id));
    const renamed = curr.filter(n => { const p = prevMap.get(n.id); return p && p.text !== n.text && n.text; });
    const parts: string[] = [];
    if (added.length > 0) {
      const names = added.slice(0, 3).map(n => `"${n.text || '…'}"`).join(', ');
      parts.push(`Added: ${names}${added.length > 3 ? ` +${added.length - 3}` : ''}`);
    }
    if (removed.length > 0) {
      const names = removed.slice(0, 3).map(n => `"${n.text || '…'}"`).join(', ');
      parts.push(`Deleted: ${names}${removed.length > 3 ? ` +${removed.length - 3}` : ''}`);
    }
    if (renamed.length > 0) {
      const names = renamed.slice(0, 2).map(n => `"${n.text}"`).join(', ');
      parts.push(`Renamed: ${names}${renamed.length > 2 ? ` +${renamed.length - 2}` : ''}`);
    }
    return parts.length > 0 ? parts.join('; ') : 'Minor changes';
  }

  // A single autosave attempt (parent + linked children), shared by the 30s interval
  // below and by the flush-on-hide listener after it. Reads everything via refs, so it's safe
  // to use as a standalone closure without any stale-state risk.
  async function attemptAutosaveTick() {
    const currentId = currentIdRef.current;
    if (!currentId || !canEditMindmapRef.current) return;
    // Only save NS=0 nodes (not the injected linked child nodes)
    const links = mindmapLinksRef.current;
    const allNodes = nodesRef.current;
    let parentNodes = links.length > 0 ? allNodes.filter(n => nodeNS(n.id) === 0) : allNodes;
    // Safety net: dedupe by id before anything else touches parentNodes. A node id can
    // never legitimately appear twice — this is a last line of defense against the
    // merge-conflict duplication bug fixed below, in case some other path reintroduces it.
    {
      const seenIds = new Set<number>();
      parentNodes = parentNodes.filter(n => seenIds.has(n.id) ? false : (seenIds.add(n.id), true));
    }
    // Guard: don't save if there are no local changes — avoids unnecessary writes
    // and protects against stale sessions overwriting remote changes.
    const currentContent = JSON.stringify({ nodes: parentNodes, title: titleRef.current });
    if (currentContent === lastSyncedContentRef.current) return;
    let titleToSave = titleRef.current;
    const attemptWrite = () => supabase.from('flowcharts').update({
      title: titleToSave,
      boxes: JSON.stringify(parentNodes.map(n => ({ ...n, priority: undefined, duration: undefined }))),
      arrows: JSON.stringify([]),
      departments: JSON.stringify([]),
      data: JSON.stringify({ nextId: nextIdRef.current, dayOrder: dayOrderRef.current, dayLabels: dayLabelsRef.current, quadrantOrder: quadrantOrderRef.current }),
      updated_by_email: user?.email || null,
    }).eq('id', currentId).eq('version', mindmapVersionRef.current).select('updated_at, version').maybeSingle();

    // Version conflict: another user wrote first. Fetch the fresh state, merge,
    // and retry once — so linked child mindmaps still get saved this tick
    // instead of waiting for the next 30s tick (which never comes
    // if the tab closes in the meantime).
    const outcome = await writeWithConflictMerge<{ nodes: MindmapNode[]; title: string }, { title: string | null; boxes: unknown; version: number }>({
      write: async () => {
        const { data, error } = await attemptWrite();
        if (error || !data) return { data: null, error };
        return { data: { updated_at: data.updated_at, version: data.version }, error: null };
      },
      expectedVersion: mindmapVersionRef.current,
      fetchFresh: async () => {
        const { data, error } = await supabase.from('flowcharts').select('title, boxes, version').eq('id', currentId).single();
        return { data, error };
      },
      parseFresh: (row) => ({
        nodes: (Array.isArray(row.boxes) ? row.boxes as MindmapNode[] : (() => { try { return JSON.parse((row.boxes as string) || '[]'); } catch { return []; } })()).filter(isMindmapNode),
        title: row.title || '',
      }),
      isEmpty: (content) => content.nodes.length === 0,
      hasLocalContent: parentNodes.length > 0,
      base: (() => { try { return lastSyncedContentRef.current ? JSON.parse(lastSyncedContentRef.current) : null; } catch { return null; } })(),
      local: { nodes: parentNodes, title: titleToSave },
      merge: (b, l, r) => {
        // collapsed/priority/duration are local per-user state (not synced) — always keep the local ones
        const localById = new Map(l.nodes.map(n => [n.id, n]));
        const mergedNodes = mergeById<MindmapNode>(b?.nodes ?? [], l.nodes, r.nodes).map(n => {
          const loc = localById.get(n.id);
          return loc ? { ...n, collapsed: loc.collapsed, priority: loc.priority, duration: loc.duration } : n;
        });
        return { nodes: mergedNodes, title: mergeScalar(b?.title ?? l.title, l.title, r.title) };
      },
    });

    // The write/fetch attempts above always targeted the mindmap that started this tick
    // (currentId, captured above), but if the user has since navigated to a
    // different mindmap, the result must no longer be applied to
    // what's currently on screen (same reasoning as currentIdRef in useFlowchartSync).
    if (currentIdRef.current !== currentId) return;

    let savedRow: { updated_at: string; version: number } | null = null;
    if (outcome.outcome === 'error') {
      // Don't mark as synced — the next interval tick (30s) will retry.
      logger.error('Mindmap autosave failed:', outcome.error);
      setSyncFailed(true);
      return;
    } else if (outcome.outcome === 'no-real-conflict') {
      // Not a real conflict (e.g. no write access) — treat as a sync failure.
      setSyncFailed(true);
      return;
    } else if (outcome.outcome === 'empty-remote-protected') {
      mindmapVersionRef.current = outcome.version; // an empty remote state must not wipe out a populated mindmap
      return;
    } else if (outcome.outcome === 'written') {
      savedRow = { updated_at: outcome.updatedAt, version: outcome.version };
    } else {
      const merged = outcome.mergedContent;
      mindmapVersionRef.current = outcome.version;
      // Preserving prev's NS!==0 entries is only valid when this mindmap actually HAS
      // linked children — otherwise `merged.nodes` already covers every node (see
      // parentNodes above), and re-appending prev's own (already-merged) copies on top
      // duplicated every node whose id happened to be >= LINK_OFFSET on each conflict
      // round. See bug_mindmap_autosave_no_merge_guard.
      setNodes(prev => links.length > 0 ? [...merged.nodes, ...prev.filter(n => nodeNS(n.id) !== 0)] : merged.nodes);
      setTitle(merged.title);
      if (merged.nodes.length > 0) setNextId(Math.max(...merged.nodes.map(n => n.id)) + 1);
      logger.warn('🔀 Mindmap sync conflict opgelost via merge (nieuwe versie:', outcome.version, ')');
      parentNodes = merged.nodes;
      titleToSave = merged.title;
      const retry = await attemptWrite();
      if (retry.error) {
        logger.error('Mindmap autosave retry failed:', retry.error);
        setSyncFailed(true);
        return;
      }
      if (currentIdRef.current !== currentId) return;
      savedRow = retry.data;
      // If the retry also conflicts (a rare race) — give up for this tick, the next
      // 30s tick or the next flush-on-hide will retry the merged state.
      if (!savedRow) return;
    }
    registerOwnWrite(savedRow.updated_at);
    if (typeof savedRow.version === 'number') mindmapVersionRef.current = savedRow.version;
    setSyncFailed(false);
    // Also save every linked child mindmap
    for (let i = 0; i < links.length; i++) {
      await saveChildMindmap(links[i].childId, i, allNodes);
    }
    lastLocalSaveTimeRef.current = Date.now();
    lastSyncedContentRef.current = JSON.stringify({ nodes: parentNodes, title: titleToSave });
    // Log to changelog at most once per 5 minutes
    const now = Date.now();
    if (now - lastAutoLogRef.current >= 5 * 60 * 1000) {
      lastAutoLogRef.current = now;
      const summary = diffNodeChanges(lastLoggedNodesRef.current, parentNodes);
      lastLoggedNodesRef.current = JSON.parse(JSON.stringify(parentNodes));
      logChange(currentId, `Auto-saved: ${summary}`);
    }
  }

  useEffect(() => {
    if (!currentId) return;
    const interval = setInterval(() => {
      // Guard: don't save if the tab is hidden — prevents a background session or a
      // just-woken tab from overwriting the DB before the visibilitychange reload can happen.
      // (On hiding itself, a flush still happens immediately — see the listener below.)
      if (document.hidden) return;
      attemptAutosaveTick();
    }, 30000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentId]);

  // Flush pending changes as soon as the tab goes to the background (tab switch, minimize,
  // close) — otherwise a just-made change waits for the next 30s tick, which never
  // comes if the tab is already closed by then.
  useEffect(() => {
    const onHidden = () => {
      if (document.visibilityState === 'hidden') attemptAutosaveTick();
    };
    document.addEventListener('visibilitychange', onHidden);
    return () => document.removeEventListener('visibilitychange', onHidden);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Session timeout: reload mindmap if the tab was inactive for > 30 min
  useEffect(() => {
    const onVisible = async () => {
      if (document.visibilityState !== 'visible') return;
      const id = currentIdRef.current;
      if (!id || !lastLoadedAtRef.current) return;
      if (Date.now() - lastLoadedAtRef.current < 30 * 60 * 1000) return;
      // Flush any pending local edit BEFORE reloading — not just "nice to have":
      // the flush-on-hide listener above already tried this once, as a plain
      // fire-and-forget call, right as the tab was going into the background —
      // exactly when a browser is most likely to throttle or freeze its JS/network
      // before that write actually lands (more likely the longer the tab then sits
      // hidden, which is exactly when this 30-minute path triggers). Awaiting one
      // more attempt here, now that the tab is active again and has a real chance
      // to complete, means the reload right after either (a) picks up our own
      // edit merged into the DB by attemptAutosaveTick's own conflict-merge, or
      // (b) at worst re-attempts a save that's still failing — either way, this
      // reload no longer blindly discards a local edit the hide-flush silently
      // lost. Confirmed via a user report: editing, switching tabs, and returning
      // after a long time intermittently showed the just-made edit reverted.
      await attemptAutosaveTick();
      if (currentIdRef.current !== id) return; // navigated away during the await
      if (loadMindmapRef.current) await loadMindmapRef.current(id);
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist per-user priorities + durations to localStorage + DB
  useEffect(() => {
    if (!currentId || !user?.id || prioritiesLoadedForRef.current !== currentId) return;
    const parentPrios: Record<number, number> = {};
    const parentDurs: Record<number, string> = {};
    const parentCompleted: Record<number, boolean> = {};
    const childPriosByChildId: Record<string, Record<number, number>> = {};
    const childDursByChildId: Record<string, Record<number, string>> = {};
    const childCompletedByChildId: Record<string, Record<number, boolean>> = {};
    Object.entries(userPriorities).forEach(([nid, p]) => {
      const id = Number(nid);
      const ns = nodeNS(id);
      if (ns === 0) {
        parentPrios[id] = p;
      } else {
        const childId = mindmapLinks[ns - 1]?.childId;
        if (childId) {
          if (!childPriosByChildId[childId]) childPriosByChildId[childId] = {};
          childPriosByChildId[childId][id - ns * LINK_OFFSET] = p;
        }
      }
    });
    Object.entries(userDurations).forEach(([nid, d]) => {
      const id = Number(nid);
      const ns = nodeNS(id);
      if (ns === 0) {
        parentDurs[id] = d;
      } else {
        const childId = mindmapLinks[ns - 1]?.childId;
        if (childId) {
          if (!childDursByChildId[childId]) childDursByChildId[childId] = {};
          childDursByChildId[childId][id - ns * LINK_OFFSET] = d;
        }
      }
    });
    Object.entries(userCompleted).forEach(([nid, c]) => {
      const id = Number(nid);
      const ns = nodeNS(id);
      if (ns === 0) {
        parentCompleted[id] = c;
      } else {
        const childId = mindmapLinks[ns - 1]?.childId;
        if (childId) {
          if (!childCompletedByChildId[childId]) childCompletedByChildId[childId] = {};
          childCompletedByChildId[childId][id - ns * LINK_OFFSET] = c;
        }
      }
    });
    localStorage.setItem(`mm-user-priorities-${user.id}-${currentId}`, JSON.stringify(parentPrios));
    localStorage.setItem(`mm-user-durations-${user.id}-${currentId}`, JSON.stringify(parentDurs));
    localStorage.setItem(`mm-user-completed-${user.id}-${currentId}`, JSON.stringify(parentCompleted));
    // Union of every child mindmap that has ANY of priorities/durations/completed —
    // a child with only a completed flag (no priority/duration ever set for it) would
    // otherwise never get its own localStorage/DB write below.
    const allChildIds = new Set([
      ...Object.keys(childPriosByChildId), ...Object.keys(childDursByChildId), ...Object.keys(childCompletedByChildId),
    ]);
    allChildIds.forEach(childId => {
      localStorage.setItem(`mm-user-priorities-${user.id}-${childId}`, JSON.stringify(childPriosByChildId[childId] ?? {}));
      localStorage.setItem(`mm-user-durations-${user.id}-${childId}`, JSON.stringify(childDursByChildId[childId] ?? {}));
      localStorage.setItem(`mm-user-completed-${user.id}-${childId}`, JSON.stringify(childCompletedByChildId[childId] ?? {}));
    });
    // Also save to the DB so priorities/durations/completed survive a cache clear and are available on other devices.
    // Only save if there's something to save — never write an empty {} to the DB (avoids overwriting existing data).
    const uid = user.id; const fid = currentId;
    const hasParentData = Object.keys(parentPrios).length > 0 || Object.keys(parentDurs).length > 0 || Object.keys(parentCompleted).length > 0;
    if (hasParentData) {
      supabase.from('user_mindmap_priorities').upsert(
        { user_id: uid, flowchart_id: fid, priorities: parentPrios, durations: parentDurs, completed: parentCompleted, updated_at: new Date().toISOString() },
        { onConflict: 'user_id,flowchart_id' }
      ).then(() => {}, (err: any) => logger.error('[priorities] DB save failed:', err));
    }
    allChildIds.forEach(childId => {
      const prios = childPriosByChildId[childId] ?? {};
      const durs = childDursByChildId[childId] ?? {};
      const compl = childCompletedByChildId[childId] ?? {};
      const hasChildData = Object.keys(prios).length > 0 || Object.keys(durs).length > 0 || Object.keys(compl).length > 0;
      if (hasChildData) {
        supabase.from('user_mindmap_priorities').upsert(
          { user_id: uid, flowchart_id: childId, priorities: prios, durations: durs, completed: compl, updated_at: new Date().toISOString() },
          { onConflict: 'user_id,flowchart_id' }
        ).then(() => {}, (err: any) => logger.error('[priorities] child DB save failed:', err));
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userPriorities, userDurations, userCompleted, currentId, user?.id, mindmapLinks]);

  useEffect(() => {
    // quadrantOrder is set (to either a real array or null) earlier in the SAME
    // loadMindmap try-block that dayOrder's own loading finishes — dayOrderLoadedForRef
    // flipping to currentId means quadrantOrder has also already settled for it, so
    // it doubles as this effect's guard too. Without it, this has the exact same race
    // dayOrderLoadedForRef was added to fix for dayOrder (see that effect's comment):
    // switching mindmaps sets currentId before this one's quadrant_order has loaded,
    // and `quadrantOrder === null` alone doesn't catch the case where the PREVIOUS
    // mindmap had a non-null quadrantOrder still sitting in state.
    if (!currentId || !user?.id || quadrantOrder === null || dayOrderLoadedForRef.current !== currentId) return;
    localStorage.setItem(`mm-user-qorder-${user.id}-${currentId}`, JSON.stringify(quadrantOrder));
    // Update quadrantOrder in the DB (the row is created by the priorities effect)
    const uid = user.id; const fid = currentId;
    supabase.from('user_mindmap_priorities')
      .update({ quadrant_order: quadrantOrder, updated_at: new Date().toISOString() })
      .eq('user_id', uid).eq('flowchart_id', fid)
      .then(() => {}, () => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quadrantOrder, currentId, user?.id]);

  // Update always-current function refs (called from touch handlers)
  applyReparentRef.current = applyReparent;
  applyReorderRef.current = applyReorder;
  computeReorderTargetRef.current = computeReorderTarget;
  startEditRef.current = startEdit;
  commitEditRef.current = commitEdit;
  reparentDragRef.current = reparentDrag;

  // ── Keyboard shortcuts ────────────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (!isActiveRef.current) return;
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      if (e.target === noteEditorRef.current) return;
      if (e.ctrlKey && e.key === 'z') { e.preventDefault(); undo(); }
      if (e.ctrlKey && e.key === 'y') { e.preventDefault(); redo(); }
      if (e.ctrlKey && e.key === 's') { e.preventDefault(); save(); }
      if (e.key === 'Delete' && canEditMindmapRef.current) {
        const multi = selectedNodesRef.current;
        if (multi.size > 0) deleteNodes([...multi]);
        else if (selectedNodeRef.current !== null) deleteNode(selectedNodeRef.current);
      }
      // Copy: Ctrl+C
      if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
        e.preventDefault();
        const multi = selectedNodesRef.current;
        const ids = multi.size > 0 ? [...multi] : (selectedNodeRef.current !== null ? [selectedNodeRef.current] : []);
        if (ids.length > 0) copyMindmapNodes(ids, false);
      }
      // Cut: Ctrl+X
      if ((e.ctrlKey || e.metaKey) && e.key === 'x') {
        e.preventDefault();
        const multi = selectedNodesRef.current;
        const ids = multi.size > 0 ? [...multi] : (selectedNodeRef.current !== null ? [selectedNodeRef.current] : []);
        if (ids.length > 0) copyMindmapNodes(ids, true);
      }
      // Paste: Ctrl+V — pasted as children of the selected node
      if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
        e.preventDefault();
        if (selectedNodeRef.current !== null) pasteMindmapNodes(selectedNodeRef.current);
      }
      if (e.key === 'Tab' && selectedNodeRef.current !== null && canEditMindmapRef.current) { e.preventDefault(); addChild(selectedNodeRef.current); }
      if (e.key === 'Enter' && selectedNodeRef.current !== null && editingNodeRef.current === null && canEditMindmapRef.current) {
        e.preventDefault();
        const cur = nodesRef.current.find(n => n.id === selectedNodeRef.current);
        if (cur?.parentId !== null && cur?.parentId !== undefined) addChild(cur.parentId);
      }

      // Select node + type a character → go straight into edit mode and replace the content
      if (
        e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey &&
        selectedNodeRef.current !== null && editingNodeRef.current === null &&
        canEditMindmapRef.current
      ) {
        e.preventDefault();
        editFromKeyRef.current = true;
        startEditRef.current(selectedNodeRef.current, e.key);
        return;
      }

      // Arrow keys: spatial navigation based on position on the map
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key) && editingNodeRef.current === null && selectedNodeRef.current !== null) {
        e.preventDefault();
        const cur = selectedNodeRef.current;
        const pos = positionsRef.current;
        const curPos = pos.get(cur);
        if (!curPos) return;

        // Determine which direction we're looking for
        const goRight = e.key === 'ArrowRight';
        const goLeft  = e.key === 'ArrowLeft';
        const goUp    = e.key === 'ArrowUp';
        const goDown  = e.key === 'ArrowDown';

        const isHoriz = goRight || goLeft;

        // Find the best candidate within a cone, then fall back to a wider cone
        const findBest = (maxRatio: number): number | null => {
          let best: number | null = null;
          let bestDist = Infinity;
          pos.forEach((p, id) => {
            if (id === cur) return;
            const dx = p.x - curPos.x;
            const dy = p.y - curPos.y;
            const primary   = isHoriz ? dx : dy;
            const secondary = isHoriz ? dy : dx;
            // Must lie in the desired direction
            if (goRight && dx <= 5) return;
            if (goLeft  && dx >= -5) return;
            if (goDown  && dy <= 5) return;
            if (goUp    && dy >= -5) return;
            // Within the cone: lateral deviation may be at most maxRatio × the main direction
            if (Math.abs(secondary) > Math.abs(primary) * maxRatio) return;
            const dist = Math.sqrt(dx * dx + dy * dy);
            if (dist < bestDist) { bestDist = dist; best = id; }
          });
          return best;
        };

        // Try progressively wider cones (60° → 80° → everything) so there's always a result
        const best = findBest(1.0) ?? findBest(2.0) ?? findBest(Infinity);
        if (best !== null) setSelectedNodeRef.current(best);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  // addChild/deleteNode/save use mutable refs internally — no re-subscription needed
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [undo, redo]);

  // Re-activate the active editor when the browser window regains focus (e.g. after copying from another app).
  // This makes Ctrl+V work after switching apps.
  useEffect(() => {
    const onWindowFocus = () => {
      setTimeout(() => {
        if (editingNodeRef.current !== null) {
          editInputRef.current?.focus();
        } else if (noteEditorRef.current) {
          noteEditorRef.current.focus();
        }
      }, 50);
    };
    window.addEventListener('focus', onWindowFocus);
    return () => window.removeEventListener('focus', onWindowFocus);
  }, []);

  // ── Touch: 1-finger gestures (pan, node interaction) ───────────────────────────
  // 2-finger pinch is handled separately at the window level
  useEffect(() => {
    const el = svgRef.current;
    if (!el) return;

    type Gesture =
      | { type: 'pan';       prevX: number; prevY: number }
      | { type: 'node-hold'; nodeId: number; startX: number; startY: number; clientX: number; clientY: number }
      | { type: 'node-drag'; nodeId: number };

    let gesture: Gesture | null = null;
    let longPressTimer: ReturnType<typeof setTimeout> | null = null;
    let lastTapTime = 0;
    let lastTapNodeId = -1;

    const toCanvas = (clientX: number, clientY: number) => {
      const r = el.getBoundingClientRect();
      return {
        cx: (clientX - r.left - panOffsetRef.current.x) / zoomRef.current,
        cy: (clientY - r.top  - panOffsetRef.current.y) / zoomRef.current,
      };
    };

    // Inline hit-test via always-current refs (no stale-closure problem)
    const hitNode = (cx: number, cy: number, exclude: Set<number> = new Set()): number | null => {
      for (const [id, pos] of positionsRef.current) {
        if (exclude.has(id)) continue;
        const node = nodesRef.current.find(n => n.id === id);
        if (!node) continue;
        const level = getNodeLevel(nodesRef.current, id);
        const { w, h, baseW } = nodeSize(level, node.text);
        const dispW = w + nodeDispAdj(userPriorities[node.id], node.note, node.url);
        const vc = getVisualCenter(pos, dispW, baseW);
        if (cx >= vc.x - dispW / 2 && cx <= vc.x + dispW / 2 &&
            cy >= vc.y - h / 2    && cy <= vc.y + h / 2) return id;
      }
      return null;
    };

    const clearLongPress = () => {
      if (longPressTimer !== null) { clearTimeout(longPressTimer); longPressTimer = null; }
    };

    const onStart = (e: TouchEvent) => {
      // Prevent browser scroll/zoom only for touches on the canvas itself (not on toolbar/sidebar).
      // Interactive elements within the canvas (buttons, inputs) are also excluded
      // so their click events keep working.
      const isOnCanvas = el.contains(e.target as Node);
      const isInteractive = !!(e.target as Element)?.closest('button, input, select, textarea, a, [role="button"]');
      if (isOnCanvas && !isInteractive) e.preventDefault();
      // Stop the active node edit on a touch outside the edit overlay
      if (editingNodeRef.current !== null) { commitEditRef.current(); }
      clearLongPress();
      // 2 fingers → handled by the window-level pinch effect
      if (e.touches.length !== 1) { gesture = null; return; }

      const t = e.touches[0];
      const { cx, cy } = toCanvas(t.clientX, t.clientY);
      const nodeId = hitNode(cx, cy);

      if (nodeId === null) {
        setSelectedNode(null);
        setSelectedNodes(new Set());
        gesture = { type: 'pan', prevX: t.clientX, prevY: t.clientY };
        return;
      }

      const now = Date.now();
      const isDblTap = lastTapNodeId === nodeId && now - lastTapTime < 350;
      lastTapTime = now;
      lastTapNodeId = nodeId;

      if (isDblTap) {
        const node = nodesRef.current.find(n => n.id === nodeId);
        if (node) startEditRef.current(nodeId, node.text);
        gesture = null;
        return;
      }

      setSelectedNode(nodeId);
      setSelectedNodes(new Set());
      setContextMenu(null);

      gesture = { type: 'node-hold', nodeId, startX: cx, startY: cy, clientX: t.clientX, clientY: t.clientY };

      longPressTimer = setTimeout(() => {
        longPressTimer = null;
        gesture = null;
        setContextMenu({ x: t.clientX, y: t.clientY, nodeId });
      }, 500);
    };

    const onMove = (e: TouchEvent) => {
      // 2+ fingers → the pinch effect (window level) takes over
      if (e.touches.length >= 2) { clearLongPress(); gesture = null; e.preventDefault(); return; }
      if (!gesture) return;
      e.preventDefault();

      if (gesture.type === 'pan') {
        const dx = e.touches[0].clientX - gesture.prevX;
        const dy = e.touches[0].clientY - gesture.prevY;
        setPanOffset(prev => ({ x: prev.x + dx, y: prev.y + dy }));
        gesture = { type: 'pan', prevX: e.touches[0].clientX, prevY: e.touches[0].clientY };
      } else if (gesture.type === 'node-hold') {
        const { cx, cy } = toCanvas(e.touches[0].clientX, e.touches[0].clientY);
        const ddx = cx - gesture.startX, ddy = cy - gesture.startY;
        if (Math.sqrt(ddx * ddx + ddy * ddy) > 8 / zoomRef.current) {
          // 1-finger move on a node → pan (not node-drag)
          clearLongPress();
          gesture = { type: 'pan', prevX: e.touches[0].clientX, prevY: e.touches[0].clientY };
        }
      } else if (gesture.type === 'node-drag') {
        const t = e.touches[0];
        const { cx, cy } = toCanvas(t.clientX, t.clientY);
        const { nodeId } = gesture;
        const cm = buildChildrenMap(nodesRef.current);
        const rdIds = reparentDragRef.current?.nodeIds ?? [nodeId];
        const excluded = new Set(rdIds.flatMap(id => [id, ...getAllDescendants(cm, id)]));
        const target = hitNode(cx, cy, excluded);
        setReparentDrag(prev => prev ? { ...prev, mouseX: t.clientX, mouseY: t.clientY, targetParentId: target } : null);
        const newReorder = target === null ? computeReorderTargetRef.current(cx, cy, nodeId) : null;
        setReorderTarget(newReorder);
      }
    };

    const onEnd = (_e: TouchEvent) => {
      clearLongPress();
      if (gesture?.type === 'node-drag') {
        const { nodeId } = gesture;
        const rd = reparentDragRef.current;
        const curParentId = nodesRef.current.find(n => n.id === nodeId)?.parentId;
        if (rd && rd.targetParentId !== null && rd.targetParentId !== curParentId) {
          applyReparentRef.current(nodeId, rd.targetParentId);
        } else if (reorderTargetRef.current !== null) {
          const { afterId, parentId } = reorderTargetRef.current;
          applyReorderRef.current(nodeId, afterId, parentId);
        }
        setReparentDrag(null);
        setReorderTarget(null);
      }
      gesture = null;
    };

    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const rect = wheelEl.getBoundingClientRect();
      applyZoom(z => Math.max(0.3, Math.min(2.5, z + (e.deltaY > 0 ? -0.1 : 0.1))), { x: e.clientX - rect.left, y: e.clientY - rect.top });
    };

    el.addEventListener('touchstart', onStart, { passive: false });
    el.addEventListener('touchmove',  onMove,  { passive: false });
    el.addEventListener('touchend',   onEnd as EventListener);
    el.addEventListener('touchcancel', onEnd as EventListener);
    // Wheel on the wrapper div instead of SVG — Firefox doesn't fire wheel reliably on SVG elements
    const wheelEl = canvasWrapperRef.current ?? el;
    wheelEl.addEventListener('wheel', onWheel as EventListener, { passive: false });
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove',  onMove);
      el.removeEventListener('touchend',   onEnd as EventListener);
      el.removeEventListener('touchcancel', onEnd as EventListener);
      wheelEl.removeEventListener('wheel', onWheel as EventListener);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Touch: 2-finger pinch-zoom + pan (delta-per-frame) ────────────────────────
  useEffect(() => {
    let prevA: { x: number; y: number } | null = null;
    let prevB: { x: number; y: number } | null = null;

    const onMove = (e: TouchEvent) => {
      if (e.touches.length < 2) {
        prevA = null; prevB = null;
        return;
      }
      e.preventDefault();
      const a = e.touches[0], b = e.touches[1];
      if (!prevA || !prevB) {
        prevA = { x: a.clientX, y: a.clientY };
        prevB = { x: b.clientX, y: b.clientY };
        return;
      }
      const prevDist = Math.hypot(prevA.x - prevB.x, prevA.y - prevB.y);
      const curDist  = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
      const prevMidX = (prevA.x + prevB.x) / 2;
      const prevMidY = (prevA.y + prevB.y) / 2;
      const curMidX  = (a.clientX + b.clientX) / 2;
      const curMidY  = (a.clientY + b.clientY) / 2;
      const dx = curMidX - prevMidX;
      const dy = curMidY - prevMidY;
      if (prevDist > 1) {
        const ratio = curDist / prevDist;
        const rect = canvasWrapperRef.current?.getBoundingClientRect();
        const anchor = rect ? { x: curMidX - rect.left, y: curMidY - rect.top } : undefined;
        applyZoom(prev => Math.max(0.3, Math.min(2.5, prev * ratio)), anchor);
      }
      if (Math.abs(dx) > 0 || Math.abs(dy) > 0) {
        setPanOffset(prev => ({ x: prev.x + dx, y: prev.y + dy }));
      }
      prevA = { x: a.clientX, y: a.clientY };
      prevB = { x: b.clientX, y: b.clientY };
    };

    const onEnd = (e: TouchEvent) => { if (e.touches.length < 2) { prevA = null; prevB = null; } };

    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onEnd as EventListener);
    window.addEventListener('touchcancel', onEnd as EventListener);
    return () => {
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onEnd as EventListener);
      window.removeEventListener('touchcancel', onEnd as EventListener);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Edit overlay position ─────────────────────────────────────────────────────
  function getEditOverlay(nodeId: number) {
    const pos = positions.get(nodeId);
    if (!pos || !svgRef.current) return null;
    const rect = svgRef.current.getBoundingClientRect();
    const level = levelById.get(nodeId) ?? 0;
    // Use the live editText so the overlay grows on the fly with the input
    // nodesForLayout has per-user priorities merged in — needed for a correct dispW
    const editNode = nodesForLayout.find(n => n.id === nodeId);
    const { w, h, baseW } = nodeSize(level, editText, editNode?.manualW);
    const dispW = w + nodeDispAdj(editNode?.priority, editNode?.note, editNode?.url);
    const vc = getVisualCenter(pos, dispW, baseW);
    return {
      x: rect.left + vc.x * zoom + panOffset.x - (dispW * zoom) / 2,
      y: rect.top + vc.y * zoom + panOffset.y - (h * zoom) / 2,
      w: dispW * zoom,
      h: h * zoom,
    };
  }

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <>
    <div style={{ width: '100vw', height: '100dvh', display: 'flex', flexDirection: 'column', overflow: 'hidden', userSelect: 'none' }}>

      {syncFailed && (
        <div style={{ position: 'fixed', top: 8, left: '50%', transform: 'translateX(-50%)', zIndex: 9999, background: '#dc2626', color: 'white', fontSize: 13, fontWeight: 600, padding: '6px 16px', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.25)' }}>
          <span>Changes not saved yet — retrying…</span>
        </div>
      )}

      {/* ── Top toolbar ── */}
      <div className="shadow" style={{ background: '#EDE7E3', flexShrink: 0, zIndex: 50, overflowX: 'auto' }}>
      <div className="px-4 py-2 flex items-center justify-between gap-2" style={{ minWidth: 'max-content' }}>

        {/* Left — buttons */}
        <div className="flex items-center gap-1.5">
          <div style={{ marginRight: '16px' }}>
            <h1 className="text-lg font-bold text-gray-800">MindYourPlan</h1>
            <p style={{ fontSize: 10, color: '#6b7280', margin: 0, lineHeight: 1 }}>{tr('appSubtitleMind')}</p>
          </div>

          <input
            value={title}
            onChange={e => setTitle(e.target.value)}
            className="px-2 py-1 border rounded text-sm w-32"
            placeholder={tr('mindmapPlaceholder')}
          />

          {/* New */}
          <button data-tour="mm-btn-new" onClick={newMindmap}
            className="text-white px-2 py-1 rounded text-xs flex items-center gap-1 transition-all"
            style={{ background: 'linear-gradient(to bottom, #6b7280, #4b5563)', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}
            onMouseOver={(e) => { e.currentTarget.style.boxShadow = '0 6px 8px rgba(0,0,0,0.15)'; e.currentTarget.style.background = 'linear-gradient(to bottom, #4b5563, #374151)'; }}
            onMouseOut={(e) => { e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)'; e.currentTarget.style.background = 'linear-gradient(to bottom, #6b7280, #4b5563)'; }}
          >
            <FileText size={13} /> {tr('new')}
          </button>

          {/* Open */}
          <button data-tour="mm-btn-open" onClick={() => onOpenProjectList({
            filterMapType: 'mindmap',
            onLoadFlowchart: (f) => loadMindmap(f.id),
            onDeleteFlowchart: deleteMindmap,
            onRestoreVersion: handleRestoreVersion,
            onSetProject: (pid) => setCurrentProjectId(pid),
            currentFlowchartId: currentId ?? undefined,
          })}
            className="text-white px-2 py-1 rounded text-xs flex items-center gap-1 transition-all"
            style={{ background: 'linear-gradient(to bottom, #82C0CC, #489FB5)', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}
            onMouseOver={(e) => { e.currentTarget.style.boxShadow = '0 6px 8px rgba(0,0,0,0.15)'; e.currentTarget.style.background = 'linear-gradient(to bottom, #489FB5, #16697A)'; }}
            onMouseOut={(e) => { e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)'; e.currentTarget.style.background = 'linear-gradient(to bottom, #82C0CC, #489FB5)'; }}
          >
            <FolderOpen size={13} /> {tr('open')}
          </button>

          {/* Save */}
          <button data-tour="mm-btn-save" onClick={save} disabled={isSaving}
            className="text-white px-2 py-1 rounded text-xs flex items-center gap-1 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ background: 'linear-gradient(to bottom, #489FB5, #16697A)', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}
            onMouseOver={(e) => { if (!isSaving) { e.currentTarget.style.boxShadow = '0 6px 8px rgba(0,0,0,0.15)'; e.currentTarget.style.background = 'linear-gradient(to bottom, #16697A, #0d4f5d)'; }}}
            onMouseOut={(e) => { e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)'; e.currentTarget.style.background = 'linear-gradient(to bottom, #489FB5, #16697A)'; }}
          >
            <Save size={13} /> {isSaving ? tr('saving') : tr('save')}
          </button>

          {/* History */}
          <button
            onClick={() => { setShowHistory(true); loadChangeLog(); }}
            disabled={!currentId}
            className="text-white px-2 py-1 rounded text-xs flex items-center gap-1 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ background: 'linear-gradient(to bottom, #FFA62B, #e8931a)', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}
            onMouseOver={(e) => { if (currentId) { e.currentTarget.style.boxShadow = '0 6px 8px rgba(0,0,0,0.15)'; e.currentTarget.style.background = 'linear-gradient(to bottom, #e8931a, #d17d0f)'; }}}
            onMouseOut={(e) => { e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)'; e.currentTarget.style.background = 'linear-gradient(to bottom, #FFA62B, #e8931a)'; }}
            title={!currentId ? tr('saveFirstHistory') : tr('versionHistory')}
          >
            <Clock size={13} /> {tr('history')}
          </button>

          {/* Share */}
          <button data-tour="mm-btn-share"
            onClick={() => setShowShare(true)}
            disabled={!currentId}
            className="text-white px-2 py-1 rounded text-xs flex items-center gap-1 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ background: 'linear-gradient(to bottom, #82C0CC, #489FB5)', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}
            onMouseOver={(e) => { if (currentId) { e.currentTarget.style.boxShadow = '0 6px 8px rgba(0,0,0,0.15)'; e.currentTarget.style.background = 'linear-gradient(to bottom, #489FB5, #16697A)'; }}}
            onMouseOut={(e) => { e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)'; e.currentTarget.style.background = 'linear-gradient(to bottom, #82C0CC, #489FB5)'; }}
            title={!currentId ? tr('saveFirstShare') : tr('shareMindmap')}
          >
            <Share2 size={13} /> {tr('share')}
          </button>

          <RoleBadge currentId={currentId} userRole={userRole} />

          <button
            onClick={() => setShowHelpPanel(p => !p)}
            className="text-gray-500 hover:text-blue-500 px-2 py-1 rounded text-xs font-bold border border-gray-300 hover:border-blue-400 transition-all"
            style={{ height: '28px', minHeight: '28px', flexShrink: 0, background: showHelpPanel ? '#eff6ff' : undefined, borderColor: showHelpPanel ? '#93c5fd' : undefined, color: showHelpPanel ? '#2563eb' : undefined }}
            title={tr('controls')}
          >?</button>

          {/* Logged-in user badge */}
          {user?.email && (() => {
            const parts = user.email.split('@')[0].split(/[.\-_]/);
            const initials = parts.length >= 2
              ? (parts[0][0] + parts[1][0]).toUpperCase()
              : user.email.slice(0, 2).toUpperCase();
            return (
              <div title={user.email} style={{ width: 28, height: 28, borderRadius: '50%', background: 'linear-gradient(135deg, #10b981, #059669)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0, boxShadow: '0 2px 6px rgba(0,0,0,0.18)', cursor: 'default', border: '2px solid white', letterSpacing: '0.02em' }}>
                {initials}
              </div>
            );
          })()}

          <button onClick={onSignOut}
            className="text-white px-2 py-1 rounded text-xs flex items-center gap-1 transition-all"
            style={{ background: 'linear-gradient(to bottom, #fca5a5, #f08080)', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', height: '28px', minHeight: '28px', maxHeight: '28px', flexShrink: 0 }}
            onMouseOver={(e) => { e.currentTarget.style.boxShadow = '0 6px 8px rgba(0,0,0,0.15)'; e.currentTarget.style.background = 'linear-gradient(to bottom, #f08080, #e06060)'; }}
            onMouseOut={(e) => { e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)'; e.currentTarget.style.background = 'linear-gradient(to bottom, #fca5a5, #f08080)'; }}
          >
            <LogOut size={13} /> {tr('logOut')}
          </button>

          {/* version number moved to the left sidebar */}

          {/* Online users badges */}
          {onlineUsers.length > 0 && (
            <div className="flex items-center gap-1" title={tr('usersOnline', { count: onlineUsers.length })}>
              {onlineUsers.slice(0, 4).map((u: { userId: string; email: string; color: string }) => (
                <div key={u.userId} title={u.email} style={{ width: 22, height: 22, borderRadius: '50%', background: u.color, border: '2px solid white', boxShadow: '0 0 0 1.5px ' + u.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: 'white', flexShrink: 0 }}>
                  {u.email.substring(0, 1).toUpperCase()}
                </div>
              ))}
              {onlineUsers.length > 4 && <span style={{ fontSize: 11, color: '#6b7280', fontWeight: 600 }}>+{onlineUsers.length - 4}</span>}
            </div>
          )}
        </div>

        {/* Right — color mode toggle + zoom */}
        <div className="flex items-center gap-2">

        {/* Collapse/expand to level — compact button row */}
        <div style={{ display: 'flex', alignItems: 'center', border: '1px solid #d1d5db', borderRadius: 6, overflow: 'hidden', flexShrink: 0, height: 28 }}>
          {[1, 2, 3].map(level => (
            <button key={level} onClick={() => collapseToLevel(level)}
              title={tr('collapseToLevel', { level })}
              style={{ padding: '0 8px', height: '100%', background: 'white', border: 'none', borderRight: '1px solid #d1d5db', fontSize: 11, fontWeight: 700, color: '#374151', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
              onMouseOver={e => { e.currentTarget.style.background = '#e0e7ff'; e.currentTarget.style.color = '#4338ca'; }}
              onMouseOut={e => { e.currentTarget.style.background = 'white'; e.currentTarget.style.color = '#374151'; }}
            >L{level}</button>
          ))}
          <button onClick={() => collapseToLevel(null)}
            title={tr('expandAll')}
            style={{ padding: '0 7px', height: '100%', background: 'white', border: 'none', fontSize: 11, fontWeight: 700, color: '#374151', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
            onMouseOver={e => { e.currentTarget.style.background = '#dcfce7'; e.currentTarget.style.color = '#16a34a'; }}
            onMouseOut={e => { e.currentTarget.style.background = 'white'; e.currentTarget.style.color = '#374151'; }}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="3" y1="5" x2="21" y2="5"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="19" x2="21" y2="19"/>
            </svg>
          </button>
        </div>

        {/* Search — highlights every node whose text contains the query, expanding any
            collapsed ancestor so a match is never hidden from view. Client-side/ephemeral
            only (never persisted, never synced) — distinct from the per-node "highlighted"
            marker toggled via the Highlighter tool. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, border: '1px solid #d1d5db', borderRadius: 6, padding: '0 4px 0 8px', background: 'white', height: 28, flexShrink: 0 }}>
          <input
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') runSearch(searchQuery); else if (e.key === 'Escape') clearSearch(); }}
            placeholder={tr('searchMindmapPlaceholder')}
            style={{ border: 'none', outline: 'none', fontSize: 11, width: 110, background: 'transparent' }}
          />
          {searchMatchIds !== null && (
            <span
              title={tr('searchMatchCount', { count: searchMatchIds.size })}
              style={{ fontSize: 10, color: searchMatchIds.size > 0 ? '#16697A' : '#ef4444', fontWeight: 700, flexShrink: 0 }}
            >
              {searchMatchIds.size}
            </span>
          )}
          {searchMatchIds !== null && (
            <button
              onClick={clearSearch}
              title={tr('clearSearch')}
              style={{ display: 'flex', alignItems: 'center', color: '#9ca3af', background: 'none', border: 'none', cursor: 'pointer', padding: 2 }}
            >
              <X size={12} />
            </button>
          )}
          <button
            onClick={() => runSearch(searchQuery)}
            title={tr('searchMindmap')}
            style={{ display: 'flex', alignItems: 'center', color: '#374151', background: 'none', border: 'none', cursor: 'pointer', padding: 2 }}
          >
            <Search size={13} />
          </button>
        </div>

        <button
          onClick={() => {
            let qs: [number[], number[], number[], number[]];
            if (quadrantOrder) {
              // Remove deleted nodes and nodes whose priority no longer matches the quadrant
              qs = quadrantOrder.map((q, qi) => q.filter(id => { const n = nodes.find(x => x.id === id); return n && userPriorities[n.id] === qi + 1; })) as [number[], number[], number[], number[]];
              const alreadyIn = new Set(qs.flat());
              nodes.forEach(n => { const p = userPriorities[n.id]; if (p && p >= 1 && p <= 4 && !alreadyIn.has(n.id)) qs[p - 1].push(n.id); });
            } else {
              qs = [[], [], [], []];
              nodes.forEach(n => { const p = userPriorities[n.id]; if (p && p >= 1 && p <= 4) qs[p - 1].push(n.id); });
            }
            setQuadrantOrder(qs);
            setShowPriorityMatrix(true);
          }}
          title={tr('priorityMatrix')}
          style={{ padding: '0 10px', height: '28px', background: 'white', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 11, fontWeight: 700, color: '#374151', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0 }}
          onMouseOver={(e) => { e.currentTarget.style.background = '#e0e7ff'; e.currentTarget.style.color = '#4338ca'; }}
          onMouseOut={(e) => { e.currentTarget.style.background = 'white'; e.currentTarget.style.color = '#374151'; }}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="3" width="8" height="8" rx="1"/><rect x="13" y="3" width="8" height="8" rx="1"/>
            <rect x="3" y="13" width="8" height="8" rx="1"/><rect x="13" y="13" width="8" height="8" rx="1"/>
          </svg>
          {tr('priorities')}
        </button>
        <button
          onClick={toggleColorMode}
          title={colorMode === 'multi' ? tr('switchToMono') : tr('switchToMulti')}
          className="flex items-center gap-1.5 px-2 py-1 rounded border text-xs font-semibold transition-all"
          style={{
            height: '28px', flexShrink: 0,
            background: colorMode === 'mono' ? 'linear-gradient(to bottom, #bfdbfe, #93c5fd)' : 'white',
            borderColor: colorMode === 'mono' ? '#3b82f6' : '#d1d5db',
            color: colorMode === 'mono' ? '#1d4ed8' : '#374151',
          }}
        >
          <span style={{
            width: 10, height: 10, borderRadius: '50%', flexShrink: 0, display: 'inline-block',
            background: colorMode === 'multi'
              ? 'conic-gradient(#6366f1 0deg 72deg, #ec4899 72deg 144deg, #f59e0b 144deg 216deg, #10b981 216deg 288deg, #3b82f6 288deg 360deg)'
              : MONO_COLOR,
          }} />
          {colorMode === 'multi' ? tr('multiColor') : tr('monoColor')}
        </button>
        <div data-tour="mm-zoom" className="flex items-center gap-1 border rounded px-2" style={{ background: 'linear-gradient(to bottom, #f9fafb, #f3f4f6)', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', height: '28px' }}>
          <button onClick={() => applyZoom(z => Math.max(0.3, z - 0.1))} className="px-1 rounded transition-colors flex items-center" title={tr('zoomOut')} style={{ background: 'transparent' }} onMouseOver={(e) => e.currentTarget.style.background = '#e5e7eb'} onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}><ZoomOut size={13} /></button>
          <span className="text-xs w-10 text-center font-medium">{Math.round(zoom * 100)}%</span>
          <button onClick={() => applyZoom(z => Math.min(2.5, z + 0.1))} className="px-1 rounded transition-colors flex items-center" title={tr('zoomIn')} style={{ background: 'transparent' }} onMouseOver={(e) => e.currentTarget.style.background = '#e5e7eb'} onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}><ZoomIn size={13} /></button>
        </div>
        <LanguageSwitcher />
        </div>
      </div>
      </div>

      {/* ── Main area ── */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden', position: 'relative' }}>

        {/* ── Left sidebar ── */}
        <div style={{ width: 56, background: '#EDE7E3', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '12px 0', gap: 4, flexShrink: 0, boxShadow: '2px 0 8px rgba(0,0,0,0.08)', overflowY: 'auto' }}>
          <span style={{ fontSize: 9, color: '#16697A', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 4 }}>{tr('addLabel')}</span>

          <SideBtn
            onClick={() => selectedNode !== null && addChild(selectedNode)}
            disabled={selectedNode === null}
            title={tr('addChildNode')}
            color="#16697A"
          >
            <Plus size={16} /><span>{tr('childNode')}</span>
          </SideBtn>

          <div style={{ width: 32, height: 1, background: '#C5B9B3', margin: '6px 0' }} />
          <span style={{ fontSize: 9, color: '#16697A', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 4 }}>{tr('editLabel')}</span>

          <SideBtn onClick={undo} title={tr('undoHint')} color="#489FB5">
            <Undo size={16} /><span>{tr('undo')}</span>
          </SideBtn>
          <SideBtn onClick={redo} title={tr('redoHint')} color="#489FB5">
            <Redo size={16} /><span>{tr('redo')}</span>
          </SideBtn>
          <SideBtn
            onClick={() => {
              if (selectedNodes.size > 0) deleteNodes([...selectedNodes]);
              else if (selectedNode !== null) deleteNode(selectedNode);
            }}
            disabled={selectedNode === null && selectedNodes.size === 0}
            title={tr('deleteSelected')}
            color="#f08080"
          >
            <Trash2 size={16} /><span>{tr('delete')}</span>
          </SideBtn>
          {(() => {
            const hasSelection = selectedNode !== null || selectedNodes.size > 0;
            const activeIds = selectedNodes.size > 0 ? [...selectedNodes] : selectedNode !== null ? [selectedNode] : [];
            const selNode = nodes.find(n => n.id === (selectedNode ?? [...selectedNodes][0]));
            const curPrio = selNode ? userPriorities[selNode.id] as 1|2|3|4|5|undefined : undefined;
            return (
              <div style={{ position: 'relative' }}>
                <SideBtn
                  onClick={() => setShowPriorities(v => !v)}
                  title={showPriorities ? tr('hidePriorities') : tr('showPriorities')}
                  color={showPriorities ? '#FFA62B' : '#82C0CC'}
                >
                  {showPriorities ? <Eye size={16} /> : <EyeOff size={16} />}
                  <span>{tr('priorities')}</span>
                </SideBtn>
                <SideBtn
                  onClick={() => { if (hasSelection) setShowPrioPopup(v => !v); }}
                  disabled={!hasSelection}
                  title={tr('setPriority')}
                  color="#82C0CC"
                >
                  <span style={{ width: 20, height: 20, borderRadius: 5, background: hasSelection ? PRIO_COLORS[1] : '#475569', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: 'white', flexShrink: 0 }}>1</span>
                  <span>{tr('priorityLabel')}</span>
                </SideBtn>
                {showPrioPopup && hasSelection && (
                  <>
                    <div style={{ position: 'fixed', inset: 0, zIndex: 399 }} onClick={() => setShowPrioPopup(false)} />
                    <div style={{ position: 'absolute', left: 48, top: 0, background: 'white', border: '1px solid #e5e7eb', borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.15)', zIndex: 400, padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <div style={{ fontSize: 10, color: '#9ca3af', fontWeight: 600, whiteSpace: 'nowrap' }}>{tr('priorityLabel')}</div>
                      <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                        {([1, 2, 3, 4, 5] as const).map(p => {
                          const active = curPrio === p;
                          return (
                            <button key={p} onClick={() => {
                              const newPrio = active ? undefined : p;
                              setUserPriorities(prev => {
                                const next = { ...prev };
                                activeIds.forEach(id => { if (newPrio) next[id] = newPrio; else delete next[id]; });
                                return next;
                              });
                              setShowPrioPopup(false);
                              setQuadrantOrder(prev => {
                                if (!prev) return prev;
                                const newQ = prev.map(q => q.filter(id => !activeIds.includes(id))) as [number[], number[], number[], number[]];
                                if (newPrio && newPrio <= 4) activeIds.forEach(id => newQ[newPrio - 1].push(id));
                                return newQ;
                              });
                            }}
                              title={tr('priority', { p })}
                              style={{ width: 32, height: 32, borderRadius: 7, background: PRIO_COLORS[p], border: active ? '2.5px solid #1f2937' : '2px solid white', cursor: 'pointer', boxShadow: '0 1px 3px rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, color: 'white' }}
                            >{p}</button>
                          );
                        })}
                        <button onClick={() => {
                          setUserPriorities(prev => {
                            const next = { ...prev };
                            activeIds.forEach(id => delete next[id]);
                            return next;
                          });
                          setShowPrioPopup(false);
                          setQuadrantOrder(prev => prev ? prev.map(q => q.filter(id => !activeIds.includes(id))) as [number[], number[], number[], number[]] : prev);
                          setDayOrder(prev => prev.map(d => d.filter(id => !activeIds.includes(id))));
                        }}
                          title={tr('removePriority')}
                          style={{ width: 24, height: 24, borderRadius: '50%', background: '#f3f4f6', border: '1.5px solid #d1d5db', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af' }}
                        ><X size={10} strokeWidth={2.5} /></button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            );
          })()}
          <div style={{ flex: 1 }} />

          <SideBtn onClick={(e: React.MouseEvent) => { setExportMenuY(e.currentTarget.getBoundingClientRect().top); setShowExportMenu(v => !v); }} title={tr('export')} color="#489FB5">
            <Download size={16} /><span>{tr('export')}</span>
          </SideBtn>

          {/* Default mindmap */}
          <SideBtn
            onClick={async () => {
              if (!currentId || !user?.id) return;
              if (currentId === defaultMindmapId) {
                setDefaultMindmapId(null);
                localStorage.removeItem('mindmap-default-id');
                localStorage.removeItem('home-map-type');
                const { error } = await supabase.from('user_profiles').update({ default_mindmap_id: null, home_map_type: null }).eq('id', user.id);
                if (error) { logger.error('Home mindmap clear error:', error); alert(tr('saveHomeFailed')); }
              } else {
                setDefaultMindmapId(currentId);
                localStorage.setItem('mindmap-default-id', currentId);
                localStorage.removeItem('flowchart-default-id');
                localStorage.setItem('home-map-type', 'mindmap');
                const { error } = await supabase.from('user_profiles').update({ default_mindmap_id: currentId, default_flowchart_id: null, home_map_type: 'mindmap' }).eq('id', user.id);
                if (error) { logger.error('Home mindmap save error:', error); alert(tr('saveHomeFailed')); }
              }
            }}
            disabled={!currentId}
            title={!currentId ? tr('saveFirstDefault') : currentId === defaultMindmapId ? tr('removeAsDefault') : tr('setAsDefaultMindmap')}
            color={currentId === defaultMindmapId ? '#FFA62B' : '#82C0CC'}
          >
            <Home size={16} /><span>{currentId === defaultMindmapId ? tr('homeActive') : tr('home')}</span>
          </SideBtn>

          {/* Mode switch */}
          <div style={{ width: 32, height: 1, background: '#C5B9B3', margin: '4px 0' }} />
          <button
            onClick={onSwitchToFlowchart}
            title={tr('switchToFlowchart')}
            style={{ width: 40, padding: '6px 0', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, background: '#C5B9B3', border: '1px solid #B3A9A4', borderRadius: 6, color: '#16697A', cursor: 'pointer', fontSize: 8 }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="7" height="7" rx="1" />
              <rect x="14" y="3" width="7" height="7" rx="1" />
              <rect x="8" y="14" width="8" height="7" rx="1" />
              <path d="M6.5 10v2M17.5 10v2M6.5 12h11M12 12v2" />
            </svg>
            <span>{tr('flowMode')}</span>
          </button>
          <button
            title={tr('mindmapModeActive')}
            style={{ width: 40, padding: '6px 0', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, background: '#16697A', border: 'none', borderRadius: 6, color: 'white', cursor: 'default', fontSize: 8 }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="3" />
              <circle cx="12" cy="3.5" r="1.5" /><line x1="12" y1="5" x2="12" y2="9" />
              <circle cx="12" cy="20.5" r="1.5" /><line x1="12" y1="15" x2="12" y2="19" />
              <circle cx="3.5" cy="12" r="1.5" /><line x1="5" y1="12" x2="9" y2="12" />
              <circle cx="20.5" cy="12" r="1.5" /><line x1="15" y1="12" x2="19" y2="12" />
            </svg>
            <span>{tr('mindMode')}</span>
          </button>

          {/* Version number */}
          <span style={{ fontSize: 13, color: '#cbd5e1', fontWeight: 500, letterSpacing: '0.03em', marginTop: 4 }}>v{APP_VERSION}</span>
        </div>

        {/* ── SVG Canvas + Minimap ── */}
        <div ref={canvasWrapperRef} style={{ position: 'relative', flex: 1, overflow: 'hidden' }}>
        <svg
          ref={svgRef}
          style={{ width: '100%', height: '100%', touchAction: 'none', cursor: isPanning ? 'grabbing' : resizeDrag ? 'ew-resize' : 'default' }}
          onMouseDown={e => {
            if (editingNode !== null) { commitEdit(); return; }
            if (e.button === 1 || e.button === 2) {
              e.preventDefault();
              setIsPanning({ startX: e.clientX, startY: e.clientY, initX: panOffset.x, initY: panOffset.y });
              return;
            }
            // LMB on empty canvas → start marquee selection
            if (e.button === 0 && (e.target as SVGElement).tagName === 'rect' && (e.target as SVGElement).getAttribute('fill') === 'transparent') {
              const { cx, cy } = toCanvas(e.clientX, e.clientY);
              setSelectionRect({ startX: cx, startY: cy, currentX: cx, currentY: cy });
              setSelectedNodes(new Set());
              setSelectedNode(null);
            }
          }}
          onMouseMove={e => {
            if (isPanning) {
              setPanOffset({ x: isPanning.initX + (e.clientX - isPanning.startX), y: isPanning.initY + (e.clientY - isPanning.startY) });
            } else if (resizeDragRef.current) {
              const rd = resizeDragRef.current;
              const { cx } = toCanvas(e.clientX, e.clientY);
              const delta = rd.isRightSide ? cx - rd.startMouseCanvasX : rd.startMouseCanvasX - cx;
              const newW = Math.max(44, rd.startW + delta);
              setNodes(prev => prev.map(n => n.id === rd.nodeId ? { ...n, manualW: newW } : n));
            } else if (pendingNodeDrag) {
              const dx = e.clientX - pendingNodeDrag.startX;
              const dy = e.clientY - pendingNodeDrag.startY;
              if (Math.sqrt(dx * dx + dy * dy) > 5) {
                setReparentDrag({ nodeId: pendingNodeDrag.nodeId, nodeIds: pendingNodeDrag.nodeIds, mouseX: e.clientX, mouseY: e.clientY, targetParentId: null });
                setPendingNodeDrag(null);
              }
            } else if (reparentDrag) {
              const { cx, cy } = toCanvas(e.clientX, e.clientY);
              const cm = buildChildrenMap(nodesRef.current);
              const excluded = new Set(reparentDrag.nodeIds.flatMap(id => [id, ...getAllDescendants(cm, id)]));
              const target = getNodeAtCanvasPos(cx, cy, excluded);
              // Cursor over a node → reparent (also onto a peer sibling).
              // Cursor in empty space → reorder mode.
              const reparentTarget = target !== null ? target : null;
              setReparentDrag(prev => prev ? { ...prev, mouseX: e.clientX, mouseY: e.clientY, targetParentId: reparentTarget } : null);
              // Only compute reorder if we're not hovering over a reparent target
              const newReorderTarget = reparentTarget === null ? computeReorderTarget(cx, cy, reparentDrag.nodeId) : null;
              setReorderTarget(newReorderTarget);
            } else if (selectionRect) {
              const { cx, cy } = toCanvas(e.clientX, e.clientY);
              const updated = { ...selectionRect, currentX: cx, currentY: cy };
              setSelectionRect(updated);
              // Compute which nodes fall within the rectangle
              const minX = Math.min(updated.startX, cx);
              const maxX = Math.max(updated.startX, cx);
              const minY = Math.min(updated.startY, cy);
              const maxY = Math.max(updated.startY, cy);
              const inside = new Set<number>();
              positions.forEach((pos, id) => {
                const node = nodesRef.current.find(n => n.id === id);
                if (!node) return;
                const level = getNodeLevel(nodesRef.current, id);
                const { w, h, baseW } = nodeSize(level, node.text, node.manualW);
                const dispW = w + nodeDispAdj(userPriorities[node.id], node.note, node.url);
                const vc = getVisualCenter(pos, dispW, baseW);
                const nx1 = vc.x - dispW / 2, nx2 = vc.x + dispW / 2;
                const ny1 = vc.y - h / 2, ny2 = vc.y + h / 2;
                if (nx2 >= minX && nx1 <= maxX && ny2 >= minY && ny1 <= maxY) inside.add(id);
              });
              setSelectedNodes(inside);
            }
          }}
          onMouseUp={() => {
            if (resizeDragRef.current) {
              const rd = resizeDragRef.current;
              // Clamp the width to the maximum single-line width.
              // If the user drags wider than needed for 1 line, snap back to that limit.
              const finalNodes = nodesRef.current.map(n => {
                if (n.id !== rd.nodeId || n.manualW === undefined) return n;
                const level = getNodeLevel(nodesRef.current, n.id);
                const autoSize = nodeSize(level, n.text);
                // Compute the width at which the full text fits on exactly 1 line
                const fsCfg = [14, 12, 11];
                const fs = fsCfg[Math.min(level, fsCfg.length - 1)];
                const hPad = level >= 3 ? 16 : 12;
                const charW = fs * 0.44;
                const singleLineW = Math.max(2 * hPad + charW, Math.ceil(n.text.length * charW + 2 * hPad));
                if (n.manualW >= singleLineW) {
                  // Wider than needed for 1 line: snap back to the optimal single-line width
                  return autoSize.lines.length === 1
                    ? { ...n, manualW: undefined }      // auto-size already gives 1 line
                    : { ...n, manualW: singleLineW };   // force 1 line
                }
                return n;
              });
              setNodes(finalNodes);
              pushHistory(finalNodes);
              setResizeDrag(null);
              resizeDragRef.current = null;
              return;
            }
            if (pendingNodeDrag) {
              setPendingNodeDrag(null);
              return;
            }
            if (reparentDrag) {
              const { nodeId, nodeIds } = reparentDrag;
              const isMulti = nodeIds.length > 1;
              const curParentId = nodesRef.current.find(n => n.id === nodeId)?.parentId;
              if (reparentDrag.targetParentId !== null && reparentDrag.targetParentId !== curParentId) {
                if (isMulti) applyMultiReparent(nodeIds, reparentDrag.targetParentId);
                else applyReparent(nodeId, reparentDrag.targetParentId);
              } else if (reorderTargetRef.current !== null) {
                const { afterId, parentId } = reorderTargetRef.current;
                if (isMulti) applyMultiReorder(nodeIds, afterId, parentId);
                else applyReorder(nodeId, afterId, parentId);
              }
              setReparentDrag(null);
              setReorderTarget(null);
              return;
            }
            if (selectionRect) {
              const dx = Math.abs(selectionRect.currentX - selectionRect.startX);
              const dy = Math.abs(selectionRect.currentY - selectionRect.startY);
              if (dx < 5 && dy < 5) { setSelectedNodes(new Set()); setSelectedNode(null); }
                if (dx >= 5 || dy >= 5) marqueeJustFinished.current = true;
              setSelectionRect(null);
            }
            setIsPanning(null);
          }}
          onMouseLeave={() => {
            if (resizeDragRef.current) { pushHistory(nodesRef.current); setResizeDrag(null); resizeDragRef.current = null; }
            setReparentDrag(null);
            setReorderTarget(null);
            setIsPanning(null);
            setSelectionRect(null);
          }}
          onContextMenu={e => e.preventDefault()}
        >
          {/* Background */}
          <defs>
            <pattern id="mm-dots" width="40" height="40" patternUnits="userSpaceOnUse">
              <circle cx="20" cy="20" r="1" fill="#d1d5db" opacity="0.6" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="#f8fafc" />
          <rect width="100%" height="100%" fill="url(#mm-dots)" />

          <g transform={`translate(${panOffset.x} ${panOffset.y}) scale(${zoom})`}>
            {/* Click-to-deselect background */}
            <rect
              x={-10000} y={-10000} width={20000} height={20000}
              fill="transparent"
              onClick={() => {
                if (marqueeJustFinished.current) { marqueeJustFinished.current = false; return; }
                setSelectedNode(null); setSelectedNodes(new Set()); setContextMenu(null); setNotePanel(null); setLinkPanel(null); setShowHistory(false);
              }}
            />

            {/* Marquee selection rectangle */}
            {selectionRect && (
              <rect
                x={Math.min(selectionRect.startX, selectionRect.currentX)}
                y={Math.min(selectionRect.startY, selectionRect.currentY)}
                width={Math.abs(selectionRect.currentX - selectionRect.startX)}
                height={Math.abs(selectionRect.currentY - selectionRect.startY)}
                fill="rgba(99,102,241,0.08)"
                stroke="#6366f1"
                strokeWidth={1 / zoom}
                strokeDasharray={`${4 / zoom},${3 / zoom}`}
                style={{ pointerEvents: 'none' }}
              />
            )}

            {/* ── Reorder insertion line ── */}
            {reparentDrag !== null && reparentDrag.targetParentId === null && reorderTarget !== null && (() => {
              const { afterId, parentId } = reorderTarget;
              const cm2 = buildChildrenMap(nodes);
              const siblings = cm2.get(parentId) ?? [];
              const otherSibs = siblings.filter(id => id !== reparentDrag.nodeId);
              if (otherSibs.length === 0) return null;

              const dragNode = nodes.find(n => n.id === reparentDrag.nodeId);
              // Same resolvedColor formula as the main renderer — see the inline
              // text-editor overlay's matching comment above for the bug this avoids.
              const lineColor = dragNode ? (dragNode.customColor ?? (colorMode === 'mono' ? MONO_COLOR : dragNode.color)) : MONO_COLOR;

              // Determine position of insertion line
              let lineY: number;
              let refId: number;

              if (afterId === null) {
                refId = otherSibs[0];
                lineY = NaN; // will be set below as "before first"
              } else {
                const afterIdx = otherSibs.indexOf(afterId);
                const nextId = afterIdx + 1 < otherSibs.length ? otherSibs[afterIdx + 1] : null;
                if (nextId !== null) {
                  const yAfter = positions.get(afterId)?.y ?? 0;
                  const yNext = positions.get(nextId)?.y ?? 0;
                  lineY = (yAfter + yNext) / 2;
                  refId = afterId;
                } else {
                  refId = afterId;
                  lineY = NaN; // will be set below as "after last"
                }
              }

              const refPos = positions.get(refId);
              if (!refPos) return null;
              const refNode = nodes.find(n => n.id === refId);
              const refLevel = levelById.get(refId) ?? 0;
              const { w: refW } = nodeSize(refLevel, refNode?.text ?? '', refNode?.manualW);
              const refDispW = refW + nodeDispAdj(refNode?.priority, refNode?.note, refNode?.url);
              const refVC = getVisualCenter(refPos, refDispW, nodeSize(refLevel, refNode?.text ?? '', refNode?.manualW).baseW);
              const gap = refLevel >= 3 ? CHILD_GAP_DEEP : CHILD_GAP_DEFAULT;

              if (isNaN(lineY)) {
                // After last sibling
                lineY = afterId === null ? refPos.y - gap / 2 : refPos.y + gap / 2;
              } else if (afterId === null) {
                lineY = refPos.y - gap / 2;
              }

              const lx1 = refVC.x - refDispW / 2 - 10;
              const lx2 = refVC.x + refDispW / 2 + 10;

              return (
                <g style={{ pointerEvents: 'none' }}>
                  <line x1={lx1} y1={lineY} x2={lx2} y2={lineY}
                    stroke={lineColor} strokeWidth={2.5 / zoom}
                    strokeDasharray={`${5 / zoom},${3 / zoom}`} opacity={0.9} />
                  <circle cx={lx1} cy={lineY} r={3.5 / zoom} fill={lineColor} opacity={0.9} />
                  <circle cx={lx2} cy={lineY} r={3.5 / zoom} fill={lineColor} opacity={0.9} />
                </g>
              );
            })()}

            {/* ── Reparent-drag preview ── */}
            {reparentDrag?.targetParentId !== null && reparentDrag?.targetParentId !== undefined && (() => {
              const { nodeId, targetParentId } = reparentDrag!;
              const dragNode = nodes.find(n => n.id === nodeId);
              if (!dragNode) return null;

              // Compute layout as if the node has already been moved
              const previewNodes = nodes.map(n => n.id === nodeId
                ? { ...n, parentId: targetParentId, manualX: undefined, manualY: undefined }
                : n);
              const previewRoot = previewNodes.find(n => n.parentId === null)?.id ?? 1;
              const previewPositions = computeLayout(previewNodes, previewRoot);

              const tpId = targetParentId as number;
              const newPos = previewPositions.get(nodeId);
              const parentPos = positions.get(tpId);
              if (!newPos || !parentPos) return null;

              const newLevel = getNodeLevel(previewNodes, nodeId);
              const { w, h, rx, baseW } = nodeSize(newLevel, dragNode.text, dragNode.manualW);
              const newVC = getVisualCenter(newPos, w, baseW);

              const parentNode = nodes.find(n => n.id === tpId);
              const parentLevel = levelById.get(tpId) ?? 0;
              const pDims = nodeSize(parentLevel, parentNode?.text ?? '', parentNode?.manualW);
              const pVC = getVisualCenter(parentPos, pDims.w, pDims.baseW);

              const p1 = treePoint(pVC.x, pVC.y, pDims.w, newVC.x);
              const p2 = treePoint(newVC.x, newVC.y, w, pVC.x);

              const parentColor = parentNode?.customColor ?? parentNode?.color ?? dragNode.customColor ?? dragNode.color;

              return (
                <g style={{ pointerEvents: 'none' }} opacity={0.55}>
                  {/* Preview connector line */}
                  <path
                    d={makePath(p1.x, p1.y, p2.x, p2.y, p1.side, p2.side)}
                    stroke={parentColor}
                    strokeWidth={newLevel === 1 ? 2.5 : 1.5}
                    strokeDasharray={`${6 / zoom},${4 / zoom}`}
                    fill="none"
                  />
                  {/* Preview node (no text) */}
                  <rect
                    x={newVC.x - w / 2} y={newVC.y - h / 2}
                    width={w} height={h} rx={rx}
                    fill={newLevel <= 1 ? parentColor : '#f8fafc'}
                    stroke={parentColor}
                    strokeWidth={2}
                    strokeDasharray={`${5 / zoom},${3 / zoom}`}
                  />
                  {/* Highlight ring around the new parent */}
                  <rect
                    x={pVC.x - pDims.w / 2 - 5} y={pVC.y - pDims.h / 2 - 5}
                    width={pDims.w + 10} height={pDims.h + 10}
                    rx={pDims.rx + 5}
                    fill="none"
                    stroke={parentColor}
                    strokeWidth={2.5 / zoom}
                    strokeDasharray={`${5 / zoom},${3 / zoom}`}
                  />
                </g>
              );
            })()}

            {/* ── Lines (from node edge to node edge) ── */}
            {nodes.map(node => {
              if (node.parentId === null) return null;
              const parentNode = nodes.find(n => n.id === node.parentId);
              const pPos = positions.get(node.parentId);
              const cPos = positions.get(node.id);
              if (!pPos || !cPos || !parentNode) return null;
              const level = levelById.get(node.id) ?? 0;
              // For NS>0 nodes use level relative to the attachment point so the linked
              // mindmap always looks visually consistent regardless of linking depth.
              const ns = nodeNS(node.id);
              const attachLevel = ns > 0 ? (levelById.get(mindmapLinks[ns - 1]?.nodeId ?? 0) ?? 0) : 0;
              const visualLevel = level - attachLevel;
              const cLiveText = editingNode === node.id ? editText : node.text;
              const pLiveText = editingNode === node.parentId ? editText : parentNode.text;
              const pDims = nodeSize(levelById.get(node.parentId) ?? 0, pLiveText, parentNode.manualW);
              const cDims = nodeSize(level, cLiveText, node.manualW);
              const pDispW = pDims.w + nodeDispAdj(userPriorities[parentNode.id], parentNode.note, parentNode.url);
              const cDispW = cDims.w + nodeDispAdj(userPriorities[node.id], node.note, node.url);
              const pVC = getVisualCenter(pPos, pDispW, pDims.baseW);
              const cVC = getVisualCenter(cPos, cDispW, cDims.baseW);
              const p1 = treePoint(pVC.x, pVC.y, pDispW, cVC.x);
              const p2 = treePoint(cVC.x, cVC.y, cDispW, pVC.x);
              return (
                <path
                  key={`line-${node.id}`}
                  d={makePath(p1.x, p1.y, p2.x, p2.y, p1.side, p2.side)}
                  stroke={node.customColor ?? (colorMode === 'mono' ? MONO_COLOR : node.color)}
                  strokeWidth={visualLevel === 1 ? 2.5 : 1.5}
                  strokeOpacity={0.55}
                  fill="none"
                  style={{ pointerEvents: 'none' }}
                />
              );
            })}

            {/* ── Collapse/expand connectors ── */}
            {nodes.map(node => {
              if (node.parentId === null) return null;
              const ch = childrenMap.get(node.id) ?? [];
              if (ch.length === 0) return null;
              const parentNode = nodes.find(n => n.id === node.parentId);
              const pPos = positions.get(node.parentId);
              const cPos = positions.get(node.id);
              if (!pPos || !cPos || !parentNode) return null;
              const level = levelById.get(node.id) ?? 0;
              const cDims = nodeSize(level, node.text, node.manualW);
              const cDispW = cDims.w + nodeDispAdj(userPriorities[node.id], node.note, node.url);
              const cVC = getVisualCenter(cPos, cDispW, cDims.baseW);
              // Button always on the outer side: right if the node is right of root, otherwise left
              const btnOffset = 11;
              const isRight = cPos.x >= 0;
              const bx = isRight ? cVC.x + cDispW / 2 + btnOffset : cVC.x - cDispW / 2 - btnOffset;
              const by = cVC.y;
              const collapsed = node.collapsed;
              const count = collapsed ? countDescendants(childrenMap, node.id) : 0;

              return (
                <g
                  key={`conn-${node.id}`}
                  onClick={e => { e.stopPropagation(); toggleCollapse(node.id); }}
                  onTouchStart={e => e.stopPropagation()}
                  onTouchEnd={e => { e.stopPropagation(); toggleCollapse(node.id); }}
                  style={{ cursor: 'pointer' }}
                >
                  {/* Large invisible touch target for easier tapping on mobile */}
                  <circle cx={bx} cy={by} r={20} fill="transparent" />
                  <circle cx={bx} cy={by} r={9} fill="white" stroke={node.customColor ?? (colorMode === 'mono' ? MONO_COLOR : node.color)} strokeWidth={1.5} />
                  <text x={bx} y={by} textAnchor="middle" dominantBaseline="central" fontSize={collapsed && count > 0 ? 9 : 13} fontWeight="700" fill={node.customColor ?? (colorMode === 'mono' ? MONO_COLOR : node.color)} style={{ pointerEvents: 'none' }}>
                    {collapsed ? (count > 0 ? count : '+') : '−'}
                  </text>
                </g>
              );
            })}

            {/* ── Nodes ── */}
            {nodes.map(node => {
              const pos = positions.get(node.id);
              if (!pos) return null;
              const level = levelById.get(node.id) ?? 0;
              // Visual level: for NS>0 nodes, relative to the attachment point
              const nodeNSVal = nodeNS(node.id);
              const nodeAttachLevel = nodeNSVal > 0 ? (levelById.get(mindmapLinks[nodeNSVal - 1]?.nodeId ?? 0) ?? 0) : 0;
              const visualLevel = level - nodeAttachLevel;
              // Use live editText while editing so the node grows on the fly
              const liveText = editingNode === node.id ? editText : node.text;
              const { w: rawW, h, rx, lines, fontSize, lineH, baseW } = nodeSize(level, liveText, node.manualW);
              const prio = showPriorities ? userPriorities[node.id] as 1|2|3|4|5|undefined : undefined;
              const hasNote = !!node.note;
              const hasUrl = !!node.url;
              const leftExtra = prio ? 20 : 0;   // priority badge on the left
              const rightExtra = (hasNote ? 20 : 0) + (hasUrl ? 20 : 0); // icons on the right
              const w = rawW + leftExtra + rightExtra;
              const vc = getVisualCenter(pos, w, baseW);
              const isSelected = selectedNode === node.id || selectedNodes.has(node.id);
              const isSearchMatch = searchMatchIds != null && searchMatchIds.has(node.id);
              const hasBorder = visualLevel <= 1;
              const resolvedColor = node.customColor ?? (colorMode === 'mono' ? MONO_COLOR : node.color);
              // The auto-created "catch-all" child node from the priority matrix's
              // "+Add priority" popup — subtler than the real highlighted state (which
              // tints the whole fill + bumps font-weight): just a yellow border, so it
              // reads as "needs sorting" without competing visually with everything else.
              const isUnsortedNode = node.text === UNSORTED_NODE_TEXT;
              const totalTextH = lines.length * lineH;
              const textStartY = (h - totalTextH) / 2 + lineH / 2;
              // Text centered between the left badge space and the right badge space
              const textX = leftExtra + rawW / 2;

              return (
                <g
                  key={`node-${node.id}`}
                  transform={`translate(${vc.x - w / 2} ${vc.y - h / 2})`}
                  onMouseDown={e => {
                    if (e.button !== 0) return;
                    e.stopPropagation();
                    setShowHistory(false);
                    if (e.ctrlKey || e.metaKey) {
                      setSelectedNodes(prev => {
                        const next = new Set(prev);
                        if (next.has(node.id)) next.delete(node.id);
                        else next.add(node.id);
                        return next;
                      });
                    } else if (node.parentId !== null) {
                      // LMB held on a non-root node → select + potential move
                      // Build the effective multi-selection from both selectedNodes and selectedNode
                      const multiSet = selectedNodesRef.current;
                      const singleSel = selectedNodeRef.current;
                      let effectiveMulti: number[] | null = null;
                      if (multiSet.size >= 1 && (multiSet.has(node.id) || singleSel === node.id)) {
                        const allSelected = new Set(multiSet);
                        if (singleSel !== null) allSelected.add(singleSel);
                        if (allSelected.size > 1) effectiveMulti = [...allSelected];
                      }
                      if (!effectiveMulti) {
                        setSelectedNodes(new Set());
                        setSelectedNode(node.id);
                      }
                      setContextMenu(null);
                      if (!node.note) setNotePanel(null);
                      if (!node.url) setLinkPanel(null);
                      setPendingNodeDrag({ nodeId: node.id, nodeIds: effectiveMulti ?? [node.id], startX: e.clientX, startY: e.clientY });
                    } else {
                      setSelectedNodes(new Set());
                      setSelectedNode(node.id);
                      setContextMenu(null);
                      if (!node.note) setNotePanel(null);
                      if (!node.url) setLinkPanel(null);
                    }
                  }}
                  onClick={e => { e.stopPropagation(); }}
                  onDoubleClick={e => { e.stopPropagation(); startEdit(node.id, node.text); }}
                  onContextMenu={e => { e.preventDefault(); e.stopPropagation(); setContextMenu({ x: e.clientX, y: e.clientY, nodeId: node.id }); }}
                  style={{ cursor: 'pointer' }}
                >
                  {/* Search-match halo — drawn outset and behind everything else so it reads as
                      a glow regardless of selection/highlighted state, in a color distinct from
                      both (selection's amber, highlighted's own node color). */}
                  {isSearchMatch && (
                    <rect
                      x={-5} y={-5} width={w + 10} height={h + 10} rx={rx + 5}
                      fill="none" stroke="#e11d48" strokeWidth={3} strokeDasharray="5 3"
                      style={{ pointerEvents: 'none' }}
                    />
                  )}
                  <rect
                    width={w} height={h} rx={rx}
                    fill={node.highlighted ? resolvedColor : (hasBorder ? resolvedColor : '#f8fafc')}
                    fillOpacity={node.highlighted ? 0.3 : 1}
                    stroke={isSelected ? '#fbbf24' : (node.highlighted ? resolvedColor : (isUnsortedNode ? '#eab308' : (hasBorder ? 'rgba(0,0,0,0.18)' : 'none')))}
                    strokeWidth={isSelected ? 2.5 : (node.highlighted ? 2 : (isUnsortedNode ? 2 : 1.5))}
                    filter={isSelected ? 'drop-shadow(0 2px 6px rgba(0,0,0,0.28))' : 'none'}
                  />
                  {prio && (
                    <g style={{ pointerEvents: 'none' }}>
                      <rect x={6} y={h / 2 - 8} width={16} height={16} rx={4} fill={PRIO_COLORS[prio]} />
                      <text x={14} y={h / 2} textAnchor="middle" dominantBaseline="central" fontSize={10} fontWeight={700} fill="white" style={{ userSelect: 'none' }}>{prio}</text>
                    </g>
                  )}
                  {/* Resize handle — shown on outer edge when single-selected */}
                  {isSelected && selectedNodes.size === 0 && (() => {
                    const isRightSide = pos.x >= -30;
                    const hx = isRightSide ? w : 0;
                    return (
                      <g
                        transform={`translate(${hx}, ${h / 2})`}
                        onMouseDown={e => {
                          e.stopPropagation();
                          const { cx } = toCanvas(e.clientX, e.clientY);
                          setResizeDrag({ nodeId: node.id, startMouseCanvasX: cx, startW: rawW, isRightSide });
                        }}
                        style={{ cursor: 'ew-resize' }}
                      >
                        <rect x={-5} y={-14} width={10} height={28} rx={5} fill={node.customColor ?? (colorMode === 'mono' ? MONO_COLOR : node.color)} opacity={0.9} />
                        <line x1={-1.5} y1={-6} x2={-1.5} y2={6} stroke="white" strokeWidth={1.5} strokeLinecap="round" style={{ pointerEvents: 'none' }} />
                        <line x1={1.5} y1={-6} x2={1.5} y2={6} stroke="white" strokeWidth={1.5} strokeLinecap="round" style={{ pointerEvents: 'none' }} />
                      </g>
                    );
                  })()}
                  {lines.map((line, i) => (
                    <text
                      key={i}
                      x={textX}
                      y={textStartY + i * lineH}
                      textAnchor="middle"
                      dominantBaseline="central"
                      fontSize={fontSize}
                      fontWeight={node.highlighted ? 700 : (visualLevel <= 1 ? 700 : 600)}
                      // Highlighted: the fill above is a translucent version of resolvedColor over
                      // the light canvas background, so white text (the normal hasBorder treatment)
                      // would have poor contrast — use a solid dark color instead, regardless of
                      // hasBorder, for consistent legibility.
                      fill={node.highlighted ? '#1f2937' : (hasBorder ? 'white' : resolvedColor)}
                      style={{ pointerEvents: 'none', userSelect: 'none', textDecoration: userCompleted[node.id] ? 'line-through' : 'none' }}
                    >
                      {line}
                    </text>
                  ))}

                  {/* Note icon on the right of the node (same style as priority on the left) */}
                  {hasNote && (
                    <g
                      transform={`translate(${leftExtra + rawW + 2} ${h / 2 - 8})`}
                      onClick={e => { e.stopPropagation(); setNotePanel({ nodeId: node.id }); }}
                      style={{ cursor: 'pointer' }}
                    >
                      <rect width={16} height={16} rx={4} fill="#fef3c7" stroke="#f59e0b" strokeWidth={1.2} />
                      <line x1={3} y1={5} x2={13} y2={5} stroke="#f59e0b" strokeWidth={1.4} strokeLinecap="round" />
                      <line x1={3} y1={8} x2={13} y2={8} stroke="#f59e0b" strokeWidth={1.4} strokeLinecap="round" />
                      <line x1={3} y1={11} x2={9} y2={11} stroke="#f59e0b" strokeWidth={1.4} strokeLinecap="round" />
                    </g>
                  )}
                  {/* URL icon on the right of the node */}
                  {hasUrl && (
                    <g
                      transform={`translate(${leftExtra + rawW + (hasNote ? 22 : 2)} ${h / 2 - 8})`}
                      onClick={e => { e.stopPropagation(); setLinkPanel({ nodeId: node.id }); }}
                      style={{ cursor: 'pointer' }}
                    >
                      <rect width={16} height={16} rx={4} fill="#c9e8ed" stroke="#489FB5" strokeWidth={1.2} />
                      <svg x={2} y={2} width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="#489FB5" strokeWidth="2.5" strokeLinecap="round">
                        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
                      </svg>
                    </g>
                  )}
                  {/* Link badge: chain icon on the root node of every linked child mindmap */}
                  {nodeNSVal > 0 && visualLevel === 1 && (
                    <g transform={`translate(${w - 10} ${-8})`} style={{ pointerEvents: 'none' }}>
                      <title>{childTitles[mindmapLinks[nodeNSVal - 1]?.childId ?? ''] ?? 'Gekoppelde mindmap'}</title>
                      <circle r={8} fill="#489FB5" />
                      <svg x={-6} y={-6} width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round">
                        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
                      </svg>
                    </g>
                  )}
                </g>
              );
            })}
          </g>
        </svg>

        {/* ── Minimap / radar ── */}
        <MinimapOverlay
          items={minimapItems}
          panOffset={panOffset}
          zoom={zoom}
          containerRef={canvasWrapperRef}
          onPanChange={setPanOffset}
        />
        </div>

        {/* ── Inline text editor overlay ── */}
        {editingNode !== null && (() => {
          const ov = getEditOverlay(editingNode);
          if (!ov) return null;
          const editLevel = levelById.get(editingNode) ?? 0;
          const editNode = nodesForLayout.find(n => n.id === editingNode);
          const editNS = nodeNS(editingNode);
          const editAttachLevel = editNS > 0 ? (levelById.get(mindmapLinks[editNS - 1]?.nodeId ?? 0) ?? 0) : 0;
          const hasBorder = (editLevel - editAttachLevel) <= 1;
          // Must match the main renderer's own resolvedColor formula below (customColor,
          // else mono override, else the node's stored color) — this used to skip the
          // colorMode check entirely and read editNode.color directly, so a node being
          // typed into showed its real (non-mono) color until commit, when the normal
          // render path (which DOES check colorMode) took back over.
          const resolvedEditColor = editNode?.customColor ?? (colorMode === 'mono' ? MONO_COLOR : editNode?.color) ?? MONO_COLOR;
          const nodeBg = hasBorder ? resolvedEditColor : '#f8fafc';
          const nodeColor = hasBorder ? 'white' : resolvedEditColor;
          // Use nodeSize to get the exact same font metrics as the SVG renderer
          const { lines: svgLines, fontSize: svgFs, lineH: svgLineH } = nodeSize(editLevel, editText, editNode?.manualW);
          const fs = svgFs * zoom;
          const lh = svgLineH * zoom;
          const isMultiLine = svgLines.length > 1;
          const totalTextH = svgLines.length * lh;
          const paddingTopVal = Math.max(2, (ov.h - totalTextH) / 2);
          return (
            <textarea
              ref={editInputRef}
              value={editText}
              onChange={e => setEditText(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === 'Escape') { e.preventDefault(); commitEdit(); }
                if (e.key === 'Tab') { e.preventDefault(); const id = editingNode; commitEdit(); setTimeout(() => addChild(id), 40); }
              }}
              onBlur={() => { if (document.hasFocus()) commitEdit(); }}
              rows={1}
              style={{
                position: 'fixed',
                left: ov.x, top: ov.y,
                width: ov.w, height: ov.h,
                textAlign: 'center',
                background: nodeBg,
                color: nodeColor,
                border: '2.5px solid #fbbf24',
                borderRadius: 10,
                paddingTop: paddingTopVal,
                paddingBottom: 2,
                paddingLeft: (editNode?.priority ? 20 : 0) + 8,
                paddingRight: ((editNode?.note || editNode?.url) ? 20 : 0) + 8,
                fontSize: fs,
                fontFamily: 'inherit',
                fontWeight: editNode?.highlighted ? 700 : (hasBorder ? 700 : 600),
                outline: 'none',
                zIndex: 200,
                boxSizing: 'border-box',
                resize: 'none',
                overflow: 'hidden',
                wordBreak: isMultiLine ? 'break-word' : 'normal',
                whiteSpace: isMultiLine ? 'pre-wrap' : 'nowrap',
                lineHeight: `${lh}px`,
              }}
            />
          );
        })()}

        {/* ── Context menu ── */}
        {contextMenu && (
          <>
            <div style={{ position: 'fixed', inset: 0, zIndex: 298 }} onClick={() => setContextMenu(null)} />
            <div
              style={{ position: 'fixed', left: Math.min(contextMenu.x, window.innerWidth - 210), top: Math.min(contextMenu.y, window.innerHeight - 360), background: 'white', border: '1px solid #e5e7eb', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.15)', zIndex: 299, minWidth: 164, overflow: 'hidden' }}
              onClick={e => e.stopPropagation()}
            >
              {canEditMindmap && (
                <>
                  <CtxItem icon={<Plus size={13} color="#10b981" />} onClick={() => { addChild(contextMenu.nodeId); setContextMenu(null); }}>{tr('addChild')}</CtxItem>
                  {nodes.find(n => n.id === contextMenu.nodeId)?.note ? (
                    <>
                      <CtxItem icon={<FileText size={13} color="#f59e0b" />} onClick={() => { setNotePanel({ nodeId: contextMenu.nodeId }); setContextMenu(null); }}>{tr('modifyNote')}</CtxItem>
                      <CtxItem icon={<Trash2 size={13} color="#ef4444" />} danger onClick={() => { const newNodes = nodesRef.current.map(n => n.id === contextMenu.nodeId ? { ...n, note: undefined } : n); pushHistory(newNodes); setNodes(newNodes); setContextMenu(null); }}>{tr('deleteNote')}</CtxItem>
                    </>
                  ) : (
                    <CtxItem icon={<FileText size={13} color="#f59e0b" />} onClick={() => { setNotePanel({ nodeId: contextMenu.nodeId }); setContextMenu(null); }}>{tr('addNoteAction')}</CtxItem>
                  )}
                  {nodes.find(n => n.id === contextMenu.nodeId)?.url ? (
                    <>
                      <CtxItem icon={<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>} onClick={() => { setLinkPanel({ nodeId: contextMenu.nodeId }); setContextMenu(null); }}>{tr('modifyLink')}</CtxItem>
                      <CtxItem icon={<Trash2 size={13} color="#ef4444" />} danger onClick={() => { const newNodes = nodesRef.current.map(n => n.id === contextMenu.nodeId ? { ...n, url: undefined } : n); pushHistory(newNodes); setNodes(newNodes); setContextMenu(null); }}>{tr('deleteLink')}</CtxItem>
                    </>
                  ) : (
                    <CtxItem icon={<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>} onClick={() => { setLinkPanel({ nodeId: contextMenu.nodeId }); setContextMenu(null); }}>{tr('addLink')}</CtxItem>
                  )}
                  <CtxItem icon={<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>} onClick={() => { const n = nodes.find(x => x.id === contextMenu.nodeId); if (n) startEdit(n.id, n.text); setContextMenu(null); }}>{tr('rename')}</CtxItem>
                  {nodes.find(n => n.id === contextMenu.nodeId)?.parentId !== null && (
                    <CtxItem icon={<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#8b5cf6" strokeWidth="2"><path d="M5 12h14"/><path d="M12 5l7 7-7 7"/></svg>} onClick={() => { setMovePicker({ nodeId: contextMenu.nodeId }); setContextMenu(null); }}>{tr('moveToNode')}</CtxItem>
                  )}
                </>
              )}
              {/* Manage linking */}
              {(() => {
                const ctxNode = nodes.find(n => n.id === contextMenu.nodeId);
                const ctxNS = nodeNS(ctxNode?.id ?? 0);
                // "Link mindmap" on any node in the own namespace (NS=0)
                const isRoot = ctxNS === 0 && ctxNode !== undefined;
                // "Unlink" on the root of an injected child mindmap (parent sits in a lower namespace)
                const isLinkedChildRoot = ctxNS > 0 &&
                  nodeNS(ctxNode?.parentId ?? 0) !== ctxNS;
                if (!canEditMindmapRef.current) return null;
                if (isLinkedChildRoot) {
                  const linkIdx = ctxNS - 1;
                  const existingLink = mindmapLinks[linkIdx];
                  if (!existingLink) return null;
                  return (
                    <CtxItem
                      icon={<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round"><path d="M18.84 12.25l1.72-1.71h-.02a5.004 5.004 0 0 0-.12-7.07 5.006 5.006 0 0 0-6.95 0l-1.72 1.71"/><path d="M5.17 11.75l-1.71 1.71a5.004 5.004 0 0 0 .12 7.07 5.006 5.006 0 0 0 6.95 0l1.71-1.71"/><line x1="8" y1="2" x2="8" y2="5"/><line x1="2" y1="8" x2="5" y2="8"/><line x1="16" y1="19" x2="16" y2="22"/><line x1="19" y1="16" x2="22" y2="16"/></svg>}
                      danger
                      onClick={async () => {
                        // Eagerly remove from ref so the realtime handler won't re-inject while the await is pending
                        mindmapLinksRef.current = mindmapLinksRef.current.filter((_, i) => i !== linkIdx);
                        // Update UI immediately (before DB round-trip so nodes don't linger)
                        setNodes(prev => {
                          const withoutNS = prev.filter(n => nodeNS(n.id) !== linkIdx + 1);
                          return withoutNS.map(n => {
                            const ns = nodeNS(n.id);
                            if (ns > linkIdx + 1) {
                              return {
                                ...n,
                                id: n.id - LINK_OFFSET,
                                parentId: n.parentId !== null && nodeNS(n.parentId) > linkIdx + 1
                                  ? n.parentId - LINK_OFFSET : n.parentId,
                              };
                            }
                            return n;
                          });
                        });
                        setMindmapLinks(prev => prev.filter((_, i) => i !== linkIdx));
                        setContextMenu(null);
                        // DB delete in background after UI is already updated
                        await supabase.from('mindmap_links').delete().eq('parent_id', currentIdRef.current).eq('child_id', existingLink.childId);
                      }}
                    >{tr('unlinkMindmap')}</CtxItem>
                  );
                }
                if (isRoot) {
                  return (
                    <CtxItem
                      icon={<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2" strokeLinecap="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>}
                      onClick={async () => {
                        const linked = mindmapLinks.map(l => l.childId);
                        const { data } = await supabase.from('flowcharts')
                          .select('id, title, flowchart_access!left(role)')
                          .eq('map_type', 'mindmap')
                          .eq('flowchart_access.user_id', user.id)
                          .is('deleted_at', null)
                          .neq('id', currentIdRef.current ?? '');
                        const filtered = (data ?? []).filter(m => !linked.includes(m.id));
                        setAvailableMindmaps(filtered.map(m => ({ id: m.id, title: m.title || tr('untitled') })));
                        setShowLinkModal({ nodeId: contextMenu.nodeId });
                        setContextMenu(null);
                      }}
                    >{tr('linkMindmap')}</CtxItem>
                  );
                }
                return null;
              })()}
              <div style={{ padding: '6px 12px' }}>
                <div style={{ fontSize: 10, color: '#9ca3af', marginBottom: 4 }}>{tr('priorityLabel')}</div>
                <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                  {([1, 2, 3, 4, 5] as const).map(p => {
                    const active = userPriorities[contextMenu.nodeId] === p;
                    return (
                      <button key={p} onClick={() => {
                        const ids = getMultiActionIds(contextMenu.nodeId);
                        const newPrio = active ? undefined : p;
                        setUserPriorities(prev => {
                          const next = { ...prev };
                          ids.forEach(id => { if (newPrio) next[id] = newPrio; else delete next[id]; });
                          return next;
                        });
                        setContextMenu(null);
                        setQuadrantOrder(prev => {
                          if (!prev) return prev;
                          const newQ = prev.map(q => q.filter(id => !ids.includes(id))) as [number[], number[], number[], number[]];
                          if (newPrio && newPrio <= 4) ids.forEach(id => newQ[newPrio - 1].push(id));
                          return newQ;
                        });
                      }}
                        title={tr('priority', { p })}
                        style={{ width: 30, height: 30, borderRadius: 7, background: PRIO_COLORS[p], border: active ? '2.5px solid #1f2937' : '2px solid white', cursor: 'pointer', boxShadow: '0 1px 3px rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, color: 'white' }}
                      >{p}</button>
                    );
                  })}
                  <button
                    onClick={() => {
                      const ids = getMultiActionIds(contextMenu.nodeId);
                      setUserPriorities(prev => {
                        const next = { ...prev };
                        ids.forEach(id => delete next[id]);
                        return next;
                      });
                      setContextMenu(null);
                      setQuadrantOrder(prev => prev ? prev.map(q => q.filter(id => !ids.includes(id))) as [number[], number[], number[], number[]] : prev);
                      setDayOrder(prev => prev.map(d => d.filter(id => !ids.includes(id))));
                    }}
                    title={tr('removePriority')}
                    style={{ width: 22, height: 22, borderRadius: '50%', background: '#f3f4f6', border: '1.5px solid #d1d5db', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af' }}
                  >
                    <X size={10} strokeWidth={2.5} />
                  </button>
                </div>
              </div>
              {canEditMindmap && (
                <>
                  <div style={{ borderTop: '1px solid #f3f4f6' }} />
                  {(() => {
                    const ids = getMultiActionIds(contextMenu.nodeId);
                    const allHighlighted = ids.every(id => nodes.find(n => n.id === id)?.highlighted);
                    return (
                      <CtxItem
                        icon={<Highlighter size={13} color="#eab308" />}
                        onClick={() => { toggleHighlight(ids); setContextMenu(null); }}
                      >
                        {allHighlighted ? tr('removeHighlight') : tr('highlightNode')}
                      </CtxItem>
                    );
                  })()}
                  {(() => {
                    const ids = getMultiActionIds(contextMenu.nodeId);
                    const allCompleted = ids.every(id => userCompleted[id]);
                    return (
                      <CtxItem
                        icon={<CheckCircle2 size={13} color="#22c55e" />}
                        onClick={() => {
                          setUserCompleted(prev => {
                            const next = { ...prev };
                            ids.forEach(id => { if (allCompleted) delete next[id]; else next[id] = true; });
                            return next;
                          });
                          setContextMenu(null);
                        }}
                      >
                        {allCompleted ? tr('removeCompleted') : tr('markCompleted')}
                      </CtxItem>
                    );
                  })()}
                  {nodes.find(n => n.id === contextMenu.nodeId)?.parentId !== null && (
                    <>
                      <div style={{ borderTop: '1px solid #f3f4f6' }} />
                      <CtxItem icon={<Trash2 size={13} color="#ef4444" />} danger onClick={() => { deleteNodes(getMultiActionIds(contextMenu.nodeId)); setContextMenu(null); }}>Delete</CtxItem>
                    </>
                  )}
                </>
              )}
            </div>
          </>
        )}



        {/* ── Link mindmap modal ── */}
        {showLinkModal && (
          <>
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 398 }} onClick={() => setShowLinkModal(null)} />
            <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', background: 'white', borderRadius: 14, boxShadow: '0 12px 40px rgba(0,0,0,0.22)', zIndex: 399, width: 360, maxHeight: 480, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <div style={{ padding: '16px 20px 12px', borderBottom: '1px solid #e5e7eb' }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>{tr('linkMindmapTitle')}</div>
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 3 }}>{tr('linkMindmapDesc')}</div>
              </div>
              <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
                {availableMindmaps.length === 0 ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>{tr('noOtherMindmaps')}</div>
                ) : availableMindmaps.map(m => (
                  <button key={m.id}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '10px 20px', background: 'none', border: 'none', fontSize: 13, color: '#374151', cursor: 'pointer', textAlign: 'left' }}
                    onMouseOver={e => { e.currentTarget.style.background = '#f3f4f6'; }}
                    onMouseOut={e => { e.currentTarget.style.background = 'none'; }}
                    onClick={async () => {
                      if (!currentIdRef.current || !showLinkModal) return;
                      const nodeId = showLinkModal.nodeId;
                      // Circular reference check
                      const { data: existingChildLinks } = await supabase.from('mindmap_links').select('child_id').eq('parent_id', m.id);
                      const childChain = (existingChildLinks ?? []).map(r => r.child_id);
                      if (m.id === currentIdRef.current || childChain.includes(currentIdRef.current)) {
                        alert(tr('cannotLinkCircular'));
                        return;
                      }
                      // Save the link
                      await supabase.from('mindmap_links').insert({ parent_id: currentIdRef.current, child_id: m.id, node_id: nodeId });
                      const newLinks = [...mindmapLinksRef.current, { nodeId, childId: m.id }];
                      setMindmapLinks(newLinks);
                      // Inject child nodes
                      const linkIdx = newLinks.length - 1;
                      const offset = (linkIdx + 1) * LINK_OFFSET;
                      const { data: childData } = await supabase.from('flowcharts').select('boxes, title, version').eq('id', m.id).single();
                      if (childData) {
                        const childNodes: MindmapNode[] = (Array.isArray(childData.boxes) ? childData.boxes : JSON.parse(childData.boxes || '[]')).filter(isMindmapNode);
                        setChildTitles(prev => ({ ...prev, [m.id]: childData.title }));
                        childVersionsRef.current[m.id] = typeof childData.version === 'number' ? childData.version : 0;
                        childBaseNodesRef.current[m.id] = childNodes;
                        const injected = childNodes.map(n => ({ ...n, id: n.id + offset, parentId: n.parentId === null ? nodeId : n.parentId + offset, manualX: undefined, manualY: undefined, manualW: undefined }));
                        setNodes(prev => [...prev, ...injected]);
                      }
                      setShowLinkModal(null);
                    }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="3"/><line x1="12" y1="2" x2="12" y2="5"/><line x1="12" y1="19" x2="12" y2="22"/><line x1="4.22" y1="4.22" x2="6.34" y2="6.34"/><line x1="17.66" y1="17.66" x2="19.78" y2="19.78"/><line x1="2" y1="12" x2="5" y2="12"/><line x1="19" y1="12" x2="22" y2="12"/><line x1="4.22" y1="19.78" x2="6.34" y2="17.66"/><line x1="17.66" y1="6.34" x2="19.78" y2="4.22"/></svg>
                    {m.title}
                  </button>
                ))}
              </div>
              <div style={{ padding: '10px 20px', borderTop: '1px solid #e5e7eb' }}>
                <button onClick={() => setShowLinkModal(null)} style={{ width: '100%', padding: '8px', background: '#f3f4f6', border: 'none', borderRadius: 8, fontSize: 13, cursor: 'pointer', color: '#374151', fontWeight: 600 }}>{tr('cancel')}</button>
              </div>
            </div>
          </>
        )}

        {/* ── Move picker modal ── */}
        {movePicker && (() => {
          const cm = buildChildrenMap(nodes);
          const excluded = new Set([movePicker.nodeId, ...getAllDescendants(cm, movePicker.nodeId)]);
          const movingNode = nodes.find(n => n.id === movePicker.nodeId);

          // Pre-order traversal for an ordered list
          const orderedNodes: { node: MindmapNode; level: number }[] = [];
          const traverse = (id: number, level: number) => {
            const n = nodes.find(x => x.id === id);
            if (!n) return;
            if (!excluded.has(id)) orderedNodes.push({ node: n, level });
            (cm.get(id) ?? []).forEach(childId => traverse(childId, level + 1));
          };
          const root = nodes.find(n => n.parentId === null);
          if (root) traverse(root.id, 0);

          return (
            <>
              <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 400 }} onClick={() => setMovePicker(null)} />
              <div style={{ position: 'fixed', left: '50%', top: '50%', transform: 'translate(-50%,-50%)', background: 'white', borderRadius: 12, boxShadow: '0 8px 32px rgba(0,0,0,0.22)', zIndex: 401, width: 'min(340px, 90vw)', maxHeight: '70dvh', display: 'flex', flexDirection: 'column' }}>
                <div style={{ padding: '14px 16px 12px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#1f2937' }}>{tr('moveToNode')}</div>
                    <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2, maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{movingNode?.text || tr('emptyNode')}</div>
                  </div>
                  <button onClick={() => setMovePicker(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 4, flexShrink: 0 }}>
                    <X size={16} />
                  </button>
                </div>
                <div style={{ overflowY: 'auto', flex: 1 }}>
                  {orderedNodes.map(({ node, level }) => {
                    const isCurrent = node.id === movingNode?.parentId;
                    const ec = node.customColor ?? node.color;
                    return (
                      <button
                        key={node.id}
                        onClick={() => { if (!isCurrent) applyReparent(movePicker.nodeId, node.id); setMovePicker(null); }}
                        style={{ width: '100%', textAlign: 'left', border: 'none', cursor: isCurrent ? 'default' : 'pointer', paddingTop: 9, paddingBottom: 9, paddingLeft: 16 + level * 14, paddingRight: 16, background: isCurrent ? '#f0fdf4' : 'white', display: 'flex', alignItems: 'center', gap: 8, borderBottom: '1px solid #f9fafb', fontSize: 13, color: '#1f2937' }}
                      >
                        <span style={{ width: 10, height: 10, borderRadius: 3, background: ec, flexShrink: 0 }} />
                        <span style={{ flex: 1 }}>{node.text || '(empty)'}</span>
                        {isCurrent && <span style={{ fontSize: 10, color: '#16a34a', fontWeight: 600, flexShrink: 0 }}>huidig</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            </>
          );
        })()}

        {/* ── Priority Matrix overlay ── */}
        {showPriorityMatrix && quadrantOrder && (() => {
          const saveLabels = (labels: [string, string, string, string]) => {
            setQuadrantLabels(labels);
            try { localStorage.setItem('mm-quadrant-labels', JSON.stringify(labels)); } catch {}
            if (user?.id) supabase.from('user_profiles').update({ quadrant_labels: labels }).eq('id', user.id).then(() => {}, () => {});
          };
          const saveDayLabels = (labels: string[]) => {
            setDayLabels(labels);
            try { localStorage.setItem('mm-day-labels', JSON.stringify(labels)); } catch {}
            if (user?.id) supabase.from('user_profiles').update({ day_labels: labels }).eq('id', user.id).then(() => {}, () => {});
          };
          const saveScheduleDays = (days: number) => {
            setScheduleDays(days);
            try { localStorage.setItem('mm-schedule-days', JSON.stringify(days)); } catch {}
            if (user?.id) supabase.from('user_profiles').update({ schedule_days: days }).eq('id', user.id).then(() => {}, () => {});
            // Shrinking drops any items in the now-hidden day columns back to
            // "unscheduled" (same safe/reversible philosophy as Clear Schedule) —
            // growing just appends empty columns. Node priorities/quadrant placement
            // are untouched either way, this only affects the Schedule view's days.
            setDayOrder(prev => {
              const next = prev.slice(0, days);
              while (next.length < days) next.push([]);
              return next;
            });
          };

          const quadrantColors = [
            { bg: '#fef2f2', border: '#fca5a5', header: '#ef4444' },
            { bg: '#fff7ed', border: '#fdba74', header: '#f97316' },
            { bg: '#fefce8', border: '#fde047', header: '#ca8a04' },
            { bg: '#eff6ff', border: '#93c5fd', header: '#3b82f6' },
          ];
          const DAY_BG = '#f0f9ff'; const DAY_BORDER = '#7dd3fc'; const DAY_HEADER = '#0369a1';

          const getAncestorLabel = (nodeId: number): string => {
            const parts: string[] = [];
            let cur = nodes.find(n => n.id === nodeId);
            while (cur?.parentId != null) {
              const pid = cur.parentId;
              const parent = nodes.find(n => n.id === pid);
              if (!parent) break;
              if (parent.parentId !== null) parts.unshift(parent.text || '…');
              cur = parent;
            }
            return parts.length > 0 ? `(${parts.join(' › ')})` : '';
          };

          const expandAncestors = (nodeId: number) => {
            const toExpand = new Set<number>();
            let cur = nodes.find(n => n.id === nodeId);
            while (cur?.parentId != null) {
              const pid = cur.parentId;
              const parent = nodes.find(n => n.id === pid);
              if (!parent) break;
              if (parent.collapsed) toExpand.add(parent.id);
              cur = parent;
            }
            if (toExpand.size > 0) setNodes(prev => prev.map(n => toExpand.has(n.id) ? { ...n, collapsed: false } : n));
          };

          const insertBefore = (arr: number[], nodeId: number, beforeNodeId: number | null): number[] => {
            const filtered = arr.filter(id => id !== nodeId);
            if (beforeNodeId === null) return [...filtered, nodeId];
            const idx = filtered.indexOf(beforeNodeId);
            return idx >= 0 ? [...filtered.slice(0, idx), nodeId, ...filtered.slice(idx)] : [...filtered, nodeId];
          };

          const applyDrop = (over: { dst: 'q'; qi: number; beforeNodeId: number | null } | { dst: 'd'; di: number; beforeNodeId: number | null }) => {
            if (!pmDrag) return;
            const movedId = pmDrag.nodeId;
            if (over.dst === 'q') {
              const newQ: number[][] = quadrantOrder.map(q => [...q]);
              if (pmDrag.src === 'q') {
                if (pmDrag.qi !== over.qi) {
                  newQ[pmDrag.qi] = newQ[pmDrag.qi].filter(id => id !== movedId);
                  setUserPriorities(prev => ({ ...prev, [movedId]: (over.qi + 1) as 1|2|3|4 }));
                }
                newQ[over.qi] = insertBefore(newQ[over.qi], movedId, over.beforeNodeId);
              } else {
                const srcDi = (pmDrag as { src: 'd'; di: number; nodeId: number }).di;
                setUserPriorities(prev => ({ ...prev, [movedId]: (over.qi + 1) as 1|2|3|4 }));
                const alreadyInTargetQ = newQ[over.qi].includes(movedId);
                if (alreadyInTargetQ && over.beforeNodeId === null) {
                  // Returning to original quadrant — keep existing position (was greyed-out there)
                } else {
                  for (let i = 0; i < 4; i++) newQ[i] = newQ[i].filter(id => id !== movedId);
                  newQ[over.qi] = insertBefore(newQ[over.qi], movedId, over.beforeNodeId);
                }
                setDayOrder(prev => prev.map((d, i) => i === srcDi ? d.filter(id => id !== movedId) : d));
              }
              setQuadrantOrder(newQ as [number[], number[], number[], number[]]);
            } else {
              if (pmDrag.src === 'q') {
                // Stays in the quadrant, only gets added to a day as well
                setDayOrder(prev => {
                  const newD = prev.map(d => d.filter(id => id !== movedId));
                  // Pad defensively: dayOrder (per-mindmap) can still be shorter than
                  // scheduleDays (per-user, global) right after bumping the day count
                  // on a different, not-yet-resized mindmap.
                  while (newD.length <= over.di) newD.push([]);
                  newD[over.di] = insertBefore(newD[over.di], movedId, over.beforeNodeId);
                  return newD;
                });
              } else {
                const srcDi = (pmDrag as { src: 'd'; di: number; nodeId: number }).di;
                setDayOrder(prev => {
                  const newD = prev.map(d => [...d]);
                  while (newD.length <= over.di || newD.length <= srcDi) newD.push([]);
                  if (srcDi !== over.di) newD[srcDi] = newD[srcDi].filter(id => id !== movedId);
                  newD[over.di] = insertBefore(newD[over.di], movedId, over.beforeNodeId);
                  return newD;
                });
              }
            }
          };
          pmApplyDropRef.current = applyDrop;

          const root = nodes.find(n => n.parentId === null);
          const l1Nodes = root ? (buildChildrenMap(nodes).get(root.id) ?? []).map(id => nodes.find(n => n.id === id)).filter(Boolean) as typeof nodes : [];

          const isUnderL1 = (nodeId: number, l1Id: number): boolean => {
            let cur = nodes.find(n => n.id === nodeId);
            while (cur && cur.parentId != null) {
              if (cur.parentId === l1Id || cur.id === l1Id) return true;
              const pid = cur.parentId;
              cur = nodes.find(n => n.id === pid);
            }
            return false;
          };

          const renderCard = (n: typeof nodes[0], borderColor: string, headerColor: string,
            durationBottom = false,
            pmSrc: { type: 'q'; qi: number } | { type: 'd'; di: number } = { type: 'q', qi: 0 }) => {
            const isDragging = pmDrag?.nodeId === n.id;
            const isScheduled = !durationBottom && dayOrder.some(d => d.includes(n.id));
            const isCompleted = !!userCompleted[n.id];
            const isFaded = isScheduled || isCompleted;
            const ancestorLabel = getAncestorLabel(n.id);
            const durationInput = (
              <input
                type="number"
                min={1}
                value={userDurations[n.id] ?? n.duration ?? ''}
                onClick={e => e.stopPropagation()}
                onChange={e => {
                  const val = e.target.value;
                  setUserDurations(prev => val ? { ...prev, [n.id]: val } : Object.fromEntries(Object.entries(prev).filter(([k]) => Number(k) !== n.id)));
                }}
                className="no-spinner"
                style={{ width: 40, fontSize: 10, border: '1px solid #d1d5db', borderRadius: 4, padding: '2px 4px', textAlign: 'center', color: '#6b7280', background: 'white', flexShrink: 0 }}
              />
            );
            return (
              <div
                key={n.id}
                data-pmcard={n.id}
                data-pmsrc-type={pmSrc.type}
                data-pmsrc-qi={pmSrc.type === 'q' ? pmSrc.qi : undefined}
                data-pmsrc-di={pmSrc.type === 'd' ? (pmSrc as { type: 'd'; di: number }).di : undefined}
                onContextMenu={e => { e.preventDefault(); e.stopPropagation(); setPmContextMenu({ nodeId: n.id, x: e.clientX, y: e.clientY }); }}
                onMouseDown={e => {
                  if (e.button !== 0) return;
                  const target = e.target as HTMLElement;
                  if (target.tagName === 'INPUT' || target.tagName === 'BUTTON' || target.tagName === 'SELECT' || target.tagName === 'TEXTAREA') return;
                  e.preventDefault();
                  pmTouchRef.current = { nodeId: n.id, src: pmSrc, isDragging: false, startX: e.clientX, startY: e.clientY };
                }}
                onClick={() => {
                  if (pmTouchRef.current || pmMouseDraggedRef.current) return;
                  expandAncestors(n.id);
                  setShowPriorityMatrix(false);
                  setPmDrag(null); setPmDragOver(null);
                  setSelectedNode(n.id);
                  centerNodeAfterMatrixRef.current = n.id;
                }}
                style={{ cursor: 'grab', padding: '4px 6px', marginBottom: 2, borderRadius: 5, background: isDragging ? 'transparent' : isFaded ? '#f9fafb' : 'white', border: `1px solid ${isDragging ? 'transparent' : borderColor}`, display: 'flex', flexDirection: durationBottom ? 'column' : 'row', alignItems: 'flex-start', gap: durationBottom ? 2 : 5, opacity: isDragging ? 0.3 : isFaded ? 0.5 : 1, pointerEvents: isDragging ? 'none' : 'auto', userSelect: 'none' }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 5, width: '100%' }}>
                  <span
                    style={{ fontSize: 12, color: '#d1d5db', flexShrink: 0, marginTop: 1, userSelect: 'none', touchAction: 'none', cursor: 'grab' }}
                    onTouchStart={e => {
                      if (e.touches.length !== 1) return;
                      e.preventDefault();
                      e.stopPropagation();
                      pmTouchRef.current = { nodeId: n.id, src: pmSrc, isDragging: false, startX: e.touches[0].clientX, startY: e.touches[0].clientY };
                    }}
                  >⠿</span>
                  <button
                    onClick={e => {
                      e.stopPropagation();
                      setUserCompleted(prev => isCompleted
                        ? Object.fromEntries(Object.entries(prev).filter(([k]) => Number(k) !== n.id))
                        : { ...prev, [n.id]: true });
                    }}
                    title={isCompleted ? 'Mark as not completed' : 'Mark as completed'}
                    style={{ background: 'none', border: 'none', padding: 0, margin: 0, cursor: 'pointer', flexShrink: 0, display: 'flex', color: isCompleted ? '#22c55e' : '#d1d5db' }}
                  >
                    {isCompleted ? <CheckCircle2 size={14} /> : <Circle size={14} />}
                  </button>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: n.customColor ?? n.color, flexShrink: 0, marginTop: 3 }} />
                  {userPriorities[n.id] && <span style={{ width: 16, height: 16, borderRadius: 4, background: PRIO_COLORS[userPriorities[n.id]], display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800, color: 'white', flexShrink: 0 }}>{userPriorities[n.id]}</span>}
                  <span style={{ fontSize: 11, color: '#1f2937', lineHeight: 1.4, flex: 1, textDecoration: isCompleted ? 'line-through' : 'none' }}>
                    {n.text || '(empty)'}
                    {ancestorLabel && <span style={{ color: '#9ca3af', marginLeft: 4 }}>{ancestorLabel}</span>}
                  </span>
                  {!durationBottom && durationInput}
                </div>
                {durationBottom && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 3, paddingLeft: 4 }}>
                    {durationInput}
                    {(userDurations[n.id] ?? n.duration) && <span style={{ fontSize: 9, color: '#9ca3af' }}>min</span>}
                  </div>
                )}
              </div>
            );
          };

          const dropLine = (color: string) => (
            <div style={{ height: 2, borderRadius: 1, background: color, margin: '2px 0', opacity: 0.8 }} />
          );

          const submitAddPriority = () => {
            const text = addPriorityText.trim();
            if (!text || addPriorityBranchId === null || !addPriorityPopup) return;
            const qi = addPriorityPopup.source === 'quadrant' ? addPriorityPopup.qi : addPriorityQi - 1;
            const di = addPriorityPopup.source === 'day' ? addPriorityPopup.di : undefined;
            addPriorityNode(addPriorityBranchId, text, qi, di);
            setAddPriorityPopup(null);
          };

          const submitEditPmTitle = () => {
            if (!editPmTitle || !canEditMindmapRef.current) return;
            const text = editPmTitle.text.trim() || 'Topic';
            const newNodes = nodesRef.current.map(n => n.id === editPmTitle.nodeId ? { ...n, text } : n);
            pushHistory(newNodes);
            setNodes(newNodes);
            setEditPmTitle(null);
          };

          const closePriorityMatrix = () => {
            setShowPriorityMatrix(false);
            setPmDrag(null);
            setPmDragOver(null);
            setPmFilter(null);
            setPmContextMenu(null);
            setAddPriorityPopup(null);
            setEditPmTitle(null);
          };

          return (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 500, display: 'flex', flexDirection: 'column' }}>
              <div style={{ background: 'white', padding: '10px 16px', display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', borderBottom: '1px solid #e5e7eb', flexShrink: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 15, fontWeight: 700, color: '#1f2937' }}>Priority Matrix</span>
                  <span style={{ fontSize: 11, color: '#9ca3af' }}>{title}</span>
                </div>
                <button
                  onClick={closePriorityMatrix}
                  style={{ fontSize: 12, padding: '5px 12px', borderRadius: 12, border: '1px solid #d1d5db', background: 'white', color: '#374151', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, justifySelf: 'center' }}
                >
                  <ArrowLeft size={13} /> {tr('backToMindmap')}
                </button>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifySelf: 'end' }}>
                  <button
                    onClick={() => {
                      const completedIds = new Set(Object.entries(userCompleted).filter(([, c]) => c).map(([k]) => Number(k)));
                      if (completedIds.size === 0) return;
                      setUserPriorities(prev => Object.fromEntries(Object.entries(prev).filter(([k]) => !completedIds.has(Number(k)))));
                      setUserCompleted(prev => Object.fromEntries(Object.entries(prev).filter(([k]) => !completedIds.has(Number(k)))));
                      setQuadrantOrder(prev => prev ? prev.map(q => q.filter(id => !completedIds.has(id))) as [number[], number[], number[], number[]] : prev);
                      setDayOrder(prev => prev.map(d => d.filter(id => !completedIds.has(id))));
                    }}
                    title={tr('completedRemovePrioTitle')}
                    style={{ fontSize: 11, padding: '3px 10px', borderRadius: 12, border: '1.5px solid #86efac', background: '#f0fdf4', color: '#16a34a', fontWeight: 400, cursor: 'pointer' }}
                  >{tr('completedRemovePrio')}</button>
                  <button
                    onClick={() => {
                      const completedIds = Object.entries(userCompleted).filter(([, c]) => c).map(([k]) => Number(k));
                      if (completedIds.length === 0) return;
                      if (!window.confirm(tr('confirmDeleteCompletedNodes', { count: completedIds.length }))) return;
                      // deleteNodes removes the nodes themselves (cascading to their
                      // descendants) and already cleans up userPriorities for everything
                      // it deletes — but not userCompleted/quadrantOrder/dayOrder, which
                      // it has no knowledge of. Compute the same descendant-inclusive set
                      // ourselves so those don't keep stale, recyclable-ID references.
                      const toRemove = new Set<number>(completedIds);
                      const cm = buildChildrenMap(nodesRef.current);
                      completedIds.forEach(id => getAllDescendants(cm, id).forEach(d => toRemove.add(d)));
                      setUserCompleted(prev => Object.fromEntries(Object.entries(prev).filter(([k]) => !toRemove.has(Number(k)))));
                      setQuadrantOrder(prev => prev ? prev.map(q => q.filter(id => !toRemove.has(id))) as [number[], number[], number[], number[]] : prev);
                      setDayOrder(prev => prev.map(d => d.filter(id => !toRemove.has(id))));
                      deleteNodes(completedIds);
                    }}
                    title={tr('completedDeleteAllTitle')}
                    style={{ fontSize: 11, padding: '3px 10px', borderRadius: 12, border: '1.5px solid #fca5a5', background: '#fef2f2', color: '#dc2626', fontWeight: 400, cursor: 'pointer' }}
                  >{tr('completedDeleteAll')}</button>
                  {pmShowDays && (
                    <>
                      <select
                        value={scheduleDays}
                        onChange={e => saveScheduleDays(Number(e.target.value))}
                        title={tr('scheduleDaysTitle')}
                        style={{ fontSize: 11, padding: '3px 6px', borderRadius: 12, border: `1.5px solid ${DAY_BORDER}`, background: DAY_BG, color: DAY_HEADER, fontWeight: 600, cursor: 'pointer' }}
                      >
                        {[3, 5, 7].map(n => <option key={n} value={n}>{tr('scheduleDaysOption', { n })}</option>)}
                      </select>
                      <button
                        onClick={() => setDayOrder(Array.from({ length: scheduleDays }, () => []))}
                        style={{ fontSize: 11, padding: '3px 10px', borderRadius: 12, border: '1.5px solid #fca5a5', background: '#fff1f2', color: '#ef4444', fontWeight: 400, cursor: 'pointer' }}
                      >Clear Schedule</button>
                    </>
                  )}
                  <div
                    onClick={() => { const next = !pmShowDays; setPmShowDays(next); try { localStorage.setItem('mm-pm-show-days', String(next)); } catch {} }}
                    role="switch"
                    aria-checked={pmShowDays}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', userSelect: 'none', flexShrink: 0 }}
                  >
                    <span style={{ fontSize: 11, color: pmShowDays ? DAY_HEADER : '#6b7280', fontWeight: pmShowDays ? 700 : 400 }}>Schedule</span>
                    <span style={{ position: 'relative', width: 32, height: 18, borderRadius: 9, background: pmShowDays ? DAY_BORDER : '#d1d5db', transition: 'background 0.15s', flexShrink: 0 }}>
                      <span style={{ position: 'absolute', top: 2, left: pmShowDays ? 16 : 2, width: 14, height: 14, borderRadius: '50%', background: 'white', transition: 'left 0.15s', boxShadow: '0 1px 2px rgba(0,0,0,0.3)' }} />
                    </span>
                  </div>
                  <button onClick={closePriorityMatrix} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', padding: 4 }}>
                    <X size={18} />
                  </button>
                </div>
              </div>
              {l1Nodes.length > 0 && (
                <div style={{ background: '#f8fafc', padding: '6px 12px', display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0, borderBottom: '1px solid #e5e7eb', overflowX: 'auto' }}>
                  <span style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, flexShrink: 0 }}>Filter</span>
                  <button onClick={() => setPmFilter(null)} style={{ fontSize: 11, padding: '3px 8px', borderRadius: 12, border: `1.5px solid ${pmFilter === null ? '#6366f1' : '#d1d5db'}`, background: pmFilter === null ? '#eef2ff' : 'white', color: pmFilter === null ? '#4338ca' : '#374151', fontWeight: pmFilter === null ? 700 : 400, cursor: 'pointer', flexShrink: 0 }}>All</button>
                  {l1Nodes.map(n => {
                    const active = pmFilter !== null && pmFilter.includes(n.id); const ec = n.customColor ?? n.color;
                    return (
                      <button key={n.id} onClick={(e) => {
                        if (e.ctrlKey || e.metaKey) {
                          setPmFilter(prev => {
                            const cur = prev ?? [];
                            if (cur.includes(n.id)) {
                              const next = cur.filter(id => id !== n.id);
                              return next.length === 0 ? null : next;
                            }
                            return [...cur, n.id];
                          });
                        } else {
                          setPmFilter(prev => prev !== null && prev.length === 1 && prev[0] === n.id ? null : [n.id]);
                        }
                      }}
                        style={{ fontSize: 11, padding: '3px 8px', borderRadius: 12, border: `1.5px solid ${active ? ec : '#d1d5db'}`, background: active ? ec : 'white', color: active ? 'white' : '#374151', fontWeight: active ? 700 : 400, cursor: 'pointer', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 4 }}>
                        <span style={{ width: 7, height: 7, borderRadius: '50%', background: active ? 'white' : ec, flexShrink: 0 }} />
                        {n.text || '(empty)'}
                      </button>
                    );
                  })}
                </div>
              )}
              <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
                <div style={{ flex: pmShowDays ? '1.3' : 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr 1fr', gap: 8, padding: 10, overflow: 'hidden', minWidth: 0 }}>
                  {quadrantLabels.map((lbl, qi) => {
                    const qc = quadrantColors[qi];
                    const allQNodes = quadrantOrder[qi].map(id => nodes.find(n => n.id === id)).filter(Boolean) as typeof nodes;
                    const qNodes = pmFilter === null ? allQNodes : allQNodes.filter(n => pmFilter.some(fId => isUnderL1(n.id, fId)));
                    return (
                      <div key={qi} style={{ background: qc.bg, border: `1.5px solid ${qc.border}`, borderRadius: 10, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
                        <div style={{ padding: '5px 8px', borderBottom: `1px solid ${qc.border}`, display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                          <span style={{ width: 18, height: 18, borderRadius: 4, background: PRIO_COLORS[qi + 1], display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, color: 'white', flexShrink: 0 }}>{qi + 1}</span>
                          <input value={lbl} onChange={e => { const next = [...quadrantLabels] as [string,string,string,string]; next[qi] = e.target.value; saveLabels(next); }}
                            style={{ flex: 1, fontSize: 11, fontWeight: 700, color: qc.header, background: 'transparent', border: 'none', outline: 'none', minWidth: 0 }} />
                          {qNodes.length > 0 && <span style={{ fontSize: 10, color: qc.header, fontWeight: 600, flexShrink: 0 }}>{qNodes.length}</span>}
                          {l1Nodes.length > 0 && (
                            <button
                              onClick={() => { setAddPriorityPopup({ source: 'quadrant', qi }); setAddPriorityBranchId(l1Nodes[0].id); setAddPriorityText(''); }}
                              title={tr('addPriorityNodeTitle')}
                              style={{ background: 'none', border: 'none', padding: 0, margin: 0, cursor: 'pointer', flexShrink: 0, display: 'flex', color: qc.header }}
                            ><Plus size={13} /></button>
                          )}
                        </div>
                        <div style={{ flex: 1, overflowY: 'auto', padding: '4px 5px' }}
                          data-pmzone="q" data-pmzone-qi={qi}>
                          {qNodes.length === 0 && pmDrag === null
                            ? <span style={{ fontSize: 11, color: '#9ca3af', fontStyle: 'italic', padding: '4px 2px', display: 'block' }}>No items</span>
                            : qNodes.map(n => (
                              <div key={n.id}>
                                {pmDragOver?.dst === 'q' && pmDragOver.qi === qi && pmDragOver.beforeNodeId === n.id && pmDrag?.nodeId !== n.id && dropLine(qc.header)}
                                {renderCard(n, qc.border, qc.header, false, { type: 'q' as const, qi })}
                              </div>
                            ))
                          }
                          {pmDrag !== null && (
                            <div style={{ height: 24, borderRadius: 5, border: `1.5px dashed ${qc.border}`, marginTop: 2, opacity: pmDragOver?.dst === 'q' && pmDragOver.qi === qi && pmDragOver.beforeNodeId === null ? 1 : 0.35 }} />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                {pmShowDays && <div style={{ width: 1, background: '#e5e7eb', flexShrink: 0 }} />}
                {pmShowDays && <div style={{ flex: 1, display: 'flex', gap: 8, padding: 10, overflowX: 'auto', minWidth: 0 }}>
                  {Array.from({ length: scheduleDays }, (_, di) => di).map(di => {
                    const lbl = dayLabels[di] ?? `Day ${di + 1}`;
                    const dNodes = (dayOrder[di] ?? []).map(id => nodes.find(n => n.id === id)).filter(Boolean) as typeof nodes;
                    const dNodesFiltered = pmFilter === null ? dNodes : dNodes.filter(n => pmFilter.some(fId => isUnderL1(n.id, fId)));
                    return (
                      <div key={di} style={{ minWidth: 110, flex: 1, background: DAY_BG, border: `1.5px solid ${DAY_BORDER}`, borderRadius: 10, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                        <div style={{ padding: '5px 8px', borderBottom: `1px solid ${DAY_BORDER}`, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 4 }}>
                          <input value={lbl} onChange={e => {
                            const next = [...dayLabels];
                            while (next.length <= di) next.push(`Day ${next.length + 1}`);
                            next[di] = e.target.value;
                            saveDayLabels(next);
                          }}
                            style={{ flex: 1, fontSize: 11, fontWeight: 700, color: DAY_HEADER, background: 'transparent', border: 'none', outline: 'none', minWidth: 0 }} />
                          {dNodesFiltered.length > 0 && <span style={{ fontSize: 10, color: DAY_HEADER, fontWeight: 600, flexShrink: 0 }}>{dNodesFiltered.length}</span>}
                          {l1Nodes.length > 0 && (
                            <button
                              onClick={() => { setAddPriorityPopup({ source: 'day', di }); setAddPriorityBranchId(l1Nodes[0].id); setAddPriorityQi(1); setAddPriorityText(''); }}
                              title={tr('addPriorityNodeTitle')}
                              style={{ background: 'none', border: 'none', padding: 0, margin: 0, cursor: 'pointer', flexShrink: 0, display: 'flex', color: DAY_HEADER }}
                            ><Plus size={13} /></button>
                          )}
                        </div>
                        <div style={{ flex: 1, overflowY: 'auto', padding: '4px 5px' }}
                          data-pmzone="d" data-pmzone-di={di}>
                          {dNodesFiltered.length === 0 && pmDrag === null
                            ? <span style={{ fontSize: 10, color: '#9ca3af', fontStyle: 'italic', padding: '4px 2px', display: 'block' }}>Drop items here</span>
                            : dNodesFiltered.map(n => (
                              <div key={n.id}>
                                {pmDragOver?.dst === 'd' && pmDragOver.di === di && pmDragOver.beforeNodeId === n.id && pmDrag?.nodeId !== n.id && dropLine(DAY_HEADER)}
                                {renderCard(n, DAY_BORDER, DAY_HEADER, true, { type: 'd' as const, di })}
                              </div>
                            ))
                          }
                          {pmDrag !== null && (
                            <div style={{ height: 24, borderRadius: 5, border: `1.5px dashed ${DAY_BORDER}`, marginTop: 2, opacity: pmDragOver?.dst === 'd' && pmDragOver.di === di && pmDragOver.beforeNodeId === null ? 1 : 0.35 }} />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>}
              </div>

              {/* ── Right-click context menu ── */}
              {pmContextMenu && (() => {
                const cmNode = nodes.find(n => n.id === pmContextMenu.nodeId);
                if (!cmNode) return null;
                return (
                  <>
                    <div style={{ position: 'fixed', inset: 0, zIndex: 599 }} onClick={() => setPmContextMenu(null)} onContextMenu={e => { e.preventDefault(); setPmContextMenu(null); }} />
                    <div style={{ position: 'fixed', left: Math.min(pmContextMenu.x, window.innerWidth - 210), top: Math.min(pmContextMenu.y, window.innerHeight - 260), background: 'white', border: '1px solid #e5e7eb', borderRadius: 8, boxShadow: '0 4px 20px rgba(0,0,0,0.15)', zIndex: 600, minWidth: 180, overflow: 'hidden', paddingBottom: 6 }}>
                      {/* Edit title */}
                      <button
                        onClick={() => { setEditPmTitle({ nodeId: cmNode.id, text: cmNode.text }); setPmContextMenu(null); }}
                        style={{ width: '100%', padding: '9px 14px', textAlign: 'left', fontSize: 13, color: '#374151', display: 'flex', alignItems: 'center', gap: 8, background: 'none', border: 'none', cursor: 'pointer', borderBottom: '1px solid #f3f4f6' }}
                        onMouseOver={e => (e.currentTarget.style.background = '#f3f4f6')}
                        onMouseOut={e => (e.currentTarget.style.background = 'none')}
                      >
                        <Pencil size={14} /> {tr('editPriorityTitle')}
                      </button>
                      {/* Delete */}
                      <button
                        onClick={() => { deleteNode(pmContextMenu.nodeId); setPmContextMenu(null); }}
                        style={{ width: '100%', padding: '9px 14px', textAlign: 'left', fontSize: 13, color: '#ef4444', display: 'flex', alignItems: 'center', gap: 8, background: 'none', border: 'none', cursor: 'pointer', borderBottom: '1px solid #f3f4f6' }}
                        onMouseOver={e => (e.currentTarget.style.background = '#fef2f2')}
                        onMouseOut={e => (e.currentTarget.style.background = 'none')}
                      >
                        <Trash2 size={14} /> Delete node
                      </button>
                      {/* Mark as completed */}
                      <button
                        onClick={() => {
                          const nowCompleted = !userCompleted[pmContextMenu.nodeId];
                          setUserCompleted(prev => {
                            const next = { ...prev };
                            if (nowCompleted) next[pmContextMenu.nodeId] = true; else delete next[pmContextMenu.nodeId];
                            return next;
                          });
                          setPmContextMenu(null);
                        }}
                        style={{ width: '100%', padding: '9px 14px', textAlign: 'left', fontSize: 13, color: '#16a34a', display: 'flex', alignItems: 'center', gap: 8, background: 'none', border: 'none', cursor: 'pointer', borderBottom: '1px solid #f3f4f6' }}
                        onMouseOver={e => (e.currentTarget.style.background = '#f0fdf4')}
                        onMouseOut={e => (e.currentTarget.style.background = 'none')}
                      >
                        <CheckCircle2 size={14} /> {userCompleted[pmContextMenu.nodeId] ? 'Mark as not completed' : 'Mark as completed'}
                      </button>
                      {/* Priority picker */}
                      <div style={{ padding: '7px 14px 2px', fontSize: 10, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Priority</div>
                      <div style={{ display: 'flex', gap: 5, padding: '4px 14px 6px', alignItems: 'center' }}>
                        {([1, 2, 3, 4, 5] as const).map(p => {
                          const active = userPriorities[cmNode.id] === p;
                          return (
                            <button key={p} onClick={() => {
                              const newPrio = active ? undefined : p;
                              setUserPriorities(prev => { const next = { ...prev }; if (newPrio) next[pmContextMenu.nodeId] = newPrio; else delete next[pmContextMenu.nodeId]; return next; });
                              setQuadrantOrder(prev => {
                                if (!prev) return prev;
                                const newQ = prev.map(q => q.filter(id => id !== pmContextMenu.nodeId)) as [number[], number[], number[], number[]];
                                if (newPrio && newPrio <= 4) newQ[newPrio - 1].push(pmContextMenu.nodeId);
                                return newQ;
                              });
                              setPmContextMenu(null);
                            }}
                              title={tr('priority', { p })}
                              style={{ width: 28, height: 28, borderRadius: 6, background: PRIO_COLORS[p], border: active ? '2.5px solid #1f2937' : '2px solid white', cursor: 'pointer', boxShadow: '0 1px 3px rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: 'white', flexShrink: 0 }}
                            >{p}</button>
                          );
                        })}
                        <button onClick={() => {
                          setUserPriorities(prev => { const next = { ...prev }; delete next[pmContextMenu.nodeId]; return next; });
                          setQuadrantOrder(prev => prev ? prev.map(q => q.filter(id => id !== pmContextMenu.nodeId)) as [number[], number[], number[], number[]] : prev);
                          setDayOrder(prev => prev.map(d => d.filter(id => id !== pmContextMenu.nodeId)));
                          setPmContextMenu(null);
                        }}
                          title={tr('noPriority')}
                          style={{ width: 24, height: 24, borderRadius: '50%', background: '#f3f4f6', border: `1.5px solid ${userPriorities[cmNode.id] === undefined ? '#1f2937' : '#d1d5db'}`, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', flexShrink: 0 }}
                        ><X size={10} strokeWidth={2.5} /></button>
                      </div>
                    </div>
                  </>
                );
              })()}

              {/* ── Add priority / schedule item popup ── */}
              {addPriorityPopup && (
                <>
                  <div style={{ position: 'fixed', inset: 0, zIndex: 699 }} onClick={() => setAddPriorityPopup(null)} />
                  <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', background: 'white', border: '1px solid #e5e7eb', borderRadius: 10, boxShadow: '0 8px 30px rgba(0,0,0,0.2)', zIndex: 700, minWidth: 280, padding: 16 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#1f2937', marginBottom: 12 }}>{tr('addPriorityNode')}</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, color: '#6b7280', fontWeight: 600 }}>
                        {tr('addPriorityBranchLabel')}
                        <select
                          value={addPriorityBranchId ?? ''}
                          onChange={e => setAddPriorityBranchId(Number(e.target.value))}
                          style={{ fontSize: 13, padding: '6px 8px', borderRadius: 6, border: '1px solid #d1d5db' }}
                        >
                          {l1Nodes.map(n => <option key={n.id} value={n.id}>{n.text || '(empty)'}</option>)}
                        </select>
                      </label>
                      {addPriorityPopup.source === 'day' && (
                        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, color: '#6b7280', fontWeight: 600 }}>
                          {tr('addPriorityQuadrantLabel')}
                          <select
                            value={addPriorityQi}
                            onChange={e => setAddPriorityQi(Number(e.target.value))}
                            style={{ fontSize: 13, padding: '6px 8px', borderRadius: 6, border: '1px solid #d1d5db' }}
                          >
                            {quadrantLabels.map((lbl, qi) => <option key={qi} value={qi + 1}>{lbl}</option>)}
                          </select>
                        </label>
                      )}
                      <input
                        autoFocus
                        value={addPriorityText}
                        onChange={e => setAddPriorityText(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') submitAddPriority();
                          else if (e.key === 'Escape') setAddPriorityPopup(null);
                        }}
                        placeholder={tr('addPriorityValuePlaceholder')}
                        style={{ fontSize: 13, padding: '6px 8px', borderRadius: 6, border: '1px solid #d1d5db' }}
                      />
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
                        <button onClick={() => setAddPriorityPopup(null)} style={{ fontSize: 12, padding: '5px 12px', borderRadius: 6, border: '1px solid #d1d5db', background: 'white', color: '#374151', cursor: 'pointer' }}>{tr('cancel')}</button>
                        <button
                          disabled={!addPriorityText.trim()}
                          onClick={submitAddPriority}
                          style={{ fontSize: 12, padding: '5px 12px', borderRadius: 6, border: 'none', background: addPriorityText.trim() ? '#6366f1' : '#c7d2fe', color: 'white', cursor: addPriorityText.trim() ? 'pointer' : 'default', fontWeight: 600 }}
                        >{tr('add')}</button>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* ── Edit item title popup ── */}
              {editPmTitle && (
                <>
                  <div style={{ position: 'fixed', inset: 0, zIndex: 699 }} onClick={() => setEditPmTitle(null)} />
                  <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', background: 'white', border: '1px solid #e5e7eb', borderRadius: 10, boxShadow: '0 8px 30px rgba(0,0,0,0.2)', zIndex: 700, minWidth: 280, padding: 16 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#1f2937', marginBottom: 12 }}>{tr('editPriorityTitle')}</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      <input
                        autoFocus
                        value={editPmTitle.text}
                        onChange={e => setEditPmTitle({ ...editPmTitle, text: e.target.value })}
                        onKeyDown={e => {
                          if (e.key === 'Enter') submitEditPmTitle();
                          else if (e.key === 'Escape') setEditPmTitle(null);
                        }}
                        placeholder={tr('addPriorityValuePlaceholder')}
                        style={{ fontSize: 13, padding: '6px 8px', borderRadius: 6, border: '1px solid #d1d5db' }}
                      />
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
                        <button onClick={() => setEditPmTitle(null)} style={{ fontSize: 12, padding: '5px 12px', borderRadius: 6, border: '1px solid #d1d5db', background: 'white', color: '#374151', cursor: 'pointer' }}>{tr('cancel')}</button>
                        <button
                          disabled={!editPmTitle.text.trim()}
                          onClick={submitEditPmTitle}
                          style={{ fontSize: 12, padding: '5px 12px', borderRadius: 6, border: 'none', background: editPmTitle.text.trim() ? '#6366f1' : '#c7d2fe', color: 'white', cursor: editPmTitle.text.trim() ? 'pointer' : 'default', fontWeight: 600 }}
                        >{tr('save')}</button>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          );
        })()}

        {/* ── Notes panel ── */}
        {notePanel && (() => {
          const noteNode = nodes.find(n => n.id === notePanel.nodeId);
          if (!noteNode) return null;

          // A list-toggle (insertUnorderedList/insertOrderedList) rewrites the DOM
          // around the selection (text node → <ul><li>...), and some browsers respond
          // to that by collapsing the Selection to zero ranges, or leaving it anchored
          // on a node that just got detached — even though DOM focus never actually
          // left the editor. With no valid selection, the NEXT execCommand silently
          // no-ops, which looks exactly like "the toolbar button did nothing, I have
          // to click back into the note body first" even though focus was fine the
          // whole time. Restoring a collapsed selection at the end of the content
          // before running a command (only when the current one is actually missing/
          // outside the editor — never overwriting a selection that's still valid)
          // fixes this without touching the editor.focus() guard below, which exists
          // for a different reason (see its own comment).
          const ensureEditorSelection = (editor: HTMLElement) => {
            const sel = window.getSelection();
            if (!sel) return;
            if (sel.rangeCount === 0 || !editor.contains(sel.anchorNode)) {
              const range = document.createRange();
              range.selectNodeContents(editor);
              range.collapse(false);
              sel.removeAllRanges();
              sel.addRange(range);
            }
          };

          const execFmt = (command: string, value?: string) => {
            const editor = noteEditorRef.current;
            if (!editor) return;
            // Only call focus() when it's actually needed — on an already-focused
            // contentEditable, a redundant focus() call is a no-op for DOM focus
            // itself, but is NOT guaranteed to be a no-op for the active selection:
            // list commands in particular can leave the browser in a state where
            // re-focusing resets the caret to the start of the content rather than
            // preserving where it was, which read as "the cursor isn't active
            // anymore" even though the element technically still has focus.
            if (document.activeElement !== editor) editor.focus();
            ensureEditorSelection(editor);
            document.execCommand(command, false, value);
          };

          const changeFontSize = (delta: number) => {
            const editor = noteEditorRef.current;
            if (!editor) return;
            if (document.activeElement !== editor) editor.focus();
            ensureEditorSelection(editor);
            const current = parseInt(document.queryCommandValue('fontSize') || '3', 10) || 3;
            const next = Math.min(7, Math.max(1, current + delta));
            document.execCommand('fontSize', false, String(next));
          };

          const FmtBtn = ({ title, onCmd, children }: { title: string; onCmd: () => void; children: React.ReactNode }) => (
            <button
              title={title}
              // preventDefault on mousedown is what stops the browser from shifting
              // focus to this button in the first place (the standard fix for a
              // toolbar button next to a contentEditable/input). The command itself
              // must run HERE too, not on a later onClick — on a touch device, a
              // preventDefault'd mousedown (synthesized from touchstart) can suppress
              // the whole rest of that synthetic mouse-event sequence in some mobile
              // browsers, including the click that would otherwise follow, which is
              // exactly what made the entire toolbar go unresponsive on touch.
              onMouseDown={e => { e.preventDefault(); onCmd(); }}
              style={{
                background: 'none', border: '1px solid #e5e7eb', borderRadius: 5,
                padding: '4px 7px', cursor: 'pointer',
                color: '#374151', lineHeight: 1, display: 'flex', alignItems: 'center',
              }}
            >
              {children}
            </button>
          );

          const commitNote = () => {
            if (!canEditMindmapRef.current) return;
            const html = DOMPurify.sanitize(noteEditorRef.current?.innerHTML ?? '');
            const newNodes = nodesRef.current.map(n =>
              n.id === notePanel.nodeId ? { ...n, note: html || undefined } : n
            );
            pushHistory(newNodes);
            setNodes(newNodes);
          };

          return (
            <div
              style={{
                position: 'fixed', top: 52, right: 0, width: notePanelWidth, height: 'calc(100dvh - 52px)',
                background: 'white', borderLeft: '1px solid #e5e7eb',
                boxShadow: '-4px 0 16px rgba(0,0,0,0.1)', zIndex: 300,
                display: 'flex', flexDirection: 'column',
              }}
              onClick={e => e.stopPropagation()}
            >
              {/* Drag handle — left edge is resizable */}
              <div
                style={{
                  position: 'absolute', left: 0, top: 0, bottom: 0, width: 6,
                  cursor: 'col-resize', zIndex: 10, background: 'transparent',
                  borderLeft: '3px solid transparent',
                  transition: 'border-color 0.15s',
                }}
                onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.borderLeftColor = '#82C0CC'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.borderLeftColor = 'transparent'; }}
                onMouseDown={e => {
                  e.preventDefault();
                  notePanelDragRef.current = { startX: e.clientX, startW: notePanelWidth };
                  const onMove = (ev: MouseEvent) => {
                    if (!notePanelDragRef.current) return;
                    const delta = notePanelDragRef.current.startX - ev.clientX;
                    setNotePanelWidth(Math.max(280, Math.min(800, notePanelDragRef.current.startW + delta)));
                  };
                  const onUp = () => {
                    notePanelDragRef.current = null;
                    document.removeEventListener('mousemove', onMove);
                    document.removeEventListener('mouseup', onUp);
                  };
                  document.addEventListener('mousemove', onMove);
                  document.addEventListener('mouseup', onUp);
                }}
              />
              {/* Header */}
              <div style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#fffbeb' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <FileText size={15} color="#f59e0b" />
                  <span style={{ fontWeight: 700, fontSize: 14, color: '#1f2937' }}>{tr('noteTitle')}</span>
                  <span style={{ fontSize: 12, color: '#6b7280', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>— {noteNode.text}</span>
                </div>
                <button
                  onClick={() => { commitNote(); setNotePanel(null); }}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 2 }}
                >
                  <X size={16} />
                </button>
              </div>

              {/* Formatting toolbar — only for editors */}
              {canEditMindmap && <div style={{ padding: '6px 12px', borderBottom: '1px solid #e5e7eb', display: 'flex', gap: 6, alignItems: 'center', background: '#f9fafb' }}>
                <FmtBtn title={tr('bold')} onCmd={() => execFmt('bold')}><Bold size={14} /></FmtBtn>
                <FmtBtn title={tr('italic')} onCmd={() => execFmt('italic')}><Italic size={14} /></FmtBtn>
                <FmtBtn title={tr('underline')} onCmd={() => execFmt('underline')}><Underline size={14} /></FmtBtn>
                <div style={{ width: 1, height: 18, background: '#e5e7eb', margin: '0 2px' }} />
                <FmtBtn title={tr('bulletList')} onCmd={() => execFmt('insertUnorderedList')}><List size={14} /></FmtBtn>
                <FmtBtn title={tr('numberedList')} onCmd={() => execFmt('insertOrderedList')}><ListOrdered size={14} /></FmtBtn>
                <div style={{ width: 1, height: 18, background: '#e5e7eb', margin: '0 2px' }} />
                <FmtBtn title={tr('textLarger')} onCmd={() => changeFontSize(+1)}><span style={{ fontWeight: 700, fontSize: 13 }}>A+</span></FmtBtn>
                <FmtBtn title={tr('textSmaller')} onCmd={() => changeFontSize(-1)}><span style={{ fontSize: 11 }}>A-</span></FmtBtn>
                <div style={{ width: 1, height: 18, background: '#e5e7eb', margin: '0 2px' }} />
                <FmtBtn title={tr('increaseIndent')} onCmd={() => execFmt('indent')}>
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="1" y1="2.5" x2="13" y2="2.5" />
                    <line x1="5" y1="6.5" x2="13" y2="6.5" />
                    <line x1="5" y1="10.5" x2="13" y2="10.5" />
                    <polyline points="1,5 3.5,7 1,9" fill="currentColor" stroke="currentColor" strokeWidth="1.2" />
                  </svg>
                </FmtBtn>
                <FmtBtn title={tr('decreaseIndent')} onCmd={() => execFmt('outdent')}>
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="1" y1="2.5" x2="13" y2="2.5" />
                    <line x1="5" y1="6.5" x2="13" y2="6.5" />
                    <line x1="5" y1="10.5" x2="13" y2="10.5" />
                    <polyline points="4,5 1.5,7 4,9" fill="currentColor" stroke="currentColor" strokeWidth="1.2" />
                  </svg>
                </FmtBtn>
              </div>}

              {/* Rich text editor — no setNodes on input so the browser undo stack stays intact */}
              <div
                ref={noteEditorRef}
                contentEditable={canEditMindmap || undefined}
                suppressContentEditableWarning
                onBlur={() => { if (document.hasFocus()) commitNote(); }}
                onKeyDown={e => {
                  if (e.ctrlKey && e.key === 'z') { e.preventDefault(); e.stopPropagation(); document.execCommand('undo'); }
                  if (e.ctrlKey && e.key === 'y') { e.preventDefault(); e.stopPropagation(); document.execCommand('redo'); }
                }}
                data-placeholder={tr('notePlaceholder')}
                style={{
                  flex: 1, padding: 16, border: 'none', outline: 'none',
                  fontSize: 15, lineHeight: 1.7, overflowY: 'auto',
                  fontFamily: 'inherit', color: '#374151', background: '#fafafa',
                }}
              />

              {/* Footer — clear button */}
              {noteNode.note && (
                <div style={{ padding: '10px 16px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => {
                      const newNodes = nodesRef.current.map(n =>
                        n.id === notePanel.nodeId ? { ...n, note: undefined } : n
                      );
                      pushHistory(newNodes);
                      setNodes(newNodes);
                      setNotePanel(null);
                    }}
                    style={{ fontSize: 12, color: '#ef4444', background: 'none', border: '1px solid #fca5a5', borderRadius: 6, padding: '4px 10px', cursor: 'pointer' }}
                  >
                    {tr('deleteNote')}
                  </button>
                </div>
              )}
            </div>
          );
        })()}

        {/* ── Export menu ── */}
        {showExportMenu && (
          <>
            <div style={{ position: 'fixed', inset: 0, zIndex: 399 }} onClick={() => setShowExportMenu(false)} />
            <div style={{ position: 'fixed', left: 60, top: exportMenuY, background: 'white', border: '1px solid #e5e7eb', borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.15)', zIndex: 400, padding: '6px', display: 'flex', flexDirection: 'column', gap: 4, minWidth: 100 }}>
              <button onClick={() => { exportAs('png'); setShowExportMenu(false); }} style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#374151', textAlign: 'left' }}>PNG</button>
              <button onClick={() => { exportAs('svg'); setShowExportMenu(false); }} style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#374151', textAlign: 'left' }}>SVG</button>
            </div>
          </>
        )}

        {/* ── Link panel ── */}
        {linkPanel && (() => {
          const linkNode = nodes.find(n => n.id === linkPanel.nodeId);
          if (!linkNode) return null;
          const canEdit = canEditMindmapRef.current;

          const normalizeUrl = (s: string) => {
            const t = s.trim();
            if (!t) return '';
            return /^https?:\/\//i.test(t) ? t : 'https://' + t;
          };

          const commitLink = () => {
            if (!linkInputRef.current) return;
            const url = normalizeUrl(linkInputRef.current.value);
            const newNodes = nodesRef.current.map(n =>
              n.id === linkPanel.nodeId ? { ...n, url: url || undefined } : n
            );
            pushHistory(newNodes);
            setNodes(newNodes);
          };

          return (
            <div
              style={{
                position: 'fixed', top: 52, right: 0, width: 360,
                background: 'white', borderLeft: '1px solid #e5e7eb',
                boxShadow: '-4px 0 16px rgba(0,0,0,0.1)', zIndex: 300,
                display: 'flex', flexDirection: 'column',
              }}
              onClick={e => e.stopPropagation()}
            >
              {/* Header */}
              <div style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#eff6ff' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2.5" strokeLinecap="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>
                  <span style={{ fontWeight: 700, fontSize: 14, color: '#1f2937' }}>{tr('link')}</span>
                  <span style={{ fontSize: 12, color: '#6b7280', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>— {linkNode.text}</span>
                </div>
                <button
                  onClick={() => { commitLink(); setLinkPanel(null); }}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 2 }}
                >
                  <X size={16} />
                </button>
              </div>

              {/* URL input */}
              <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                <input
                  ref={linkInputRef}
                  type="url"
                  placeholder="https://example.com"
                  disabled={!canEdit}
                  onBlur={() => { if (canEdit && document.hasFocus()) commitLink(); }}
                  onKeyDown={e => {
                    if (e.key === 'Enter') { e.preventDefault(); commitLink(); setLinkPanel(null); }
                    if (e.key === 'Escape') { setLinkPanel(null); }
                  }}
                  style={{
                    width: '100%', padding: '8px 10px', border: '1px solid #d1d5db',
                    borderRadius: 7, fontSize: 14, outline: 'none',
                    boxSizing: 'border-box', color: '#1f2937',
                    background: canEdit ? 'white' : '#f9fafb',
                  }}
                />
                <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between' }}>
                  <button
                    onClick={() => {
                      const url = normalizeUrl(linkInputRef.current?.value ?? linkNode.url ?? '');
                      if (url) window.open(url, '_blank', 'noopener,noreferrer');
                    }}
                    style={{ fontSize: 13, color: '#3b82f6', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 6, padding: '5px 14px', cursor: 'pointer', fontWeight: 600 }}
                  >
                    {tr('openLink')}
                  </button>
                  {canEdit && linkNode.url && (
                    <button
                      onClick={() => {
                        const newNodes = nodesRef.current.map(n =>
                          n.id === linkPanel.nodeId ? { ...n, url: undefined } : n
                        );
                        pushHistory(newNodes);
                        setNodes(newNodes);
                        setLinkPanel(null);
                      }}
                      style={{ fontSize: 12, color: '#ef4444', background: 'none', border: '1px solid #fca5a5', borderRadius: 6, padding: '5px 10px', cursor: 'pointer' }}
                    >
                      {tr('deleteLink')}
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })()}

        {/* ── Share dialog ── */}
        {showShare && currentId && (
          <ShareDialog
            flowchartId={currentId}
            flowchartTitle={title}
            user={user}
            onClose={() => setShowShare(false)}
          />
        )}

        {/* ── Realtime write toast ── */}
        {lastWriteToast && (
          <div style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 9997, background: 'rgba(17,24,39,0.92)', color: 'white', borderRadius: 8, padding: '10px 18px', fontSize: 13, fontWeight: 500, boxShadow: '0 4px 16px rgba(0,0,0,0.2)', display: 'flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap', pointerEvents: 'none' }}>
            <span style={{ fontSize: 15 }}>✏️</span>
            <span>{tr('updatedBy')} <strong>{lastWriteToast.includes('@') ? lastWriteToast.split('@')[0] : lastWriteToast}</strong></span>
          </div>
        )}

        {/* ── Help panel (controls overview) ── */}
        {showHelpPanel && (
          <div style={{ position: 'fixed', top: 52, left: 68, zIndex: 400, width: 300, background: 'white', borderRadius: 10, boxShadow: '0 4px 24px rgba(0,0,0,0.13), 0 1px 4px rgba(0,0,0,0.08)', border: '1px solid #e5e7eb', display: 'flex', flexDirection: 'column', maxHeight: 'calc(100dvh - 64px)' }}>
            <div style={{ padding: '10px 14px 8px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>{tr('controlsReminder')}</span>
              <button onClick={() => setShowHelpPanel(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 2, display: 'flex', alignItems: 'center', borderRadius: 4 }}><X size={14} /></button>
            </div>
            <div style={{ overflowY: 'auto', padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: 6 }}>
              {[
                { icon: '🖱️', keys: tr('mmCtrlDblClick'), action: tr('mmEditText') },
                { icon: '🖱️', keys: tr('mmCtrlDrag'), action: tr('mmMoveNode') },
                { icon: '🖱️', keys: tr('mmShiftDrag'), action: tr('mmMoveParent') },
                { icon: '🖱️', keys: tr('ctrlDragCanvas'), action: tr('mmSelectMultiple') },
                { icon: '🖱️', keys: tr('mmScrollDrag'), action: tr('mmZoomPan') },
                { icon: '⌨️', keys: 'Tab', action: tr('mmCreateChild') },
                { icon: '⌨️', keys: 'Enter', action: tr('mmCreateSibling') },
                { icon: '⌨️', keys: '↑ ↓ ← →', action: tr('mmNavigate') },
                { icon: '⌨️', keys: tr('ctrlDelete'), action: tr('mmDeleteNode') },
                { icon: '⌨️', keys: 'Ctrl + Z / Y', action: tr('ctrlUndoRedo') },
                { icon: '⌨️', keys: 'Ctrl + S', action: tr('ctrlSave') },
              ].map(({ icon, keys, action }) => (
                <div key={keys} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 13, width: 18, textAlign: 'center', flexShrink: 0 }}>{icon}</span>
                  <span style={{ background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: 4, padding: '1px 6px', fontSize: 10, fontWeight: 600, color: '#374151', whiteSpace: 'nowrap', flexShrink: 0 }}>{keys}</span>
                  <span style={{ fontSize: 11, color: '#6b7280' }}>{action}</span>
                </div>
              ))}
            </div>
            <div style={{ padding: '8px 14px 10px', borderTop: '1px solid #f3f4f6', flexShrink: 0 }}>
              <button
                onClick={() => { setShowHelpPanel(false); setShowTour(true); }}
                style={{ width: '100%', padding: '6px 0', borderRadius: 6, border: 'none', background: 'linear-gradient(135deg, #6366f1, #3b82f6)', color: 'white', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
              >{tr('restartTour')}</button>
            </div>
          </div>
        )}

        {/* ── Tour overlay ── */}
        {showTour && (
          <MindmapTourOverlay onClose={() => {
            setShowTour(false);
            localStorage.setItem('mindmap-tour-done', '1');
            if (user?.id) {
              supabase.from('user_profiles').update({ mindmap_tour_seen: true }).eq('id', user.id).then(() => {});
            }
          }} />
        )}

        {/* ── Change log panel ── */}
        {showHistory && (
          <div style={{ position: 'fixed', top: 52, right: 0, width: 360, height: 'calc(100dvh - 52px)', background: 'white', borderLeft: '1px solid #e5e7eb', boxShadow: '-4px 0 16px rgba(0,0,0,0.1)', zIndex: 300, display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#fff7ed' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Clock size={15} color="#f97316" />
                <span style={{ fontWeight: 700, fontSize: 14, color: '#1f2937' }}>{tr('changeLog')}</span>
                <span style={{ fontSize: 12, color: '#6b7280' }}>— {title}</span>
              </div>
              <button onClick={() => setShowHistory(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 2 }}><X size={16} /></button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: 12 }}>
              {loadingHistory ? (
                <div style={{ textAlign: 'center', padding: '32px 0', color: '#9ca3af' }}>{tr('loading')}</div>
              ) : historyVersions.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '32px 0', color: '#9ca3af' }}>
                  <Clock size={36} style={{ margin: '0 auto 10px', display: 'block', opacity: 0.3 }} />
                  <p style={{ fontSize: 13 }}>{tr('noChangesLogged')}</p>
                  <p style={{ fontSize: 11, marginTop: 4 }}>{tr('changesTrackedAfterSave')}</p>
                </div>
              ) : historyVersions.map((entry, i) => (
                <div key={i} style={{ padding: '10px 12px', borderRadius: 8, border: '1px solid #f3f4f6', marginBottom: 8, background: i === 0 ? '#fffbeb' : 'white' }}>
                  <div style={{ fontSize: 13, color: '#1f2937', marginBottom: 3 }}>{entry.action}</div>
                  <div style={{ fontSize: 11, color: '#9ca3af', display: 'flex', justifyContent: 'space-between' }}>
                    <span>{entry.user_email || tr('unknown')}</span>
                    <span>{new Date(entry.created_at).toLocaleString()}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>

    </>
  );
}

// ─── Small helper components ──────────────────────────────────────────────────

function SideBtn({ onClick, disabled, title, color, children }: { onClick?: (e: React.MouseEvent) => void; disabled?: boolean; title: string; color: string; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      style={{
        width: 40, padding: '6px 0',
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
        background: disabled ? '#C5B9B3' : color,
        border: 'none', borderRadius: 6,
        color: disabled ? '#7a6f6a' : 'white',
        cursor: disabled ? 'not-allowed' : 'pointer',
        fontSize: 9, opacity: disabled ? 0.5 : 1,
        transition: 'opacity 0.15s',
      }}
    >
      {children}
    </button>
  );
}

function CtxItem({ icon, onClick, danger, children }: { icon: React.ReactNode; onClick: () => void; danger?: boolean; children: React.ReactNode }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{ width: '100%', padding: '8px 12px', textAlign: 'left', background: hover ? (danger ? '#fef2f2' : '#f3f4f6') : 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: danger ? '#ef4444' : '#1f2937', display: 'flex', alignItems: 'center', gap: 8 }}
    >
      {icon} {children}
    </button>
  );
}
